import * as T from 'three';
import {FrostCoat} from './frost-coat.js';
import {WarmthField} from './warmth-field.js';
import {GumBubble} from './gum-bubble.js';
import {CheekPouch} from './cheek-pouch.js';
import {PopcornPot} from './popcorn-pot.js';
import {MochiPounding} from './mochi-pounding.js';
import {JellySwim} from './jelly-swim.js';
import {ShavedIce} from './shaved-ice.js';
import {RolyPoly} from './roly-poly.js';
import {PaperBalloon} from './paper-balloon.js';
import {DangoPolish} from './dango-polish.js';
import {SoapBubbles} from './soap-bubbles.js';
import {KingyoSukui} from './kingyo-sukui.js';
import {Meringue} from './meringue.js';
import {applyFrostShading} from './frost-shading.js';
import {PopDomes} from './pop-domes.js';
import {SurpriseEgg} from './surprise-egg.js';
import {SlipTube} from './slip-tube.js';
import {StrainColor} from './strain-color.js';
import {CakeSlices} from './cake-slices.js';
import {makeKnifeTool,knifeDemoTarget,KNIFE_DEMO_SECONDS} from './knife-tool.js';
import {SplatThrow} from './splat-throw.js';
import {SnowGlobe} from './snow-globe.js';
import {sampleField} from './slip-tube.js';
import {LavaBlobs} from './lava-lamp.js';
import {MarchingCubes} from 'three/addons/objects/MarchingCubes.js';
import {PeelPetals} from './peel-petals.js';
import {SandLoaf} from './sand-loaf.js';
import {VoxelSurface} from './voxel-surface.js';
import {makeSandMaterial} from './sand-material.js';
import {YolkFlow} from './yolk-flow.js';
import {ParticleFluid} from './particle-fluid.js';
import {ScreenSpaceFluid} from './screen-space-fluid.js';
import {RippleWater} from './ripple-water.js';
import {PressurePuff} from './pressure-puff.js';
import {JetStream} from './jet-stream.js';
import {mochiToastShader} from './toy-materials.js';
import {SHAPES,shellUniforms,patchShell,CrackWeb,rimWeights,Shards,Crumbs} from './halloween-shells.js';
import {CandyPile} from './candy-pile.js';
import {BatSwarm} from './bat-swarm.js';
import {toySound} from './toy-sound.js';

// Per-kind interactions that plug into Stage through a small set of hooks:
//   rotation, pickable(), grab(hit) → 'tool' | undefined, moveTool(target),
//   squeeze() → handled?, squeezePoint(body), flip(), step(dt), activity
//   (geometry is moving), active (anything to render), held (fit while held),
//   sample(item,i,out) for items with behaviorSample, deform(item,i,out,normal)
//   for items with behaviorDeform, poseProp(item), release(), reset(), dispose().
// Everything works in the toy's local space after Stage.loadToy normalised it.
const clamp=(v,a,b)=>Math.max(a,Math.min(b,v));
const boxOf=items=>{const box=new T.Box3();for(const item of items)box.union(item.mesh.geometry.boundingBox);return box;};
// Bends fixed props (water, a lid, a pool of soap) with the soft container
// around them, so a squeeze does not leave the contents sticking out.
function cageFollower(stage,items){
  const parts=items.filter(Boolean).map(item=>({item,rest:item.mesh.geometry.attributes.position.array.slice()}));
  const d=[0,0,0],q=[0,0,0];let bent=false;
  return ()=>{
    const body=stage.body;if(!body)return;
    const moving=!!body.hold||body.activity>1e-3;if(!moving&&!bent)return;bent=moving;
    for(const {item,rest} of parts){
      const attr=item.mesh.geometry.attributes.position,a=attr.array;
      for(let i=0;i<a.length;i+=3){q[0]=rest[i];q[1]=rest[i+1];q[2]=rest[i+2];body.displacementAt(q,d);a[i]=rest[i]+d[0];a[i+1]=rest[i+1]+d[1];a[i+2]=rest[i+2]+d[2];}
      attr.needsUpdate=true;item.mesh.geometry.computeBoundingSphere();
    }
  };
}

export function createToyBehavior(stage,record){
  const build={popcloud:popCloud,surpriseegg:surpriseEgg,watersnake:waterSnake,moodmallow:moodMallow,rollcake:rollCake,splatocto:splatOcto,snowglobe:snowGlobe,waxlamp:waxLamp,peelmikan:peelMikan,smilesand:smileSand,yolkegg:yolkEgg,squirtduck:squirtDuck,honeytoast:honeyToast,yakimochi:yakiMochi,treasurejelly:treasureJelly,fizzball:fizzBall,waterballoon:waterBalloon,treatpumpkin:treatPumpkin,cracklebat:crackleBat,frostmochi:frostMochi,snowbunny:snowBunny,bubblegum:bubbleGum,hamster:hamsterPouch,popcornpot:popcornPot,mochitsuki:mochiTsuki,jellyfish:jellyfish,kakigori:kakigori,daruma:darumaToy,kamifusen:paperBalloon,dorodango:mudDango,bubblewand:bubbleWand,kingyo:goldfishScoop,meringue:meringueBowl}[record.kind];
  const behavior=build?build(stage,record):null;
  // Plain squishies with a gimmick on top can still be pushed about the floor.
  if(behavior&&CARRIED.has(record.kind))behavior.carry=true;
  return behavior;
}
const CARRIED=new Set(['popcloud','moodmallow','frostmochi','bubblegum','hamster']);

function popCloud(stage){
  const items=stage.meshes.filter(item=>/^dome_\d+/.test(item.mesh.name)).sort((a,b)=>Number(/\d+/.exec(a.mesh.name)[0])-Number(/\d+/.exec(b.mesh.name)[0]));
  if(!items.length)return null;
  const domes=items.map((item,index)=>{
    const box=item.mesh.geometry.boundingBox,radius=(box.max.x-box.min.x)/2/1.12;
    item.domeIndex=index;item.behaviorDeform=true;
    return {center:[(box.min.x+box.max.x)/2,box.max.y-radius,(box.min.z+box.max.z)/2],radius,axis:[0,1,0]};
  });
  const pops=new PopDomes(domes),rest=[0,0,0];
  pops.onSnap=()=>{stage.canvas.dataset.poppedDomes=String(pops.downCount);};
  return {
    // Tilt the pad toward the viewer so the domes read from the front camera.
    rotation:[.42,-.18,0],
    pops,
    get activity(){return pops.activity;},
    get active(){return pops.activity>0;},
    grab(hit){const item=stage.meshes.find(m=>m.mesh===hit.object);if(item?.domeIndex!==undefined)pops.grab(item.domeIndex);},
    step(dt){
      if(stage.body?.hold&&pops.pressed>=0&&!pops.auto){
        const radius=pops.domes[pops.pressed].radius,down=Math.max(0,(stage.grabPoint?.[1]??0)-(stage.dragTarget?.[1]??0));
        pops.press((stage.pressAmount??0)/.55+down/(.35*radius));
      }else if(!stage.body?.hold)pops.release();
      pops.step(dt);
    },
    deform(item,i,out,normal){const k=i*3;rest[0]=item.rest[k];rest[1]=item.rest[k+1];rest[2]=item.rest[k+2];pops.deform(item.domeIndex,rest,out,normal);},
    squeeze(){return pops.demo();},
    flip(){return pops.flipAll();},
    release(){pops.release();},
    reset(){pops.reset();stage.canvas.dataset.poppedDomes='0';},
    dispose(){},
  };
}

function surpriseEgg(stage){
  const parts=stage.meshes.filter(item=>/^prop_pop/.test(item.mesh.name));
  const shell=stage.meshes.filter(item=>!item.isProp);
  if(!parts.length||!shell.length)return null;
  const box=boxOf(parts),shellBox=boxOf(shell),center=box.getCenter(new T.Vector3());
  const pivot=[center.x,box.min.y,center.z],binding=stage.body.bind(center.toArray());
  const egg=new SurpriseEgg({travel:(shellBox.max.y-shellBox.min.y)*.55});
  for(const item of parts){item.popEyes=/(eyes|glints)$/.test(item.mesh.name);item.popLids=/lids$/.test(item.mesh.name);}
  const q=new T.Quaternion(),up=new T.Vector3(0,1,0),turned=new T.Vector3(),sample=[0,0,0];
  const pose=item=>{
    const p=egg.pose();stage.body.sample(binding,sample);
    q.setFromAxisAngle(up,p.angle);item.mesh.quaternion.copy(q);item.mesh.scale.set(p.squash,p.stretch,p.squash);
    turned.set(pivot[0]*p.squash,pivot[1]*p.stretch,pivot[2]*p.squash).applyQuaternion(q);
    item.mesh.position.set(pivot[0]-turned.x+sample[0]-binding.point[0],pivot[1]+p.lift-turned.y+sample[1]-binding.point[1],pivot[2]-turned.z+sample[2]-binding.point[2]);
    if(item.popEyes)item.mesh.visible=egg.eyesOpen;
    if(item.popLids)item.mesh.visible=!egg.eyesOpen;
  };
  for(const item of parts)pose(item);
  return {
    egg,
    get activity(){return egg.activity;},
    get active(){return egg.activity>0;},
    step(dt){
      if(stage.body?.hold)egg.squeeze((stage.pressAmount??0)*1.5);else egg.release();
      egg.step(dt);stage.canvas.dataset.surpriseRise=egg.rise.toFixed(3);
    },
    poseProp(item){if(parts.includes(item))pose(item);},
    release(){egg.release();},
    reset(){egg.reset();for(const item of parts)pose(item);},
    dispose(){},
  };
}

function waterSnake(stage){
  const body=stage.meshes.find(item=>item.mesh.name==='body_tube_water');
  if(!body)return null;
  const box=body.mesh.geometry.boundingBox,floaterOf=name=>/fish|eye/.test(name)?0:/star/.test(name)?1:2;
  const tube=new SlipTube({length:box.max.x-box.min.x,axis:0,floaters:3,center:[(box.min.y+box.max.y)/2,(box.min.z+box.max.z)/2],radius:(box.max.y-box.min.y)/2});
  for(const item of stage.meshes){
    if(item.isProp){item.floater=floaterOf(item.mesh.name);continue;}
    item.behaviorSample=true;item.transportNormals=false;
  }
  const rest=[0,0,0],scratch=[0,0,0];
  return {
    tube,
    get activity(){return tube.activity;},
    get active(){return tube.activity>0;},
    step(dt){
      const hold=stage.body?.hold;
      if(hold)tube.hold(hold.start[0],stage.pressAmount??0);else tube.release();
      tube.step(dt);
      // Once the tube has slipped out of the pinch, the finger holds nothing.
      if(tube.escapedNow){stage.body.release();stage.canvas.dataset.slipEscapes=String(tube.escapes);}
      stage.canvas.dataset.slipShift=tube.shift.toFixed(3);
    },
    sample(item,i,out){const k=i*3;rest[0]=item.rest[k];rest[1]=item.rest[k+1];rest[2]=item.rest[k+2];tube.deform(stage.body,rest,out,scratch);},
    poseProp(item){
      const f=tube.floaters[item.floater]??tube.floaters[2];
      item.mesh.position.set(f.offset,0,0);
      item.mesh.rotation.set(0,0,clamp(-f.velocity*.05,-.3,.3));
    },
    // The demonstration pinches the front of the tube near its right end.
    squeezePoint(b){return {point:[b.min[0]+(b.max[0]-b.min[0])*.8,(b.min[1]+b.max[1])*.5,b.max[2]*.9],direction:[0,0,-1]};},
    release(){tube.release();},
    reset(){tube.reset();for(const item of stage.meshes)if(item.isProp){item.mesh.position.set(0,0,0);item.mesh.rotation.set(0,0,0);}},
    dispose(){},
  };
}

function moodMallow(stage){
  const items=stage.meshes.filter(item=>item.mesh.material.userData.moodStrain&&item.bindings.length);
  if(!items.length)return null;
  const color=new StrainColor(stage.body);
  const records=items.map(item=>{
    const record=color.attach(item,item.bindings);
    record.attribute=new T.BufferAttribute(new Float32Array(record.memory.length),1);
    item.mesh.geometry.setAttribute('moodStrain',record.attribute);
    return record;
  });
  const upload=()=>{for(const record of records){record.attribute.array.set(record.memory);record.attribute.needsUpdate=true;}stage.dirty=true;};
  return {
    color,
    get activity(){return 0;},
    get active(){return color.activity>0;},
    step(dt){if(color.step(dt))upload();stage.canvas.dataset.moodPeak=color.peak.toFixed(3);},
    release(){},
    reset(){color.reset();upload();},
    dispose(){},
  };
}

function rollCake(stage){
  const bySlice=new Map();
  for(const item of stage.meshes){
    const match=/^slice_(\d+)/.exec(item.mesh.name);
    if(match){const index=Number(match[1]);if(!bySlice.has(index))bySlice.set(index,[]);bySlice.get(index).push(item);}
  }
  if(bySlice.size<2)return null;
  const order=[...bySlice.keys()].sort((a,b)=>a-b);
  const slices=order.map(index=>{const box=boxOf(bySlice.get(index));return {min:box.min.toArray(),max:box.max.toArray()};});
  const cake=new CakeSlices(slices);
  // Faces and other decorations follow the slice they sit on.
  for(const item of stage.meshes){
    if(item.isProp)continue;
    const match=/^slice_(\d+)/.exec(item.mesh.name),x=item.center.x;
    item.sliceIndex=match?order.indexOf(Number(match[1])):slices.findIndex(s=>x>=s.min[0]-.01&&x<=s.max[0]+.01);
    item.behaviorDeform=item.sliceIndex>=0;
  }
  const all=boxOf(stage.meshes.filter(item=>!item.isProp)),top=all.max.y,height=all.max.y-all.min.y;
  const sliceWidth=slices[0].max[0]-slices[0].min[0];
  const knife=makeKnifeTool(height);stage.toy.add(knife);
  const home=[(slices.at(-2).min[0]+slices.at(-2).max[0])*.5,top+height*.12,0];
  knife.position.fromArray(home);
  let target=null,demo=null,moving=false;
  const place=(goal,dt,tau)=>{
    const k=1-Math.exp(-dt/tau),p=knife.position;
    p.x+=(goal[0]-p.x)*k;p.y+=(goal[1]-p.y)*k;p.z+=(goal[2]-p.z)*k;
    moving=Math.abs(goal[0]-p.x)+Math.abs(goal[1]-p.y)+Math.abs(goal[2]-p.z)>.0005;
    if(!moving)p.fromArray(goal);
  };
  return {
    cake,knife,
    rotation:[0,-.55,0],
    toolNormal:[0,0,1],
    get activity(){return cake.activity;},
    get active(){return cake.activity>0||moving||!!demo||!!target;},
    get held(){return !!target||!!demo;},
    pickable(){return knife.children;},
    grab(hit){
      if(hit.object.userData.knifeTool){demo=null;target=knife.position.toArray();stage.grabPoint=[...target];return 'tool';}
      // A press on a moved slice dents the sponge where the slice rests.
      const item=stage.meshes.find(m=>m.mesh===hit.object);
      if(item?.sliceIndex>=0&&stage.grabPoint)stage.grabPoint[0]-=cake.offset[item.sliceIndex];
    },
    moveTool(goal){if(goal?.every(Number.isFinite))target=[...goal];},
    step(dt){
      let goal=null;
      if(demo){demo.time+=dt;goal=knifeDemoTarget(demo.time,demo);if(demo.time>=KNIFE_DEMO_SECONDS)demo=null;}
      else if(target)goal=[...target];
      if(goal){
        goal[2]=0;
        goal[0]=clamp(goal[0],all.min.x-height*.3,all.max.x+height*.3);goal[1]=clamp(goal[1],0,top+height*1.1);
        const k=cake.seamNear(goal[0],sliceWidth*.42);
        if(goal[1]<top-.005){
          if(k>=0){
            // The blade finds the score and slides into it.
            const seam=cake.seamX(k);goal[0]=seam+(goal[0]-seam)*.12;
            const depth=clamp((top-goal[1])/height,0,1);
            if(!cake.cut[k]){
              cake.setWedge(k,depth/.5);
              if(depth>=.5){cake.slice(k);cake.setWedge(-1,0);stage.canvas.dataset.cutSeams=String(cake.cuts);}
            }
          }else{goal[1]=top;cake.setWedge(-1,0);}
        }else cake.setWedge(-1,0);
        place(goal,dt,.03);
      }else{cake.setWedge(-1,0);place(home,dt,.22);}
      cake.step(dt);
    },
    deform(item,i,out,normal){cake.deform(item.sliceIndex,out,normal);},
    squeeze(){
      let k=-1;for(let s=cake.cut.length-1;s>=0;s--)if(!cake.cut[s]){k=s;break;}
      if(k<0){cake.rejoin();stage.canvas.dataset.cutSeams='0';return true;}
      target=null;demo={time:0,start:knife.position.toArray(),home,seamX:cake.seamX(k),top,height};return true;
    },
    release(){target=null;},
    reset(){target=null;demo=null;cake.rejoin();knife.position.fromArray(home);moving=false;stage.canvas.dataset.cutSeams='0';},
    dispose(){knife.userData.dispose();stage.toy.remove(knife);},
  };
}

// A wet print left on the glass: a soft six-lobed shape that fades out.
function makeSplatPrint(){
  const material=new T.ShaderMaterial({transparent:true,depthWrite:false,
    uniforms:{opacity:{value:0},tint:{value:new T.Color('#ffe3ee')},phase:{value:0}},
    vertexShader:'varying vec2 vUv;void main(){vUv=uv*2.-1.;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.);}',
    fragmentShader:`uniform float opacity;uniform vec3 tint;uniform float phase;varying vec2 vUv;
      void main(){float r=length(vUv),a=atan(vUv.y,vUv.x);float edge=1./(1.+.16*cos(6.*a+phase));
      float body=smoothstep(edge,edge*.62,r);float rim=smoothstep(edge*.98,edge*.86,r)-smoothstep(edge*.86,edge*.7,r);
      float spots=smoothstep(.75,.95,sin(vUv.x*19.+phase)*sin(vUv.y*17.-phase));
      gl_FragColor=vec4(tint+vec3(.08)*rim,opacity*(body*.42+rim*.4+spots*body*.12));}`});
  const mesh=new T.Mesh(new T.PlaneGeometry(2,2),material);mesh.name='splat_print';mesh.visible=false;mesh.frustumCulled=false;mesh.renderOrder=10;
  // The print frames nothing: fitting reads the toy, not marks on the glass.
  mesh.geometry.boundingBox=new T.Box3();
  return mesh;
}

function splatOcto(stage){
  if(!stage.body)return null;
  // Thrown about and stuck to the glass, it has no floor of its own.
  stage.body.floor=null;
  const splat=new SplatThrow(stage.body),print=makeSplatPrint();stage.toy.add(print);
  const samples=[];let fade=0,hold=false,printNormal=new T.Vector3(),printPose=null;
  splat.onImpact=()=>{
    if(!stage.state?.reducedMotion)stage.hitstop=.07;
    stage.canvas.dataset.splats=String(splat.impacts);
  };
  const launch=velocity=>{
    if(stage.state?.reducedMotion||!stage.camera||splat.active)return false;
    stage.toy.updateMatrixWorld(true);
    const inverse=stage.toy.matrixWorld.clone().invert(),camera=stage.camera.position.clone().applyMatrix4(inverse);
    const b=stage.body,center=new T.Vector3((b.min[0]+b.max[0])/2,(b.min[1]+b.max[1])/2,(b.min[2]+b.max[2])/2);
    const toward=camera.clone().sub(center),distance=toward.length();toward.normalize();
    // The pane sits a third of the way from the toy to the viewer, so the splat reads larger.
    const point=center.clone().addScaledVector(toward,distance*.34);
    const lateral=new T.Vector3(...velocity).multiplyScalar(.09);
    if(lateral.length()>.55)lateral.setLength(.55);
    printNormal.copy(toward);
    return splat.launch({point:point.toArray(),toward:toward.toArray(),lateral:lateral.toArray()});
  };
  return {
    splat,print,
    get activity(){return splat.active?1:0;},
    get active(){return splat.active||fade>0;},
    get freeFit(){return splat.active||fade>0;},
    step(dt){
      const now=performance.now();
      if(stage.body?.hold&&stage.active&&!stage.active.orbit&&stage.dragTarget){
        samples.push({t:now,p:[...stage.dragTarget]});while(samples.length>8||samples[0].t<now-160)samples.shift();hold=true;
      }else if(hold){hold=false;}
      const before=splat.phase;splat.update(dt);
      if(before==='stuck'&&splat.phase==='sliding'){
        // Measure the contact while it is fullest; the print appears when it peels.
        const t=splat.toward,q=splat.point,centerPoint=new T.Vector3();let n=0;const touching=[];
        for(const item of stage.meshes){
          if(item.isProp)continue;const a=item.mesh.geometry.attributes.position.array;
          for(let i=0;i<a.length;i+=3){const d=(a[i]-q[0])*t[0]+(a[i+1]-q[1])*t[1]+(a[i+2]-q[2])*t[2];if(d>-.06){touching.push(a[i],a[i+1],a[i+2]);centerPoint.x+=a[i];centerPoint.y+=a[i+1];centerPoint.z+=a[i+2];n++;}}
        }
        printPose=null;
        if(n>8){
          centerPoint.multiplyScalar(1/n);let radius=0;
          for(let k=0;k<touching.length;k+=3)radius=Math.max(radius,Math.hypot(touching[k]-centerPoint.x,touching[k+1]-centerPoint.y,touching[k+2]-centerPoint.z));
          printPose={center:centerPoint,radius};
        }
      }
      if(before==='sliding'&&splat.phase==='peeling'&&printPose){
        print.position.copy(printPose.center).addScaledVector(printNormal,.03);
        print.quaternion.setFromUnitVectors(new T.Vector3(0,0,1),printNormal);
        print.scale.setScalar(Math.max(.15,printPose.radius*.9));print.material.uniforms.phase.value=Math.random()*6.28;
        fade=1;print.visible=true;
      }
      if(fade>0&&splat.phase!=='peeling'){fade=Math.max(0,fade-dt/2.6);}
      print.material.uniforms.opacity.value=fade;print.visible=fade>0;
      stage.canvas.dataset.splatPhase=splat.phase;
    },
    pointerUp(){
      if(samples.length<2||!stage.dragTarget)return;
      const first=samples[0],last=samples.at(-1),seconds=(last.t-first.t)/1000;
      if(seconds<=0)return;
      const velocity=last.p.map((v,a)=>(v-first.p[a])/seconds),speed=Math.hypot(...velocity);
      samples.length=0;
      // A quick flick throws; a slow drag just lets go.
      if(speed>3.2&&launch(velocity))stage.canvas.dataset.lastFlick=speed.toFixed(2);
    },
    squeeze(){if(stage.state?.reducedMotion)return false;stage.release('throw-demo');return launch([(Math.random()-.5)*2,1,0]);},
    release(){},
    reset(){splat.cancel();fade=0;print.visible=false;samples.length=0;},
    dispose(){splat.cancel();print.geometry.dispose();print.material.dispose();stage.toy.remove(print);},
  };
}

function snowGlobe(stage){
  const dome=stage.meshes.find(item=>item.mesh.name==='body_globe_dome'),base=stage.meshes.find(item=>item.mesh.name==='body_globe_base');
  if(!dome||!base||!stage.body)return null;
  const box=dome.mesh.geometry.boundingBox,radius=(box.max.x-box.min.x)/2;
  const center=[(box.min.x+box.max.x)/2,box.max.y-radius,(box.min.z+box.max.z)/2];
  const baseTop=base.mesh.geometry.boundingBox.max.y,moundItem=stage.meshes.find(item=>item.mesh.name==='prop_scene_mound');
  const mb=moundItem?.mesh.geometry.boundingBox,mc=mb?.getCenter(new T.Vector3()),ms=mb?.getSize(new T.Vector3()).multiplyScalar(.5);
  const floor=(x,z)=>{
    if(!mb)return baseTop;
    const q=1-((x-mc.x)/ms.x)**2-((z-mc.z)/ms.z)**2;
    return Math.max(baseTop,q>0?mc.y+ms.y*Math.sqrt(q):baseTop);
  };
  const globe=new SnowGlobe({center,radius:radius*.94,floor,count:320});
  const geometry=new T.CircleGeometry(1,6),material=new T.MeshStandardMaterial({color:'#ffffff',roughness:.4,emissive:'#eaf3ff',emissiveIntensity:.35,side:T.DoubleSide});
  const flakes=new T.InstancedMesh(geometry,material,globe.count);flakes.name='snow_flakes';flakes.frustumCulled=false;
  stage.toy.add(flakes);
  const props=stage.meshes.filter(item=>item.isProp&&/^prop_scene/.test(item.mesh.name));
  for(const item of props){item.sceneBinding=stage.body.bind(item.center.toArray());item.sceneEyes=/eyes$/.test(item.mesh.name);item.sceneLids=/lids$/.test(item.mesh.name);}
  const matrix=new T.Matrix4(),q=new T.Quaternion(),axis=new T.Vector3(),pos=new T.Vector3(),scale=new T.Vector3(),point=[0,0,0],out=[0,0,0],sample=[0,0,0];
  let previous=null,awake=false,calm=0;
  const draw=()=>{
    for(let f=0;f<globe.count;f++){
      point[0]=globe.px[f];point[1]=globe.py[f];point[2]=globe.pz[f];sampleField(stage.body,point,out);
      axis.fromArray(globe.axis,f*3);q.setFromAxisAngle(axis,globe.spin[f]);
      const r=globe.flakeRadius*globe.size[f];
      flakes.setMatrixAt(f,matrix.compose(pos.fromArray(out),q,scale.set(r,r,r)));
    }
    flakes.instanceMatrix.needsUpdate=true;
  };
  const eyes=open=>{for(const item of props){if(item.sceneEyes)item.mesh.visible=open;if(item.sceneLids)item.mesh.visible=!open;}};
  draw();eyes(false);
  return {
    globe,flakes,
    get activity(){return globe.activity>0?1:0;},
    get active(){return globe.activity>0;},
    step(dt){
      const hold=stage.body?.hold;
      if(hold&&stage.dragTarget&&dt>0){
        // A finger on the dome stirs the water where it presses.
        const target=stage.dragTarget,inside=[...target],d=Math.hypot(...inside.map((v,a)=>v-center[a]));
        if(d>radius*.85)for(let a=0;a<3;a++)inside[a]=center[a]+(inside[a]-center[a])*radius*.85/d;
        const velocity=previous?target.map((v,a)=>(v-previous[a])/dt*1.4):[0,0,0];
        const push=(stage.pressDirection??[0,0,0]).map(v=>v*(stage.pressAmount??0)*radius*2.2);
        globe.inject(inside,velocity.map((v,a)=>v+push[a]),radius*.75);
        // Moving the dome sloshes all of the water, not only the water under the finger.
        const speed=Math.hypot(...velocity);if(speed>.2)globe.shake(Math.min(.2,speed*dt*.5));
        previous=[...target];
      }else previous=null;
      const was=globe.activity;globe.step(dt);
      if(was>0||globe.activity>0)draw();
      // The bear wakes while snow is in the air and dozes off once it settles.
      if(globe.airborne>globe.count*.2){awake=true;calm=0;}else if(awake&&globe.airborne<globe.count*.05){calm+=dt;if(calm>.8)awake=false;}
      eyes(awake);stage.canvas.dataset.snowAirborne=String(globe.airborne);
    },
    poseProp(item){
      if(!item.sceneBinding)return;stage.body.sample(item.sceneBinding,sample);
      item.mesh.position.set(sample[0]-item.sceneBinding.point[0],sample[1]-item.sceneBinding.point[1],sample[2]-item.sceneBinding.point[2]);
    },
    squeeze(){stage.release('shake-demo');globe.shake(1);return true;},
    release(){previous=null;},
    reset(){globe.reset();draw();awake=false;eyes(false);for(const item of props)item.mesh.position.set(0,0,0);},
    dispose(){stage.toy.remove(flakes);flakes.dispose();geometry.dispose();material.dispose();},
  };
}

function waxLamp(stage){
  const glass=stage.meshes.find(item=>item.mesh.name==='body_lamp_glass'),cap=stage.meshes.find(item=>item.mesh.name==='body_lamp_cap');
  if(!glass||!stage.body)return null;
  const box=glass.mesh.geometry.boundingBox,center=[(box.min.x+box.max.x)/2,0,(box.min.z+box.max.z)/2];
  const bottom=box.min.y+.02,top=Math.min(box.max.y,cap?cap.mesh.geometry.boundingBox.min.y:box.max.y)-.02;
  // Inner radius by height, read from the bottle's own vertices.
  const bins=24,radius=new Float64Array(bins),rest=glass.rest;
  for(let i=0;i<rest.length;i+=3){const b=Math.min(bins-1,Math.max(0,Math.floor((rest[i+1]-box.min.y)/(box.max.y-box.min.y)*bins)));radius[b]=Math.max(radius[b],Math.hypot(rest[i]-center[0],rest[i+2]-center[2]));}
  for(let b=1;b<bins;b++)if(!radius[b])radius[b]=radius[b-1];
  const profile=y=>{const f=clamp((y-box.min.y)/(box.max.y-box.min.y)*bins-.5,0,bins-1.001),b=Math.floor(f),t=f-b;return (radius[b]*(1-t)+radius[Math.min(bins-1,b+1)]*t)*.92;};
  // MarchingCubes only builds surfaces in the inner cells of its grid, so the
  // cube reaches past the cap: wax pressed against the top closes up there,
  // hidden by the cap, instead of being cut flat below it.
  const maxRadius=Math.max(...radius),wide=Math.max(top-bottom,2*maxRadius)*1.26;
  const blobs=new LavaBlobs({center:[center[0],0,center[2]],bottom,top,profile,count:7,radius:wide*.085});
  // Metaballs: field = strength / d^2 - subtract, surface where the field reaches the isolation value.
  const material=new T.MeshPhysicalMaterial({vertexColors:true,color:'#ffffff',roughness:.3,clearcoat:.55,clearcoatRoughness:.18,sheen:.45,sheenColor:new T.Color('#ffe0ec'),emissive:new T.Color('#ff94b6'),emissiveIntensity:.05});
  const lava=new MarchingCubes(28,material,false,true,8000);lava.name='lava_wax';lava.frustumCulled=false;lava.isolation=80;
  lava.position.set(center[0],(bottom+top)/2,center[2]);lava.scale.setScalar(wide/2);lava.geometry.boundingBox=new T.Box3();
  stage.toy.add(lava);
  const subtract=30,base=lava.position.toArray().map((v,a)=>v-wide/2);
  // sRGB pastels, scaled up because the field weight at the surface is a little under one half.
  const palette=['#ff7fa8','#ffa070','#ad8cff','#ffc957','#ff8ccf','#7fbfff','#ff8f80'].map(hex=>new T.Color(hex).multiplyScalar(1.9));
  const point=[0,0,0],out=[0,0,0];let previous=null;
  const draw=()=>{
    lava.reset();
    for(let i=0;i<blobs.count;i++){
      point[0]=blobs.x[i];point[1]=blobs.y[i];point[2]=blobs.z[i];sampleField(stage.body,point,out);
      const r=blobs.r[i]/wide;
      lava.addBall((out[0]-base[0])/wide,(out[1]-base[1])/wide,(out[2]-base[2])/wide,(lava.isolation+subtract)*r*r,subtract,palette[i%palette.length]);
    }
    lava.update();
    // Upload only the triangles this frame produced.
    for(const name of ['position','normal','color']){const a=lava.geometry.getAttribute(name);if(a){a.clearUpdateRanges();a.addUpdateRange(0,lava.count*3);}}
  };
  draw();
  return {
    blobs,lava,
    get activity(){return blobs.activity>0?1:0;},
    get active(){return blobs.activity>0;},
    step(dt){
      const hold=stage.body?.hold;
      if(hold&&stage.dragTarget&&dt>0){
        // Squeezing low on the lamp works the heater; higher up it warms and pushes the wax.
        const at=stage.grabPoint??hold.start,low=(at[1]-bottom)/(top-bottom)<.35;
        if(low)blobs.heatBase(dt*2.2);
        blobs.warm(at,dt*2.4,(stage.pressDirection??[0,0,0]).map(v=>v*(stage.pressAmount??0)*.8));
        if(previous)blobs.stir(stage.dragTarget,stage.dragTarget.map((v,a)=>(v-previous[a])/dt));
        previous=[...stage.dragTarget];
      }else previous=null;
      const was=blobs.activity;blobs.step(dt);
      if(was>0||blobs.activity>0)draw();
      stage.canvas.dataset.lavaTop=Math.max(...blobs.y).toFixed(3);
    },
    squeeze(){stage.release('heat-demo');blobs.heatBase(1.1);return true;},
    release(){previous=null;},
    reset(){blobs.reset();draw();},
    dispose(){stage.toy.remove(lava);lava.geometry.dispose();material.dispose();},
  };
}

function peelMikan(stage){
  const fruit=stage.meshes.find(item=>item.mesh.name==='body_mikan_fruit');
  const items=stage.meshes.filter(item=>/^peel_petal_\d+$/.test(item.mesh.name)).sort((a,b)=>Number(a.mesh.name.slice(11))-Number(b.mesh.name.slice(11)));
  if(!fruit||items.length<3||!stage.body)return null;
  const box=fruit.mesh.geometry.boundingBox,center=box.getCenter(new T.Vector3()).toArray(),size=box.getSize(new T.Vector3());
  // The segment bulges add about 3% around the equator, none at the poles.
  const radii=[size.x/2/1.03,size.y/2,size.z/2/1.03],L=(radii[0]+radii[1]+radii[2])/3,n=13;
  const wrap=a=>Math.atan2(Math.sin(a),Math.cos(a));
  let shell=0;
  const chains=items.map((item,index)=>{
    const r=item.rest,count=r.length/3,lat=new Float64Array(count),lon=new Float64Array(count);
    let sx=0,sz=0,top=-Infinity,bottom=Infinity,low=Infinity,high=0;
    for(let i=0;i<count;i++){
      const qx=(r[i*3]-center[0])/radii[0],qy=(r[i*3+1]-center[1])/radii[1],qz=(r[i*3+2]-center[2])/radii[2],rho=Math.hypot(qx,qy,qz);
      lat[i]=Math.atan2(qy,Math.hypot(qx,qz));lon[i]=Math.atan2(qz,qx);sx+=qx;sz+=qz;
      top=Math.max(top,lat[i]);bottom=Math.min(bottom,lat[i]);low=Math.min(low,rho);high=Math.max(high,rho);
    }
    const middle=Math.atan2(sz,sx),rho=(low+high)/2;let half=0;
    for(let i=0;i<count;i++)if(lat[i]<1.2)half=Math.max(half,Math.abs(wrap(lon[i]-middle)));
    shell+=rho/items.length;
    const rest=[],normal=[],side=[],width=[];
    for(let i=0;i<n;i++){
      const a=top+(bottom-top)*i/(n-1),q=[rho*Math.cos(a)*Math.cos(middle),rho*Math.sin(a),rho*Math.cos(a)*Math.sin(middle)];
      rest.push(...q.map((v,j)=>center[j]+v*radii[j]));
      const g=q.map((v,j)=>v/radii[j]),gl=Math.hypot(...g);normal.push(...g.map(v=>v/gl));
      side.push(-Math.sin(middle),0,Math.cos(middle));width.push(L*rho*Math.cos(a)*Math.sin(half));
    }
    const f=new Float32Array(count);
    for(let i=0;i<count;i++)f[i]=clamp((top-lat[i])/(top-bottom)*(n-1),0,n-1);
    item.petal={index,f,middle};item.behaviorSample=true;item.transportNormals=false;
    return {rest,normal,side,width,inner:(rho-low)*L,depth:(rho-1)*L};
  });
  // The rind shader dimples by rest position, so pores stay put as petals bend.
  for(const item of stage.meshes)if(item.mesh.material.name==='rind_mikan')item.mesh.geometry.setAttribute('restPosition',new T.BufferAttribute(Float32Array.from(item.rest),3));
  const petals=new PeelPetals({chains,center,radii,shell:shell+.01,floor:0,scale:L,
    bind:point=>stage.body.bind(point),sample:(binding,out)=>stage.body.sample(binding,out)});
  // Pith strands: thin instanced cylinders from the fruit to the peel.
  const geometry=new T.CylinderGeometry(1,1,1,6,1,true);geometry.translate(0,.5,0);
  const material=new T.MeshStandardMaterial({color:'#fff3df',roughness:.8});
  const strands=new T.InstancedMesh(geometry,material,petals.maxStrands);strands.name='mikan_strands';strands.count=0;strands.frustumCulled=false;
  strands.boundingBox=new T.Box3();stage.toy.add(strands);
  const matrix=new T.Matrix4(),quaternion=new T.Quaternion(),up=new T.Vector3(0,1,0),direction=new T.Vector3(),position=new T.Vector3(),scale=new T.Vector3();
  const drawStrands=()=>{
    let m=0;
    for(const s of petals.strands){
      const lift=s.state==='stretch'?0:.004*L;
      position.set(s.a[0]+s.normal[0]*lift,s.a[1]+s.normal[1]*lift,s.a[2]+s.normal[2]*lift);
      quaternion.setFromUnitVectors(up,direction.set(s.dir[0],s.dir[1],s.dir[2]));
      // A stretched strand thins as it lengthens.
      const thin=s.state==='stretch'?clamp(Math.sqrt(s.maxLength*.3/Math.max(1e-6,s.length)),.45,1):1;
      strands.setMatrixAt(m++,matrix.compose(position,quaternion,scale.set(s.radius*thin,Math.max(1e-4,s.length),s.radius*thin)));
    }
    strands.count=m;strands.instanceMatrix.needsUpdate=true;
  };
  const eyes=stage.meshes.filter(item=>/^face_mikan_(eyes|glints)$/.test(item.mesh.name)),lids=stage.meshes.filter(item=>item.mesh.name==='face_mikan_lids');
  let awake=false,grab=null,demo=null;
  const setAwake=value=>{awake=value;for(const item of eyes)item.mesh.visible=value;for(const item of lids)item.mesh.visible=!value;stage.canvas.dataset.mikanAwake=value?'1':'0';};
  // Waking up: a small upward stretch through the top of the soft fruit.
  const hop=()=>{
    const b=stage.body;if(stage.state?.reducedMotion)return;
    for(let i=0;i<b.count;i++){const w=Math.max(0,(b.initialRest[i*3+1]-center[1])/radii[1]);b.velocity[i*3+1]+=2.4*L*w;}
    b.activity=1;
  };
  setAwake(false);
  petals.onClosed=()=>setAwake(false);
  const scratch=[0,0,0],attached=[0,0,0];
  // Turn a screen drag into progress along the petal's swing rail. Progress is
  // the drag along the rail's chord as seen on screen: the swing first rises
  // over the top, so a nearest-point search would stall at the start.
  const flat=(p,v)=>{const along=p[0]*v[0]+p[1]*v[1]+p[2]*v[2];return [p[0]-v[0]*along,p[1]-v[1]*along,p[2]-v[2]*along];};
  const startRail=(k,i)=>{
    const rail=petals.rail(k,i),view=viewInToy(),start=petals.railPoint(rail,0),end=petals.railPoint(rail,1);
    const chord=flat(end.map((x,a)=>x-start[a]),view);
    return {rail,view,chord,length:Math.max(1e-9,chord[0]*chord[0]+chord[1]*chord[1]+chord[2]*chord[2]),origin:[...stage.grabPoint]};
  };
  const railFollow=goal=>{
    const d=flat(goal.map((v,a)=>v-grab.origin[a]),grab.view),u=grab.chord;
    const along=(d[0]*u[0]+d[1]*u[1]+d[2]*u[2])/grab.length,s=clamp(along*1.35,0,1);
    // A sideways wiggle off the rail moves the peel a little.
    const off=d.map((x,a)=>(x-u[a]*along)*.4),length=Math.hypot(...off),limit=.3*L;
    if(length>limit)for(let a=0;a<3;a++)off[a]*=limit/length;
    const point=petals.railPoint(grab.rail,s,scratch);
    petals.move([point[0]+off[0],point[1]+off[1],point[2]+off[2]]);
  };
  const viewInToy=()=>stage.camera.getWorldDirection(new T.Vector3()).transformDirection(stage.toy.matrixWorld.clone().invert()).toArray();
  return {
    petals,strands,
    get activity(){return petals.activity>0||demo?1:0;},
    get active(){return petals.activity>0||!!demo;},
    get held(){return !!grab||!!demo;},
    grab(hit){
      const item=stage.meshes.find(m=>m.mesh===hit.object);
      if(!item?.petal||!stage.grabPoint)return;
      const k=item.petal.index,front=petals.front(k),f=item.petal.f[hit.face?.a??0];
      let i;
      if(f<front-.3)i=clamp(Math.round(f),0,front-1);
      else if(front<petals.n-1&&f<front+4.5)i=front;
      else return;
      // The finger takes over from the demonstration.
      if(demo){petals.release();demo=null;}
      grab=startRail(k,i);
      petals.grab(k,i,petals.railPoint(grab.rail,0));
      return 'tool';
    },
    moveTool(goal){if(grab&&goal?.every(Number.isFinite))railFollow(goal);},
    step(dt){
      if(demo){
        demo.t+=dt;const s=Math.min(1,demo.t/1.3);
        petals.move(petals.railPoint(demo.rail,s*s*(3-2*s)));
        if(demo.t>=1.45){petals.release();demo=null;}
      }
      const wasActive=petals.activity>0||petals.strands.length>0;
      petals.step(dt);
      if(wasActive||petals.activity>0)drawStrands();
      const opened=petals.opened;
      if(!awake&&opened>=.7){setAwake(true);hop();}
      stage.canvas.dataset.peelFronts=petals.chains.map(chain=>chain.front).join(',');
      stage.canvas.dataset.pithSnaps=String(petals.snaps);
    },
    sample(item,i,out){
      const k=i*3;stage.body.sample(item.bindings[i],attached);
      petals.skin(item.petal.index,item.rest[k],item.rest[k+1],item.rest[k+2],item.petal.f[i],out,attached);
    },
    squeeze(){
      if(demo)return true;
      if(petals.opened>=.7){petals.close();return true;}
      // Peel the next petal, starting with the one that faces the viewer.
      const view=viewInToy(),facing=Math.atan2(-view[2],-view[0]);
      const order=items.map((item,k)=>k).sort((a,b)=>Math.abs(wrap(items[a].petal.middle-facing))-Math.abs(wrap(items[b].petal.middle-facing)));
      const k=order.find(k=>petals.peeled(k)<.7);if(k===undefined)return false;
      const i=petals.front(k),rail=petals.rail(k,i);
      petals.grab(k,i,petals.railPoint(rail,0));demo={k,rail,t:0};grab=null;return true;
    },
    release(){if(grab){petals.release();grab=null;}},
    reset(){grab=null;demo=null;petals.reset();setAwake(false);drawStrands();stage.canvas.dataset.peelFronts=petals.chains.map(()=>0).join(',');},
    dispose(){stage.toy.remove(strands);strands.dispose();geometry.dispose();material.dispose();},
  };
}

// A voxel surface in a dynamic BufferGeometry that shares the extractor's arrays.
function voxelMesh(surface,material,name){
  const geometry=new T.BufferGeometry();
  for(const [key,array] of [['position',surface.positions],['normal',surface.normals],['sandRest',surface.rests]]){
    geometry.setAttribute(key,new T.BufferAttribute(array,3).setUsage(T.DynamicDrawUsage));
  }
  geometry.setDrawRange(0,0);geometry.boundingBox=new T.Box3();geometry.boundingSphere=new T.Sphere();
  const mesh=new T.Mesh(geometry,material);mesh.name=name;mesh.castShadow=true;mesh.receiveShadow=true;mesh.frustumCulled=false;
  return mesh;
}
function uploadVoxels(mesh,surface){
  const geometry=mesh.geometry,count=surface.count;
  for(const key of ['position','normal','sandRest']){const a=geometry.getAttribute(key);a.clearUpdateRanges();a.addUpdateRange(0,count*3);a.needsUpdate=true;}
  geometry.setDrawRange(0,count);
  if(count){geometry.boundingBox.min.fromArray(surface.min);geometry.boundingBox.max.fromArray(surface.max);}
  else geometry.boundingBox.makeEmpty();
  geometry.boundingBox.getBoundingSphere(geometry.boundingSphere);
}

function smileSand(stage){
  const block=stage.meshes.find(item=>item.mesh.name==='body_sand_block');
  if(!block||!stage.body)return null;
  // The authored block only sets the size; the sand itself is voxels.
  block.mesh.visible=false;block.unpickable=true;
  const box=block.mesh.geometry.boundingBox,min=box.min.toArray(),max=box.max.toArray(),H=max[1]-min[1],D=max[2]-min[2];
  const sand=new SandLoaf({min,max,cell:H/18});
  const surface=new VoxelSurface(sand.nx,sand.ny,sand.nz,60000);
  const material=makeSandMaterial({min,max,period:H*.24});
  const mesh=voxelMesh(surface,material,'sand_voxels');stage.toy.add(mesh);
  let built=-1;
  const rebuild=()=>{if(built===sand.version)return;built=sand.version;surface.build(sand.occupancy,sand.rest,sand.origin,sand.cell);uploadVoxels(mesh,surface);stage.shadowDirty=true;};
  rebuild();
  // Tipping slices: their own small voxel meshes, turned about the outer edge.
  const slabs=new Map();
  const addSlab=slab=>{
    const [a,b]=slab.piece,nx=b-a+3,origin=[sand.origin[0]+(a-1)*sand.cell,sand.origin[1],sand.origin[2]];
    const local=new VoxelSurface(nx,sand.ny,sand.nz,12000),occupancy=new Uint8Array(nx*sand.ny*sand.nz),rest=new Float32Array(occupancy.length*3);
    for(const c of slab.cells){const q=(c.i-a+1)+nx*(c.j+sand.ny*c.k);occupancy[q]=1;rest.set(c.rest,q*3);}
    local.build(occupancy,rest,origin,sand.cell);
    const piece=voxelMesh(local,material,'sand_slice');uploadVoxels(piece,local);piece.matrixAutoUpdate=false;
    stage.toy.add(piece);slabs.set(slab,piece);
  };
  const turnSlabs=()=>{
    for(const [slab,piece] of slabs){
      const [px,py]=slab.pivot,phi=-slab.dir*slab.angle;
      piece.matrix.makeTranslation(px,py,0).multiply(new T.Matrix4().makeRotationZ(phi)).multiply(new T.Matrix4().makeTranslation(-px,-py,0));
      piece.matrixWorldNeedsUpdate=true;
    }
  };
  // Crumbs: a few small grains that hop out where a slice cracks on landing.
  // One instanced mesh per pastel, so no per-instance colour is needed.
  const crumbGeometry=new T.IcosahedronGeometry(1,1),palette=['#cdb8ff','#a8e8cc','#ffe08c','#ffb5c9','#abd9ff'];
  const crumbSets=palette.map(hex=>{
    const set=new T.InstancedMesh(crumbGeometry,new T.MeshStandardMaterial({color:hex,roughness:.95}),16);
    set.name='sand_crumbs';set.count=0;set.frustumCulled=false;set.boundingBox=new T.Box3();set.castShadow=true;stage.toy.add(set);return set;
  });
  const flying=[];
  const matrix=new T.Matrix4(),quaternion=new T.Quaternion(),scale=new T.Vector3(),position=new T.Vector3();
  const spray=(points,impact)=>{
    for(const p of points.concat(Array.from({length:10},()=>impact.map((v,a)=>v+(Math.random()-.5)*(a===1?0:H*.5))))){
      if(flying.length>=palette.length*16)flying.shift();
      flying.push({p:[...p],v:[(Math.random()-.5)*H*1.4,H*(.6+Math.random()*1.4),(Math.random()-.5)*H*1.4],r:sand.cell*(.22+.25*Math.random()),age:0,set:Math.floor(Math.random()*palette.length)});
    }
  };
  const drawCrumbs=dt=>{
    const counts=crumbSets.map(()=>0);
    for(const c of flying){
      c.age+=dt;c.v[1]-=sand.gravity*dt;for(let a=0;a<3;a++)c.p[a]+=c.v[a]*dt;
      if(c.p[1]<c.r){c.p[1]=c.r;c.v[1]*=-.25;c.v[0]*=.6;c.v[2]*=.6;}
      const fade=c.age<2.2?1:Math.max(0,1-(c.age-2.2)/.6),set=crumbSets[c.set];
      if(counts[c.set]<16)set.setMatrixAt(counts[c.set]++,matrix.compose(position.fromArray(c.p),quaternion,scale.setScalar(c.r*fade)));
    }
    for(let i=flying.length-1;i>=0;i--)if(flying[i].age>2.8)flying.splice(i,1);
    crumbSets.forEach((set,i)=>{set.count=counts[i];set.instanceMatrix.needsUpdate=true;});
  };
  sand.onLand=(slab,points)=>{
    const piece=slabs.get(slab);if(piece){stage.toy.remove(piece);piece.geometry.dispose();slabs.delete(slab);}
    const x=slab.pivot[0]+slab.dir*H*.55;spray(points,[x,0,0]);
    // A soft thud: a very short hitstop, as fighting games do on impact.
    if(!stage.state?.reducedMotion)stage.hitstop=Math.max(stage.hitstop??0,.045);
    stage.canvas.dataset.sandLandings=String(sand.landings);
  };
  const knife=makeKnifeTool(H);knife.scale.z=D*1.18/(H*.46);stage.toy.add(knife);
  const home=()=>{const right=sand.blocks.at(-1)??[sand.column(max[0]),sand.column(max[0])];return [sand.origin[0]+(right[1]+.5)*sand.cell-H*.32,max[1]+H*.16,0];};
  knife.position.fromArray(home());
  let mode=null,target=null,locked=null,demo=null,moving=false,pressPoint=null,pressTime=0,pressDepth=null;
  const columnX=i=>sand.origin[0]+(i+.5)*sand.cell;
  const place=(goal,dt,tau)=>{
    const k=1-Math.exp(-dt/tau),p=knife.position;
    p.x+=(goal[0]-p.x)*k;p.y+=(goal[1]-p.y)*k;p.z+=(goal[2]-p.z)*k;
    moving=Math.abs(goal[0]-p.x)+Math.abs(goal[1]-p.y)+Math.abs(goal[2]-p.z)>.0005;if(!moving)p.fromArray(goal);
  };
  const demoTarget=t=>{
    const s=x=>{x=clamp(x,0,1);return x*x*(3-2*x);},mix=(a,b,u)=>a.map((v,i)=>v+(b[i]-v)*s(u));
    const above=[demo.x,max[1]+H*.12,0],floor=[demo.x,0,0],lifted=[demo.x,max[1]+H*.18,0];
    if(t<.3)return mix(demo.start,above,t/.3);
    if(t<1.0)return mix(above,floor,(t-.3)/.7);
    if(t<1.3)return mix(floor,lifted,(t-1.0)/.3);
    return mix(lifted,demo.home,(t-1.3)/.35);
  };
  const surfaceHit=()=>{
    stage.toy.updateMatrixWorld(true);
    const hit=stage.ray.intersectObject(mesh,false)[0];
    return hit?stage.toy.worldToLocal(hit.point.clone()).toArray():null;
  };
  return {
    sand,mesh,knife,
    // Turned so the cut end faces the viewer and slices tip toward them.
    rotation:[.1,-.95,0],
    get toolNormal(){return mode==='knife'?[0,0,1]:undefined;},
    get activity(){return sand.moving||slabs.size||flying.length?1:0;},
    get active(){return sand.moving||slabs.size>0||flying.length>0||moving||!!demo||!!target||!!pressPoint;},
    get held(){return !!target||!!demo||!!pressPoint;},
    pickable(){return [mesh,...knife.children];},
    grab(hit){
      if(hit.object.userData.knifeTool){mode='knife';demo=null;target=knife.position.toArray();stage.grabPoint=[...target];return 'tool';}
      if(hit.object===mesh){
        mode='press';pressPoint=stage.toy.worldToLocal(hit.point.clone()).toArray();pressTime=0;
        // The print deepens only so far below the first touch.
        const d=stage.ray.ray.direction.clone().transformDirection(stage.toy.matrixWorld.clone().invert()).toArray();
        pressDepth={direction:d,limit:pressPoint[0]*d[0]+pressPoint[1]*d[1]+pressPoint[2]*d[2]+H*.13};
        return 'tool';
      }
    },
    moveTool(goal){
      if(mode==='knife'&&goal?.every(Number.isFinite))target=[...goal];
      else if(mode==='press'){const p=surfaceHit();if(p)pressPoint=p;}
    },
    step(dt){
      let goal=null;
      if(demo){demo.time+=dt;goal=demoTarget(demo.time);if(demo.time>=1.65)demo=null;}
      else if(target)goal=[...target];
      if(goal){
        goal[2]=0;goal[1]=clamp(goal[1],0,max[1]+H*1.1);
        goal[0]=clamp(goal[0],sand.origin[0]+sand.cell,sand.origin[0]+(sand.nx-1)*sand.cell);
        const i=locked??sand.column(goal[0]),top=sand.top(i),inBlock=!!sand.blockAt(i);
        if(inBlock&&goal[1]<top-.005){
          // The blade keeps to its slot once it is in the sand.
          if(locked===null)locked=i;
          goal[0]=columnX(locked)+(goal[0]-columnX(locked))*.06;
          sand.carve(locked,goal[1]);
          if(goal[1]<=sand.cell*.55){const slab=sand.finishCut(locked);if(slab)addSlab(slab);stage.canvas.dataset.sandCuts=String(sand.cuts);locked=null;}
        }else if(locked!==null&&goal[1]>=sand.top(locked))locked=null;
        place(goal,dt,.03);
      }else place(home(),dt,.22);
      if(pressPoint){
        // A held fingertip sinks a little deeper; a moving one draws a groove.
        pressTime+=dt;sand.press(pressPoint,H*(.07+.05*Math.min(1,pressTime/.8)),pressDepth?.direction,pressDepth?.limit);
      }
      sand.step(dt);turnSlabs();
      if(flying.length||crumbSets.some(set=>set.count))drawCrumbs(dt);
      rebuild();
    },
    squeeze(){
      if(demo)return true;
      // Slice the thinner end of the longest block; once it is all sliced, mould a fresh loaf.
      const cells=Math.max(2,Math.round(H*.3/sand.cell)),right=sand.blocks.at(-1),left=sand.blocks[0];
      let x=null;
      if(right&&right[1]-right[0]+1>cells+1)x=columnX(right[1]-cells);
      else if(left&&left[1]-left[0]+1>cells+1)x=columnX(left[0]+cells);
      if(x===null){sand.fill();locked=null;target=null;rebuild();stage.canvas.dataset.sandCuts='0';return true;}
      target=null;mode='knife';demo={time:0,start:knife.position.toArray(),home:home(),x};return true;
    },
    release(){target=null;pressPoint=null;mode=null;},
    reset(){target=null;demo=null;locked=null;pressPoint=null;mode=null;sand.fill();for(const piece of slabs.values()){stage.toy.remove(piece);piece.geometry.dispose();}slabs.clear();flying.length=0;drawCrumbs(0);rebuild();knife.position.fromArray(home());stage.canvas.dataset.sandCuts='0';},
    dispose(){
      for(const piece of slabs.values()){stage.toy.remove(piece);piece.geometry.dispose();}
      stage.toy.remove(mesh);mesh.geometry.dispose();material.dispose();for(const set of crumbSets){stage.toy.remove(set);set.material.dispose();set.dispose();}crumbGeometry.dispose();
      knife.userData.dispose();stage.toy.remove(knife);
    },
  };
}

function yolkEgg(stage){
  const white=stage.meshes.find(item=>item.mesh.name==='body_egg_white'),yolk=stage.meshes.find(item=>item.mesh.name==='body_egg_yolk');
  if(!white||!yolk||!stage.body)return null;
  const faces=stage.meshes.filter(item=>/^face_yolk_/.test(item.mesh.name)),dome=[yolk,...faces];
  const box=white.mesh.geometry.boundingBox,yb=yolk.mesh.geometry.boundingBox;
  // Height field over the white: the top of the white in each cell.
  const nx=80,nz=Math.max(8,Math.round(nx*(box.max.z-box.min.z)/(box.max.x-box.min.x)));
  const min=[box.min.x,box.min.z],max=[box.max.x,box.max.z],dx=(max[0]-min[0])/nx,dz=(max[1]-min[1])/nz;
  const terrain=new Float32Array(nx*nz).fill(-Infinity),normals=white.mesh.geometry.attributes.normal.array,rest=white.rest;
  for(let v=0;v<rest.length;v+=3){
    if(normals[v+1]<.25)continue;
    const i=Math.floor((rest[v]-min[0])/dx),j=Math.floor((rest[v+2]-min[1])/dz);
    if(i>=0&&j>=0&&i<nx&&j<nz)terrain[i+j*nx]=Math.max(terrain[i+j*nx],rest[v+1]);
  }
  // Fill cells between vertices, then keep the yolk off the rounded rim.
  for(let pass=0;pass<6;pass++){
    const next=terrain.slice();
    for(let j=0;j<nz;j++)for(let i=0;i<nx;i++){
      const q=i+j*nx;if(terrain[q]>-Infinity)continue;let sum=0,count=0;
      for(const [a,b] of [[i-1,j],[i+1,j],[i,j-1],[i,j+1]])if(a>=0&&b>=0&&a<nx&&b<nz&&terrain[a+b*nx]>-Infinity){sum+=terrain[a+b*nx];count++;}
      if(count>=2)next[q]=sum/count;
    }
    terrain.set(next);
  }
  const mask=new Uint8Array(nx*nz),floorY=box.min.y;
  for(let j=0;j<nz;j++)for(let i=0;i<nx;i++){
    const q=i+j*nx;let inside=terrain[q]>-Infinity;
    for(const [a,b] of [[i-1,j],[i+1,j],[i,j-1],[i,j+1],[i-2,j],[i+2,j],[i,j-2],[i,j+2]])if(a<0||b<0||a>=nx||b>=nz||terrain[a+b*nx]===-Infinity)inside=false;
    mask[q]=inside?1:0;if(terrain[q]===-Infinity)terrain[q]=floorY;
  }
  const flow=new YolkFlow({nx,nz,min,max,terrain,mask});
  const centre=[(yb.min.x+yb.max.x)/2,(yb.min.z+yb.max.z)/2],radius=(yb.max.x-yb.min.x)/2;
  const [ci,cj]=flow.cell(...centre),base=terrain[clamp(ci,0,nx-1)+clamp(cj,0,nz-1)*nx],domeHeight=yb.max.y-base;
  // The puddle: one vertex per cell. Only quads that touch yolk are drawn, and
  // the rim fades out through vertex alpha, so it never shows the grid.
  const positions=new Float32Array(nx*nz*3),indices=new Uint32Array((nx-1)*(nz-1)*6),shown=new Float32Array(nx*nz),smooth=new Float32Array(nx*nz);
  const colors=new Float32Array(nx*nz*4).fill(1);
  const geometry=new T.BufferGeometry();geometry.setAttribute('position',new T.BufferAttribute(positions,3).setUsage(T.DynamicDrawUsage));
  geometry.setAttribute('color',new T.BufferAttribute(colors,4).setUsage(T.DynamicDrawUsage));
  geometry.setIndex(new T.BufferAttribute(indices,1).setUsage(T.DynamicDrawUsage));
  geometry.boundingBox=new T.Box3();geometry.boundingSphere=new T.Sphere();
  const material=new T.MeshPhysicalMaterial({color:'#ff9400',roughness:.24,clearcoat:.9,clearcoatRoughness:.08,vertexColors:true,transparent:true});
  const puddle=new T.Mesh(geometry,material);puddle.name='yolk_puddle';puddle.frustumCulled=false;puddle.visible=false;puddle.castShadow=false;puddle.receiveShadow=true;
  stage.toy.add(puddle);
  const point=[0,0,0],out=[0,0,0];let fade=1;
  const drawPuddle=()=>{
    const w=flow.water;
    // Display only: two light 3x3 passes hide the cell steps and round the rim.
    shown.set(w);
    for(let pass=0;pass<2;pass++){
      smooth.set(shown);
      for(let j=0;j<nz;j++)for(let i=0;i<nx;i++){
        let sum=0,weight=0;
        for(let b=-1;b<=1;b++)for(let a=-1;a<=1;a++){const x=i+a,z=j+b;if(x<0||z<0||x>=nx||z>=nz||!mask[x+z*nx])continue;const k=a||b?1:2;sum+=smooth[x+z*nx]*k;weight+=k;}
        shown[i+j*nx]=weight?sum/weight:0;
      }
    }
    for(let q=0;q<shown.length;q++)shown[q]*=fade;
    for(let j=0;j<nz;j++)for(let i=0;i<nx;i++){
      const q=i+j*nx,k=q*3,wet=shown[q];
      point[0]=min[0]+(i+.5)*dx;point[1]=terrain[q];point[2]=min[1]+(j+.5)*dz;sampleField(stage.body,point,out);
      // Follow the white when it is squeezed.
      positions[k]=out[0];positions[k+1]=out[1]+wet+.004;positions[k+2]=out[2];
      colors[q*4+3]=clamp((wet-.002)/.008,0,1);
    }
    let n=0;
    for(let j=0;j<nz-1;j++)for(let i=0;i<nx-1;i++){
      const a=i+j*nx,b=a+1,c=a+nx,d=c+1;
      if(shown[a]<=.003&&shown[b]<=.003&&shown[c]<=.003&&shown[d]<=.003)continue;
      indices[n++]=a;indices[n++]=c;indices[n++]=b;indices[n++]=b;indices[n++]=c;indices[n++]=d;
    }
    geometry.setDrawRange(0,n);geometry.index.needsUpdate=true;geometry.attributes.position.needsUpdate=true;geometry.attributes.color.needsUpdate=true;geometry.computeVertexNormals();
    geometry.boundingBox.makeEmpty();geometry.boundingSphere.set(new T.Vector3((min[0]+max[0])/2,base,(min[1]+max[1])/2),Math.hypot(max[0]-min[0],max[1]-min[1])/2+domeHeight);
  };
  // Dome scale: 1 whole, 0 spilled.
  let state='whole',scale=1,timer=0,awake=0,volume=0,previous=null;
  for(const item of dome)item.behaviorDeform=true;
  const eyes=faces.filter(item=>/(eyes|glints)$/.test(item.mesh.name)),lids=faces.filter(item=>/lids$/.test(item.mesh.name));
  const look=()=>{const show=scale>.05;for(const item of dome)item.mesh.visible=show;if(show){for(const item of eyes)item.mesh.visible=awake>0;for(const item of lids)item.mesh.visible=awake<=0;}};
  look();
  const pop=()=>{
    if(state!=='whole')return false;
    state='popping';timer=0;fade=1;flow.clear();flow.deposit(centre,radius*.98,domeHeight*.9);volume=flow.volume;puddle.visible=true;
    if(!stage.state?.reducedMotion)stage.hitstop=Math.max(stage.hitstop??0,.05);
    stage.canvas.dataset.yolk='broken';return true;
  };
  return {
    flow,puddle,
    rotation:[.5,-.15,0],
    get activity(){return state!=='whole'&&(flow.activity||state!=='broken')||scale<1&&state!=='broken'||awake>0?1:0;},
    get active(){return this.activity>0;},
    step(dt){
      const hold=stage.body?.hold;
      if(state==='whole'&&hold&&stage.grabPoint){
        // A firm press on the yolk breaks its skin.
        const g=stage.grabPoint,onYolk=Math.hypot(g[0]-centre[0],g[2]-centre[1])<radius*.95&&g[1]>base+domeHeight*.25;
        if(onYolk&&(stage.pressAmount??0)>.6)pop();
      }
      if(state==='broken'&&hold&&stage.dragTarget&&dt>0){
        const t=stage.dragTarget;
        if(previous)flow.push([t[0],t[2]],[t[0]-previous[0],t[2]-previous[2]],radius*.7);
        previous=[...t];
      }else previous=null;
      if(state==='popping'){timer+=dt;scale=Math.max(0,1-timer/.14);if(scale===0)state='broken';}
      if(state==='gathering'&&flow.volumeWithin(centre,radius*1.25)>=volume*.88){state='rising';timer=0;}
      if(state==='rising'){
        timer+=dt;const t=Math.min(1,timer/.38);scale=t*t*(3-2*t);fade=1-scale;
        if(t>=1){state='whole';scale=1;fade=1;flow.clear();puddle.visible=false;awake=1.4;stage.canvas.dataset.yolk='whole';}
      }
      flow.step(dt);
      if(awake>0)awake=Math.max(0,awake-dt);
      if(puddle.visible)drawPuddle();
      look();
    },
    deform(item,i,out){
      if(scale>=1)return;
      // The dome slumps and spreads as it empties.
      const s=Math.max(.02,scale),spread=1+.35*(1-s);
      out[1]=base+(out[1]-base)*s;out[0]=centre[0]+(out[0]-centre[0])*spread;out[2]=centre[1]+(out[2]-centre[1])*spread;
    },
    squeeze(){
      if(state==='whole'){stage.release('pop-demo');return pop();}
      if(state==='broken'){flow.gather(centre,radius);state='gathering';return true;}
      return true;
    },
    release(){previous=null;},
    reset(){state='whole';scale=1;fade=1;awake=0;flow.clear();puddle.visible=false;look();stage.canvas.dataset.yolk='whole';},
    dispose(){stage.toy.remove(puddle);geometry.dispose();material.dispose();},
  };
}

function squirtDuck(stage){
  const body=stage.meshes.find(item=>item.mesh.name==='body_duck'),beak=stage.meshes.find(item=>item.mesh.name==='body_duck_beak');
  const tub=stage.meshes.find(item=>item.mesh.name==='prop_tub_basin');
  if(!body||!beak||!tub||!stage.body)return null;
  const duckItems=stage.meshes.filter(item=>!item.isProp);
  for(const item of duckItems)item.behaviorDeform=true;
  const tb=tub.mesh.geometry.boundingBox,bb=body.mesh.geometry.boundingBox,kb=beak.mesh.geometry.boundingBox;
  const tubCenter=[(tb.min.x+tb.max.x)/2,(tb.min.z+tb.max.z)/2],outer=(tb.max.x-tb.min.x)/2,inner=outer*.84;
  const rim=tb.max.y,waterY=tb.min.y+(tb.max.y-tb.min.y)*.69,size=bb.max.y-bb.min.y;
  const duckCenter=bb.getCenter(new T.Vector3()),duckRadii=bb.getSize(new T.Vector3()).multiplyScalar(.45);
  // Collision shapes: the floating body (lower part) and the head behind the beak.
  const bodyCenter=new T.Vector3(duckCenter.x-duckRadii.x*.1,bb.min.y+(bb.max.y-bb.min.y)*.3,duckCenter.z),bodyRadii=new T.Vector3(duckRadii.x*.95,(bb.max.y-bb.min.y)*.3,duckRadii.z*.95);
  const tip=[kb.max.x-.01,(kb.min.y+kb.max.y)/2,(kb.min.z+kb.max.z)/2];
  const headRadius=(kb.max.y-kb.min.y)*2.3,headCenter=new T.Vector3(kb.min.x-headRadius*.75,tip[1]+headRadius*.25,tip[2]);
  // The tub's water: ripples on a round height grid, drawn as a polar disc.
  const ripple=new RippleWater({n:64,center:tubCenter,radius:inner});
  const rings=28,segments=96,positions=[],indices=[];
  positions.push(tubCenter[0],waterY,tubCenter[1]);
  for(let r=1;r<=rings;r++)for(let s=0;s<segments;s++){const a=s/segments*Math.PI*2,d=inner*r/rings;positions.push(tubCenter[0]+Math.cos(a)*d,waterY,tubCenter[1]+Math.sin(a)*d);}
  for(let s=0;s<segments;s++)indices.push(0,1+(s+1)%segments,1+s);
  for(let r=1;r<rings;r++)for(let s=0;s<segments;s++){const a=1+(r-1)*segments+s,b=1+(r-1)*segments+(s+1)%segments;indices.push(a,b,b+segments,a,b+segments,a+segments);}
  const waterGeometry=new T.BufferGeometry();waterGeometry.setAttribute('position',new T.BufferAttribute(new Float32Array(positions),3).setUsage(T.DynamicDrawUsage));waterGeometry.setIndex(indices);waterGeometry.computeVertexNormals();
  const waterMaterial=new T.MeshPhysicalMaterial({color:'#a9e2ff',roughness:.05,clearcoat:1,clearcoatRoughness:.03,transparent:true,opacity:.6,depthWrite:false});
  const water=new T.Mesh(waterGeometry,waterMaterial);water.name='tub_water';water.renderOrder=5;stage.toy.add(water);
  const drawWater=()=>{
    const a=waterGeometry.attributes.position.array;
    // Waves fade out at the wall, so the water's edge stays on the tub's side.
    for(let v=0;v<a.length;v+=3){const edge=clamp((inner-Math.hypot(a[v]-tubCenter[0],a[v+2]-tubCenter[1]))/(inner*.08),0,1);a[v+1]=waterY+ripple.height(a[v],a[v+2])*1.2*edge;}
    waterGeometry.attributes.position.needsUpdate=true;waterGeometry.computeVertexNormals();
  };
  // The squirt is a jet (JetStream) that leaves the beak forward and a little
  // up, as a bath duck shoots water, and arcs into the tub. Gravity is about a
  // quarter of real for a duck this size, so the arc is quick but readable.
  // The strongest squeeze lands four fifths of the way to the far side of the
  // tub; the jet speed follows how fast the duck is being squeezed.
  const h=size*.05,jetGravity=size*35,angle=28*Math.PI/180;let bob=0,bobSpeed=0;
  const lift=tip[1]-waterY,reachX=(tubCenter[0]+inner-tip[0])*.8,cos=Math.cos(angle),tan=Math.tan(angle);
  const vmax=Math.sqrt(jetGravity*reachX*reachX/(2*cos*cos*(reachX*tan+lift)));
  const jet=new JetStream({capacity:240,gravity:jetGravity,radius:size*.034,interval:1/120,segments:10});
  const jetMaterial=new T.MeshPhysicalMaterial({color:'#c4e8ff',roughness:.04,clearcoat:1,clearcoatRoughness:.02,ior:1.33,transparent:true,opacity:.75,depthWrite:false,envMapIntensity:1.3});
  // Like a glass rod, the stream is clear where it faces the eye and denser
  // toward its outline, with a bright line down its middle.
  jetMaterial.onBeforeCompile=shader=>{shader.fragmentShader=shader.fragmentShader.replace('#include <opaque_fragment>',`#include <opaque_fragment>
    float facing=abs(dot(normalize(geometryNormal),normalize(geometryViewDir))),edge=1.-facing,shine=pow(facing,24.);
    gl_FragColor.rgb=gl_FragColor.rgb*(1.-.18*edge)+vec3(.6)*shine;gl_FragColor.a=clamp(gl_FragColor.a*(.55+.6*pow(edge,1.5))+.35*shine,0.,1.);`);};
  const jetMesh=new T.Mesh(jet.geometry,jetMaterial);jetMesh.name='duck_jet';jetMesh.frustumCulled=false;jetMesh.renderOrder=6;jetMesh.unpickable=true;stage.toy.add(jetMesh);
  const tipBinding=stage.body.bind(tip),origin=[0,0,0];let flow=0;
  // Contacts move the previous position with the particle, so a push out of
  // the duck never turns into a burst of speed.
  const push=(x,prev,k,center,radii)=>{
    const dx=(x[k]-center.x)/radii.x,dy=(x[k+1]-center.y-bob)/radii.y,dz=(x[k+2]-center.z)/radii.z,d=Math.hypot(dx,dy,dz);
    if(d>=1||d<1e-6)return;
    const s=1/d,nx=center.x+dx*s*radii.x,ny=center.y+bob+dy*s*radii.y,nz=center.z+dz*s*radii.z;
    prev[k]+=nx-x[k];prev[k+1]+=ny-x[k+1];prev[k+2]+=nz-x[k+2];x[k]=nx;x[k+1]=ny;x[k+2]=nz;
  };
  const headRadii=new T.Vector3();
    // Splash drops that jump where the jet lands: particles, absorbed when they
  // fall back. Almost no pressure, so drops born close together do not burst apart.
  const fluid=new ParticleFluid({capacity:300,radius:h,viscosity:.06,stiffness:.002,nearStiffness:.004,gravity:-jetGravity,collide:(x,v,prev,i)=>{
    const k=i*3;
    if(fluid.age[i]>.12){push(x,prev,k,bodyCenter,bodyRadii);push(x,prev,k,headCenter,headRadii.setScalar(headRadius));}
    const r=Math.hypot(x[k]-tubCenter[0],x[k+2]-tubCenter[1]);
    if(r>=inner&&r<=outer&&x[k+1]<rim){x[k+1]=rim;prev[k]+=(x[k]-prev[k])*.5;prev[k+2]+=(x[k+2]-prev[k+2])*.5;}
    if(x[k+1]<0){x[k+1]=0;prev[k]+=(x[k]-prev[k])*.6;prev[k+2]+=(x[k+2]-prev[k+2])*.6;}
  }});
  const fluidView=new ScreenSpaceFluid({capacity:300,radius:h*.72,tint:'#e4f6ff',absorption:[.9,.32,.1],opacity:.55});stage.toy.add(fluidView.mesh);
  let water01=1,previousPress=0,resting=new Float32Array(300);
  // Where the jet meets the tub's water it rings the surface, and now and then
  // a splash drop jumps up. Points that hit the rim or miss the tub are gone.
  const land=p=>{
    const r=Math.hypot(p.x-tubCenter[0],p.z-tubCenter[1]);
    if(r<inner){
      const surface=waterY+ripple.height(p.x,p.z);if(p.y>surface)return false;
      const strength=Math.min(1,p.speed/vmax);ripple.disturb(p.x,p.z,-.0024*(strength+.3),h*1.3);
      if(Math.random()<.55*strength&&fluid.count<fluid.capacity){
        const a=Math.random()*Math.PI*2,out=size*(.15+.35*Math.random());
        fluid.add(p.x,surface+h*.3,p.z,p.vx*.2+Math.cos(a)*out,size*(1.2+1.4*Math.random()),p.vz*.2+Math.sin(a)*out);
      }
      return true;
    }
    return (r<=outer&&p.y<rim)||p.y<0;
  };
  const ringAround=(amount)=>{for(let s=0;s<10;s++){const a=s/10*Math.PI*2;ripple.disturb(duckCenter.x+Math.cos(a)*duckRadii.x*1.1,duckCenter.z+Math.sin(a)*duckRadii.z*1.1,amount,size*.12);}};
  return {
    fluid,jet,ripple,water,fluidView,
    rotation:[.18,-.4,0],
    get activity(){return fluid.count||jet.count||ripple.activity||Math.abs(bobSpeed)>1e-4||Math.abs(bob)>1e-4||water01<1?1:0;},
    get active(){return this.activity>0;},
    step(dt){
      if(!(dt>0))return;
      const held=!!stage.body?.hold,press=held?stage.pressAmount??0:0,rate=(press-previousPress)/dt;previousPress=press;
      // Squeezing pushes water out of the beak, faster for a harder squeeze.
      jet.step(dt,land);
      if(rate>0&&water01>0){
        // The squeeze rate comes from frame differences; smoothing it keeps uneven
        // frame times from shaking the jet into a zigzag.
        if(!(flow>0))toySound.event('squirt',.6+.4*Math.min(1,rate*1.6),.6);
        const target=Math.min(1,rate*1.6);flow=flow>0?flow+(target-flow)*(1-Math.exp(-dt/.06)):target;const speed=vmax*(.15+.85*flow);
        // The jet starts just inside the beak, so its round end stays hidden.
        stage.body.sample(tipBinding,origin);origin[0]-=jet.radius*1.4;origin[1]+=bob;
        const made=jet.emit(origin,[cos*speed,Math.sin(angle)*speed,0],dt);
        water01=Math.max(0,water01-made*speed/vmax/80);
      }else{jet.stop();flow=0;}
      jet.build();
      // Letting go draws water back in through the hole underneath.
      if(rate<0&&water01<1){water01=Math.min(1,water01-rate*.7);ripple.disturb(duckCenter.x,duckCenter.z,rate*.0015,size*.25);}
      if(!held&&water01<1)water01=Math.min(1,water01+dt*.25);
      // A press sinks the duck a little; buoyancy brings it back, with ripples.
      const push=held?-press*size*.09:0,acc=(push-bob)*55-bobSpeed*5.5;bobSpeed+=acc*dt;bob+=bobSpeed*dt;
      if(Math.abs(bobSpeed)>.02)ringAround(-bobSpeed*dt*.6);
      if(!held&&Math.abs(bob)<1e-4&&Math.abs(bobSpeed)<1e-4){bob=0;bobSpeed=0;}
      for(let s=0;s<2;s++)fluid.step(dt/2);
      // Drops that reach the tub's water join it as ripples; drops elsewhere fade.
      const x=fluid.x,v=fluid.v;
      for(let i=fluid.count-1;i>=0;i--){
        const k=i*3,r=Math.hypot(x[k]-tubCenter[0],x[k+2]-tubCenter[1]);
        if(r<inner&&x[k+1]<waterY+ripple.height(x[k],x[k+2])){ripple.disturb(x[k],x[k+2],Math.max(-.02,v[k+1]*.0035),h*1.6);fluid.remove(i);resting[i]=resting[fluid.count];continue;}
        // A drop that comes down on the rim or outside the tub runs off and is gone.
        if(r>=inner&&x[k+1]<=rim+h*.2){fluid.remove(i);resting[i]=resting[fluid.count];continue;}
        const speed=Math.hypot(v[k],v[k+1],v[k+2]);resting[i]=speed<.2?resting[i]+dt:0;
        if(resting[i]>.6||fluid.age[i]>6){fluid.remove(i);resting[i]=resting[fluid.count];}
      }
      ripple.step(dt);drawWater();
      stage.canvas.dataset.duckWater=water01.toFixed(2);stage.canvas.dataset.duckDrops=String(fluid.count);
    },
    deform(item,i,out){out[1]+=bob;},
    beforeRender(renderer,camera){fluidView.update(fluid.x,fluid.count);fluidView.render(renderer,camera,stage.toy.matrixWorld);},
    squeezePoint(b){return {point:[duckCenter.x-duckRadii.x*.2,duckCenter.y+duckRadii.y*.3,b.max[2]*.8],direction:[0,-.35,-1]};},
    release(){},
    reset(){fluid.clear();jet.clear();ripple.now.fill(0);ripple.before.fill(0);ripple.activity=0;bob=0;bobSpeed=0;water01=1;previousPress=0;drawWater();},
    dispose(){stage.toy.remove(water);waterGeometry.dispose();waterMaterial.dispose();stage.toy.remove(fluidView.mesh);fluidView.dispose();stage.toy.remove(jetMesh);jet.geometry.dispose();jetMaterial.dispose();},
  };
}

// A wooden honey dipper with honey on its grooved head. The origin is the tip.
function makeHoneyDipper(size){
  const group=new T.Group();group.name='honey_dipper';
  const wood=new T.MeshPhysicalMaterial({color:'#d8a165',roughness:.55,clearcoat:.3,clearcoatRoughness:.4});
  const honey=new T.MeshPhysicalMaterial({color:'#ffa51f',roughness:.12,clearcoat:1,clearcoatRoughness:.05,sheen:.3,sheenColor:new T.Color('#ffd27a')});
  const handle=new T.Mesh(new T.CylinderGeometry(size*.022,size*.026,size*.62,16),wood);handle.position.y=size*.46;
  group.add(handle);
  for(let i=0;i<5;i++){
    const ring=new T.Mesh(new T.TorusGeometry(size*(.06-.004*Math.abs(i-2)),size*.018,10,24),i%2?honey:wood);
    ring.rotation.x=Math.PI/2;ring.position.y=size*(.045+i*.035);group.add(ring);
  }
  const core=new T.Mesh(new T.SphereGeometry(size*.055,20,14),honey);core.scale.set(1,1.6,1);core.position.y=size*.11;group.add(core);
  const drop=new T.Mesh(new T.SphereGeometry(size*.03,16,12),honey);drop.position.y=size*.015;drop.scale.set(1,1.3,1);group.add(drop);
  for(const mesh of group.children){mesh.userData.honeyDipper=true;mesh.castShadow=true;}
  group.userData.dispose=()=>{for(const mesh of group.children)mesh.geometry.dispose();wood.dispose();honey.dispose();};
  return group;
}

function honeyToast(stage){
  const toast=stage.meshes.find(item=>item.mesh.name==='body_honeytoast'),butter=stage.meshes.find(item=>item.mesh.name==='body_honeytoast_butter');
  if(!toast||!stage.body)return null;
  const tb=toast.mesh.geometry.boundingBox,center=tb.getCenter(new T.Vector3()),half=tb.getSize(new T.Vector3()).multiplyScalar(.5),size=half.x*2;
  const corner=half.x*.32,top=tb.max.y;
  const bb=butter?.mesh.geometry.boundingBox,butterCenter=bb?.getCenter(new T.Vector3()),butterHalf=bb?.getSize(new T.Vector3()).multiplyScalar(.5);
  // Rounded-box distance and outward normal (Quilez's sdRoundBox).
  const q=[0,0,0];
  const box=(p,c,hx,hy,hz,r,out)=>{
    q[0]=Math.abs(p[0]-c.x)-(hx-r);q[1]=Math.abs(p[1]-c.y)-(hy-r);q[2]=Math.abs(p[2]-c.z)-(hz-r);
    const ox=Math.max(q[0],0),oy=Math.max(q[1],0),oz=Math.max(q[2],0),outside=Math.hypot(ox,oy,oz),inside=Math.min(Math.max(q[0],q[1],q[2]),0);
    if(outside>1e-9){out[0]=ox/outside*Math.sign(p[0]-c.x);out[1]=oy/outside*Math.sign(p[1]-c.y);out[2]=oz/outside*Math.sign(p[2]-c.z);}
    else{const a=q[0]>q[1]?(q[0]>q[2]?0:2):(q[1]>q[2]?1:2);out[0]=out[1]=out[2]=0;out[a]=Math.sign([p[0]-c.x,p[1]-c.y,p[2]-c.z][a])||1;}
    return outside+inside-r;
  };
  const h=size*.055,normal=[0,0,0],point=[0,0,0];
  const stick=(x,prev,k,d,n)=>{
    // Push out, keep the motion along the surface only, and let honey cling.
    const push=-d;x[k]+=n[0]*push;x[k+1]+=n[1]*push;x[k+2]+=n[2]*push;
    let vx=x[k]-prev[k],vy=x[k+1]-prev[k+1],vz=x[k+2]-prev[k+2];const vn=vx*n[0]+vy*n[1]+vz*n[2];
    if(vn<0){vx-=vn*n[0];vy-=vn*n[1];vz-=vn*n[2];}
    const cling=n[1]>.6?.3:.16;
    prev[k]=x[k]-vx*(1-cling);prev[k+1]=x[k+1]-vy*(1-cling);prev[k+2]=x[k+2]-vz*(1-cling);
  };
  const fluid=new ParticleFluid({capacity:650,radius:h,viscosity:14,viscosityQuadratic:1,stretchViscosity:true,stiffness:.018,nearStiffness:.05,gravity:-size*3.4,collide:(x,v,prev,i)=>{
    const k=i*3;point[0]=x[k];point[1]=x[k+1];point[2]=x[k+2];
    const skin=h*.35,d=box(point,center,half.x,half.y,half.z,corner,normal);
    if(d<skin)stick(x,prev,k,d-skin,normal);
    if(butter){point[0]=x[k];point[1]=x[k+1];point[2]=x[k+2];const e=box(point,butterCenter,butterHalf.x,butterHalf.y,butterHalf.z,butterHalf.y*.6,normal);if(e<skin)stick(x,prev,k,e-skin,normal);}
    if(x[k+1]<skin){x[k+1]=skin;prev[k]+=(x[k]-prev[k])*.7;prev[k+2]+=(x[k+2]-prev[k+2])*.7;if(prev[k+1]<x[k+1]-1e-4)prev[k+1]=x[k+1];}
  }});
  const fluidView=new ScreenSpaceFluid({capacity:650,radius:h*.8,tint:'#ffd27a',absorption:[.06,.26,.85],opacity:.9,gloss:140});stage.toy.add(fluidView.mesh);
  // Honey runs on the toast's rest shape, where its collision box is exact, and
  // is drawn moved with the bread: each drop takes the cage's displacement at
  // its place. Drops on the bread move fully; the falling thread fades out of
  // it with height, so it still hangs from the dipper.
  const shown=new Float32Array(650*3),moved=[0,0,0];
  const drawn=()=>{
    const x=fluid.x;
    for(let i=0;i<fluid.count;i++){
      const k=i*3;point[0]=x[k];point[1]=x[k+1];point[2]=x[k+2];
      const w=1-clamp((box(point,center,half.x,half.y,half.z,corner,normal)-h)/(h*3),0,1);
      if(w>0)stage.body.displacementAt(point,moved);
      shown[k]=x[k]+(w>0?moved[0]*w:0);shown[k+1]=x[k+1]+(w>0?moved[1]*w:0);shown[k+2]=x[k+2]+(w>0?moved[2]*w:0);
    }
    return shown;
  };
  // Soak: honey near the bread darkens and glosses it, and stays until reset.
  const rest=toast.rest,soak=new Float32Array(rest.length/3),soakAttribute=new T.BufferAttribute(soak,1);
  toast.mesh.geometry.setAttribute('honeySoak',soakAttribute);
  // Bread vertices bucketed once; each drop then soaks the vertices within reach.
  const soakCell=h*1.2,cellKey=(x,y,z)=>((Math.floor(x/soakCell)+512)*1024+(Math.floor(y/soakCell)+512))*1024+(Math.floor(z/soakCell)+512);
  const vertexCells=new Map();
  for(let v=0;v<soak.length;v++){const key=cellKey(rest[v*3],rest[v*3+1],rest[v*3+2]);let list=vertexCells.get(key);if(!list)vertexCells.set(key,list=[]);list.push(v);}
  const reach=(h*.95)**2;
  const updateSoak=dt=>{
    const x=fluid.x;let changed=false;
    for(let i=0;i<fluid.count;i++){
      const px=x[i*3],py=x[i*3+1],pz=x[i*3+2];
      for(let dz=-1;dz<=1;dz++)for(let dy=-1;dy<=1;dy++)for(let dx=-1;dx<=1;dx++){
        const list=vertexCells.get(cellKey(px+dx*soakCell,py+dy*soakCell,pz+dz*soakCell));if(!list)continue;
        for(const v of list){
          if(soak[v]>=1)continue;
          const ex=rest[v*3]-px,ey=rest[v*3+1]-py,ez=rest[v*3+2]-pz;
          if(ex*ex+ey*ey+ez*ez<reach){soak[v]=Math.min(1,soak[v]+dt*.8);changed=true;}
        }
      }
    }
    if(changed){soakAttribute.needsUpdate=true;stage.dirty=true;}
  };
  // The face wakes up once honey reaches it.
  const eyes=stage.meshes.filter(item=>/^face_toast_(eyes|glints)$/.test(item.mesh.name)),lids=stage.meshes.filter(item=>item.mesh.name==='face_toast_lids');
  const faceBox=new T.Box3();for(const item of stage.meshes)if(/^face_toast_/.test(item.mesh.name))faceBox.union(item.mesh.geometry.boundingBox);
  faceBox.expandByScalar(h*1.5);const probe=new T.Vector3();
  let happy=0;const look=()=>{for(const item of eyes)item.mesh.visible=happy>0;for(const item of lids)item.mesh.visible=happy<=0;stage.canvas.dataset.toastHappy=happy>0?'1':'0';};look();
  const dipper=makeHoneyDipper(size);stage.toy.add(dipper);
  const home=[center.x+half.x*1.25,top+size*.18,center.z+half.z*.2];dipper.position.fromArray(home);
  let target=null,demo=null,honeyLeft=1,stream=0,moving=false,previousTip=null,frame=0,lifted=0;
  const place=(goal,dt,tau)=>{
    const k=1-Math.exp(-dt/tau),p=dipper.position;p.x+=(goal[0]-p.x)*k;p.y+=(goal[1]-p.y)*k;p.z+=(goal[2]-p.z)*k;
    moving=Math.abs(goal[0]-p.x)+Math.abs(goal[1]-p.y)+Math.abs(goal[2]-p.z)>.0005;if(!moving)p.fromArray(goal);
  };
  // A thin thread of honey leaves the tip, one particle spacing at a time.
  const emit=dt=>{
    const tip=dipper.position,speed=size*.75,gap=fluid.spacing;
    const vx=previousTip?(tip.x-previousTip[0])/dt*.4:0,vz=previousTip?(tip.z-previousTip[2])/dt*.4:0;
    stream+=speed*dt;
    while(stream>=gap&&honeyLeft>0&&fluid.count<fluid.capacity-2){
      stream-=gap;const age=stream/speed;
      for(let n=0;n<3;n++){const a=n/3*Math.PI*2+Math.random()*.5,r=gap*.4;fluid.add(tip.x+Math.cos(a)*r,tip.y-speed*age,tip.z+Math.sin(a)*r,vx,-speed,vz);}
      honeyLeft=Math.max(0,honeyLeft-1/200);
    }
  };
  return {
    fluid,dipper,soak,drawn,
    rotation:[.12,-.5,0],
    toolNormal:[0,0,1],
    get activity(){return fluid.count&&(target||demo||fluid.v.subarray(0,fluid.count*3).some(v=>Math.abs(v)>.02))||happy>0||moving?1:0;},
    get active(){return this.activity>0||!!target||!!demo;},
    get held(){return !!target||!!demo;},
    pickable(){return dipper.children;},
    grab(hit){if(hit.object.userData.honeyDipper){demo=null;target=dipper.position.toArray();stage.grabPoint=[...target];return 'tool';}},
    moveTool(goal){if(goal?.every(Number.isFinite))target=[...goal];},
    step(dt){
      if(!(dt>0))return;
      let goal=null;
      if(demo){
        demo.t+=dt;const a=demo.t*2.2,r=half.x*(.3+.45*Math.min(1,demo.t/3));
        goal=demo.t<4?[center.x+Math.cos(a)*r,top+size*.42,center.z+Math.sin(a)*r]:null;
        if(!goal)demo=null;
      }else if(target)goal=[...target];
      if(goal){
        goal[1]=Math.max(goal[1],top+size*.12);place(goal,dt,demo?.12:.05);lifted+=dt;
        // Honey starts to fall a moment after the dipper is lifted; it runs out and refills at rest.
        if(honeyLeft>0&&lifted>(demo?.7:.25))emit(dt);
      }else{place(home,dt,.25);stream=0;lifted=0;honeyLeft=Math.min(1,honeyLeft+dt*.4);}
      previousTip=dipper.position.toArray();
      fluid.step(dt/2);fluid.step(dt/2);
      if(++frame%3===0)updateSoak(dt*3);
      let onFace=0;const x=fluid.x;
      for(let i=0;i<fluid.count;i++)if(faceBox.containsPoint(probe.set(x[i*3],x[i*3+1],x[i*3+2])))onFace++;
      if(onFace>4)happy=2.5;else happy=Math.max(0,happy-dt);
      look();stage.canvas.dataset.honeyDrops=String(fluid.count);
    },
    beforeRender(renderer,camera){fluidView.update(drawn(),fluid.count);fluidView.render(renderer,camera,stage.toy.matrixWorld);},
    squeeze(){demo={t:0};target=null;return true;},
    release(){target=null;},
    reset(){target=null;demo=null;fluid.clear();soak.fill(0);soakAttribute.needsUpdate=true;honeyLeft=1;stream=0;happy=0;look();dipper.position.fromArray(home);},
    dispose(){stage.toy.remove(dipper);dipper.userData.dispose();stage.toy.remove(fluidView.mesh);fluidView.dispose();},
  };
}

// A crunchy candy shell (halloween-shells.js) on the stage's forty pieces:
// crack fronts spread from the finger as stress loads, cracked pieces sink and
// tilt, broken pieces fly as shards, and crumbs puff out.
// shatter: the share of hard landings that burst into crumbs (packed snow: all).
function candyShell(stage,record,kind,{lining,onBreak,onSettle,shatter=.75,landing=1.2}={}){
  const shells=stage.meshes.filter(item=>item.isShell);
  if(!shells.length||!stage.crackle||!stage.body)return null;
  const shape=SHAPES[kind],random=seeded(record.id??record.kind),box=new T.Box3();
  for(const item of shells){item.mesh.geometry.computeBoundingBox();box.union(item.mesh.geometry.boundingBox);}
  const scale=Math.max(box.max.x,-box.min.x)/shape.maxX,center=new T.Vector3(0,(shape.C-shape.bottom)*scale,0);
  const uniforms=shellUniforms(shape,scale),web=new CrackWeb(uniforms.crackOrigins);
  for(const item of shells){rimWeights(item.mesh.geometry,stage.shells[item.shellIndex].center.toArray(),center);patchShell(item.mesh.material,kind,uniforms);}
  const dirs=stage.shells.map(g=>g.center.clone().sub(center).normalize().toArray());
  // Shards touch the floor at eight of their own vertices.
  const pieces=stage.shells.map((group,i)=>{
    const rest=shells.find(item=>item.shellIndex===i).rest,n=rest.length/3,points=[];
    for(let k=0;k<8;k++){const v=Math.floor(k/8*n)*3;points.push([rest[v]-group.center.x,rest[v+1]-group.center.y,rest[v+2]-group.center.z]);}
    return {center:group.center.toArray(),points,normal:group.center.clone().sub(center).normalize().toArray()};
  });
  // Thin candy shatters when it lands hard: most shards burst into crumbs on
  // the floor, a few stay whole, so the sweets are not buried under the shell.
  // Crumbs take the shell's own colour (a rare toy's too), a lighter shade, and the lining.
  const base=shells[0].mesh.material.color,colors=[base.clone(),base.clone().lerp(new T.Color(1,1,1),.3),new T.Color(lining)];
  const crumbs=new Crumbs(stage.toy,{colors,random,size:.026*scale});
  const shards=new Shards(pieces,{random,onImpact:(i,speed)=>{
    toySound.tick(speed/(3*scale));
    if(speed<landing*scale||random()>shatter)return false;
    const p=shards.items[i].p;crumbs.burst([p[0],.02,p[2]],[0,1,0],14,.9*scale);return true;
  }});
  const tilt=stage.shells.map(()=>[(random()-.5)*.14,(random()-.5)*.14,(random()-.5)*.14]);
  let ages=Float64Array.from(stage.crackle.ages),steps=0;
  // Support: pieces touch their near neighbours. A piece with no chain of
  // whole pieces down to the bottom ring has nothing to hold it, so it falls
  // too, a moment later; a cap over a broken ring caves in.
  // Pieces are neighbours when their borders meet: outer-edge vertices of two
  // pieces within a hair of each other, found through a spatial hash.
  const centers=stage.shells.map(g=>g.center),low=Math.min(...centers.map(c=>c.y)),cell=.05*scale,touch=cell*cell,grid=new Map(),neighbours=centers.map(()=>new Set());
  const key=(x,y,z)=>`${Math.floor(x/cell)},${Math.floor(y/cell)},${Math.floor(z/cell)}`;
  for(const item of shells){
    const g=item.mesh.geometry,p=g.attributes.position.array,r=g.attributes.crackRim.array,i=item.shellIndex;
    for(let v=0;v<r.length;v++){
      if(r[v]<.5)continue;const x=p[v*3],y=p[v*3+1],z=p[v*3+2],cx=Math.floor(x/cell),cy=Math.floor(y/cell),cz=Math.floor(z/cell);
      for(let a=-1;a<=1;a++)for(let b=-1;b<=1;b++)for(let c=-1;c<=1;c++)for(const [j,ox,oy,oz] of grid.get(`${cx+a},${cy+b},${cz+c}`)??[]){
        if(j!==i&&(x-ox)**2+(y-oy)**2+(z-oz)**2<touch){neighbours[i].add(j);neighbours[j].add(i);}
      }
      const k=key(x,y,z);if(!grid.has(k))grid.set(k,[]);grid.get(k).push([i,x,y,z]);
    }
  }
  const ground=centers.map(c=>c.y<low+(center.y-low)*.45);
  const collapse=()=>{
    const crackle=stage.crackle,whole=i=>crackle.ages[i]<0&&!crackle.pending.some(p=>p.i===i),held=new Set(),queue=[];
    centers.forEach((_,i)=>{if(ground[i]&&whole(i)){held.add(i);queue.push(i);}});
    while(queue.length){const i=queue.pop();for(const j of neighbours[i])if(!held.has(j)&&whole(j)){held.add(j);queue.push(j);}}
    let k=0;centers.forEach((_,i)=>{if(whole(i)&&!held.has(i))crackle.pending.push({i,at:crackle.clock+.06+.05*k++});});
  };
  const launch=(i,settle=false)=>{
    // Shards pop out a little and mostly drop around the base, so they do not cover the toy.
    const c=stage.shells[i].center,d=dirs[i],out=settle?.35+.3*random():.45+.45*random();
    shards.launch(i,c.toArray(),[d[0]*out+(random()-.5)*.25,.6+.4*random(),d[2]*out+(random()-.5)*.25],[(random()-.5)*9,(random()-.5)*9,(random()-.5)*9]);
    if(!settle){crumbs.burst(c.toArray(),d,7,1.3*scale);toySound.snap();}
  };
  // Saved damage: those pieces already lie on the floor. A reset restores the
  // saved damage after the behaviour resets, so settle again on the next step.
  const settle=()=>toySound.hush(()=>{
    const list=[];for(let i=0;i<stage.crackle.ages.length;i++)if(stage.crackle.ages[i]>=0){launch(i,true);list.push(i);}
    for(let t=0;t<4&&shards.active;t+=1/60)shards.step(1/60);
    ages=Float64Array.from(stage.crackle.ages);return list;
  });
  const saved=settle();let resettle=false;
  const crack=(point,reach)=>{
    web.add(point,reach);toySound.hairline(steps);
    if(!stage.state?.reducedMotion)stage.hitstop=Math.max(stage.hitstop??0,.028);
    const d=new T.Vector3().fromArray(point).sub(center).normalize().toArray();crumbs.burst(point,d,4,1.1*scale);
  };
  return {
    shells,center,scale,dirs,web,shards,crumbs,saved,random,
    step(dt){
      // settle() must run even without onSettle: an optional call skips its arguments.
      if(resettle){resettle=false;const list=settle();onSettle?.(list);}
      const crackle=stage.crackle,contact=stage.body?.hold&&stage.dragTarget?stage.dragTarget:null;
      // Quick cracks while the stress loads, before the snap.
      if(contact&&!crackle.lockout)for(const at of [.3,.62])if(steps<2&&crackle.charge>=at&&steps===[.3,.62].indexOf(at)){crack(contact,scale*(.34+.3*crackle.charge));steps++;}
      if(crackle.charge<.05)steps=0;
      let broke=false;
      for(let i=0;i<ages.length;i++)if(ages[i]<0&&crackle.ages[i]>=0){launch(i);web.add(stage.shells[i].center.toArray(),scale*.3);onBreak?.(i);broke=true;}
      if(broke)collapse();
      ages=Float64Array.from(crackle.ages);
      web.step(dt);shards.step(dt);crumbs.step(dt);
    },
    shellPose(i){return shards.pose(i);},
    shellNudge(i){
      const amount=web.at(stage.shells[i].center.toArray());if(amount<=0)return null;
      const d=dirs[i],sink=.012*scale*amount;return {offset:[-d[0]*sink,-d[1]*sink,-d[2]*sink],rotation:tilt[i].map(t=>t*amount)};
    },
    get busy(){return shards.active||crumbs.active||web.list.some(c=>web.clock-c.born<.4);},
    reset(){web.clear();shards.reset();crumbs.clear();steps=0;resettle=true;},
    dispose(){crumbs.dispose();},
  };
}

// Crackle treat pumpkin: crack the candy shell and the sweets inside pop out
// of each new hole and tumble onto the floor around it.
function treatPumpkin(stage,record){
  let pile=null,stem=null;
  const shell=candyShell(stage,record,'pumpkin',{lining:'#ffbd6b',onSettle:list=>settleSweets(list),
    onBreak:i=>{pile?.burst(i);if(stage.crackle.damage.length>stage.shells.length*.55)pile?.loosen();if(stage.shells[i].center.y>shellTop*.8)freeStem();}});
  if(!shell)return null;
  const {center,scale}=shell,shape=SHAPES.pumpkin,shellTop=center.y+shape.H*scale;
  const radii=[shape.W*scale*.95,shape.H*scale*.95,shape.W*scale*.95];
  const sweets=stage.meshes.filter(item=>/^prop_sweet_/.test(item.mesh.name)).sort((a,b)=>a.mesh.name.localeCompare(b.mesh.name));
  const sizeOf=item=>{const b=item.mesh.geometry.boundingBox,v=b.getSize(new T.Vector3());return Math.max(v.x,v.y,v.z)*.42;};
  const local=v=>{const x=v.x/radii[0],y=(v.y-center.y)/radii[1],z=v.z/radii[2],d=Math.hypot(x,y,z)||1;return [x/d,y/d,z/d];};
  pile=new CandyPile({sweets:sweets.map(item=>({center:item.center.toArray(),radius:sizeOf(item)})),shell:{center:center.toArray(),radii},
    pieces:stage.shells.map(g=>local(g.center)),open:i=>stage.crackle.isBroken(i),random:shell.random});
  // The stem rides the top until the shell under it breaks, then tumbles.
  const stemItem=stage.meshes.find(item=>item.mesh.name==='prop_pumpkin_stem');
  if(stemItem){const g=stemItem.mesh.geometry,n=g.attributes.position.count,c=stemItem.center,points=[];for(let k=0;k<8;k++){const v=Math.floor(k/8*n);points.push([g.attributes.position.getX(v)-c.x,g.attributes.position.getY(v)-c.y,g.attributes.position.getZ(v)-c.z]);}
    stem=new Shards([{center:c.toArray(),points}],{random:shell.random});}
  const freeStem=(settle=false)=>{if(!stem||stem.items[0].p)return;stem.launch(0,stemItem.center.toArray(),[.3+.4*shell.random(),settle?.5:1.4,.2],[(shell.random()-.5)*6,(shell.random()-.5)*6,(shell.random()-.5)*6]);};
  // Settle the sweets at the bottom; saved damage has already let some out.
  const settleSweets=list=>{
    for(const i of list){pile.burst(i);if(stage.shells[i].center.y>shellTop*.8)freeStem(true);}
    if(list.length>stage.shells.length*.55)pile.loosen();
    for(let t=0;t<4;t+=1/60){pile.step(1/60);stem?.step(1/60);}
  };
  settleSweets(shell.saved);
  const posed=new T.Matrix4();
  return {
    pile,stem,web:shell.web,shards:shell.shards,rigidShell:true,
    rotation:[.2,0,0],
    // Flying shards and sweets would shrink the fitted view; hold it once the shell breaks.
    get freeFit(){return stage.crackle.damage.length>0&&stage.playFitScale!==undefined;},
    get activity(){return pile.activity||stem?.active||shell.busy?1:0;},
    get active(){return this.activity>0;},
    step(dt){shell.step(dt);pile.step(dt);stem?.step(dt);stage.canvas.dataset.sweetsOut=String(pile.outside);},
    // With the whole shell gone, the button bounces the sweets instead.
    squeeze(){if(stage.crackle.damage.length<stage.shells.length)return false;pile.hop();return true;},
    poseProp(item){
      const i=sweets.indexOf(item);if(i>=0){pile.pose(i,item.mesh);return;}
      if(item===stemItem){const pose=stem?.pose(0);if(!pose)return;item.mesh.rotation.fromArray(pose.rotation);const turned=item.center.clone().applyEuler(item.mesh.rotation);item.mesh.position.fromArray(pose.center).sub(turned);}
    },
    shellPose:shell.shellPose,shellNudge:shell.shellNudge,
    reset(){shell.reset();pile.reset();stem?.reset();for(let t=0;t<2;t+=1/60)pile.step(1/60);for(const item of [...sweets,stemItem])if(item){item.mesh.position.set(0,0,0);item.mesh.rotation.set(0,0,0);}},
    dispose(){shell.dispose();},
  };
}

// Crackle bat: little bats sit in a heap in the bowl of the candy bat. Once
// a hole is open they stream out of it, circle above the toy, then come back
// through the hole; a press sends them out again. When the bowl breaks they
// sit on the floor among the shards, and a tap on the heap startles them.
// Bubble gum: hold to blow a bubble from the pouting mouth. It grows more
// slowly as it gets bigger while its film thins, from deep pink and opaque to
// pale, clear and rainbow-sheened, and it wobbles in the air. Let go and it
// sighs back in. Blown past its limit it pops: the toy flinches, the gum
// snaps back over its face, and it chews it back in from the edges.
function bubbleGum(stage,record){
  const skin=stage.meshes.find(item=>/^body_gum/.test(item.mesh.name)),mouthItem=stage.meshes.find(item=>/^face_mouth/.test(item.mesh.name)),splat=stage.meshes.find(item=>/^gum_splat/.test(item.mesh.name));
  if(!skin||!mouthItem||!splat||!stage.body)return null;
  const body=stage.body,random=seeded(record.id??record.kind),box=boxOf([skin]),R=box.getSize(new T.Vector3()).x/2,centre=box.getCenter(new T.Vector3());
  const mouth=mouthItem.center.clone(),mouthBinding=body.bind(mouth.toArray());
  const out=mouth.clone().sub(centre);out.y*=.3;out.normalize();
  const toward=new T.Vector3(),scratch=new T.Vector3(),inverse=new T.Matrix4();
  const gum=new GumBubble({size:R,random});
  const uniforms={film:{value:0},time:{value:0},wobble:{value:0}};
  const material=new T.MeshPhysicalMaterial({color:'#fb6aa5',transparent:true,roughness:.25,clearcoat:1,iridescence:0,iridescenceIOR:1.3,depthWrite:false,side:T.DoubleSide});
  material.onBeforeCompile=shader=>{
    Object.assign(shader.uniforms,uniforms);
    shader.vertexShader='uniform float time;uniform float wobble;\n'+shader.vertexShader.replace('#include <begin_vertex>',`#include <begin_vertex>
      // A thin film sways and ripples in the air.
      float ripple=sin(dot(position,vec3(3.1,2.3,1.7))*2.+time*3.1)*.6+sin(dot(position,vec3(-1.9,3.7,2.9))*3.+time*4.3)*.4;
      transformed+=normal*ripple*wobble;`);
    shader.fragmentShader='uniform float film;\n'+shader.fragmentShader.replace('#include <emissivemap_fragment>',`#include <emissivemap_fragment>
      float filmRim=pow(1.-abs(dot(normalize(normal),normalize(vViewPosition))),2.);
      // Thick gum is deep pink and opaque; a stretched film is pale and clear but for its rim.
      diffuseColor.rgb=mix(diffuseColor.rgb,vec3(1.,.82,.9),film*.75);
      diffuseColor.a=mix(.96,mix(.16,.85,filmRim),smoothstep(.15,.85,film));
      totalEmissiveRadiance+=vec3(1.,.75,.9)*filmRim*film*.25;`);
  };
  material.customProgramCacheKey=()=>'gum-bubble';
  const bubble=new T.Mesh(new T.SphereGeometry(1,40,28),material);bubble.name='gum_bubble';bubble.visible=false;bubble.scale.setScalar(1e-4);bubble.renderOrder=2;stage.toy.add(bubble);
  // The stuck gum shows within a reach of the mouth, with a torn, ragged edge.
  splat.unpickable=true;
  const splatGeometry=splat.mesh.geometry;if(!splatGeometry.getAttribute('restPosition'))splatGeometry.setAttribute('restPosition',new T.BufferAttribute(splat.rest.slice(),3));
  const reach={value:0},mouthRest={value:mouth.clone()};
  const splatMaterial=splat.mesh.material,previousCompile=splatMaterial.onBeforeCompile;
  splatMaterial.onBeforeCompile=(shader,renderer)=>{
    previousCompile?.call(splatMaterial,shader,renderer);
    shader.uniforms.splatReach=reach;shader.uniforms.splatMouth=mouthRest;
    shader.vertexShader='attribute vec3 restPosition;varying vec3 vSplatRest;\n'+shader.vertexShader.replace('#include <begin_vertex>','#include <begin_vertex>\nvSplatRest=restPosition;');
    shader.fragmentShader='uniform float splatReach;uniform vec3 splatMouth;varying vec3 vSplatRest;\nfloat splatHash(vec3 p){p=fract(p*.3183099+.1);p*=17.;return fract(p.x*p.y*p.z*(p.x+p.y+p.z));}\n'+shader.fragmentShader.replace('#include <clipping_planes_fragment>',`#include <clipping_planes_fragment>
      float tear=splatHash(floor(vSplatRest*22.))*.6+splatHash(floor(vSplatRest*9.))*.4;
      if(length(vSplatRest-splatMouth)>splatReach*(.8+.4*tear))discard;`);
  };
  splatMaterial.customProgramCacheKey=()=>'gum-splat';splatMaterial.needsUpdate=true;
  let auto=0,clock=0,pops=0,chew=0;stage.canvas.dataset.gumPops='0';stage.canvas.dataset.gumBlowing='0';
  const fullReach=R*1.25;
  return {
    gum,bubble,
    // The view keeps its fit while a bubble is out, so the toy does not shrink as it grows.
    get freeFit(){return gum.radius>0&&stage.playFitScale!==undefined;},
    get activity(){return gum.radius>0||gum.phase!=='idle'||auto>0?1:0;},
    get active(){return this.activity>0;},
    squeeze(){auto=6;return true;},
    step(dt){
      clock+=dt;uniforms.time.value=clock;
      const blowing=!!body.hold||auto>0;
      if(auto>0)auto=Math.max(0,auto-dt);
      for(const event of gum.step(dt,blowing)){
        if(event==='pop'){auto=0;stage.canvas.dataset.gumPops=String(++pops);stage.face?.flinch(.8);chew=0;
          // The burst kicks the face back a little.
          for(let i=0;i<body.count;i++)body.velocity[i*3+2]-=R*.8*Math.max(0,(body.initialRest[i*3+2]-centre.z)/R);body.activity=1;}
      }
      stage.canvas.dataset.gumBlowing=gum.phase==='blowing'?'1':'0';
      const r=gum.radius,at=body.sample(mouthBinding);
      // The bubble swells toward the viewer, sagging a little under its weight.
      stage.toy.updateMatrixWorld();toward.copy(stage.camera.position).applyMatrix4(inverse.copy(stage.toy.matrixWorld).invert()).sub(scratch.set(...at)).normalize();
      out.copy(toward);out.y-=.25*gum.film;out.normalize();
      bubble.visible=r>R*.02;
      // Hidden objects still count toward the view's fit, so a hidden bubble is shrunk away.
      if(!bubble.visible)bubble.scale.setScalar(1e-4);
      else{
        const sway=r/R*.05;
        bubble.position.set(at[0]+out.x*r*.92+Math.sin(clock*1.3)*sway*r,at[1]+out.y*r*.92+Math.sin(clock*1.7+1)*sway*r*.6,at[2]+out.z*r*.92);
        bubble.scale.set(r,r*.97,r);
        uniforms.film.value=gum.film;uniforms.wobble.value=.012*gum.film*gum.film;material.iridescence=gum.film;
      }
      // The stuck gum snaps out at once, then shrinks back as it is chewed in.
      reach.value=gum.splat*fullReach;
      if(gum.phase==='popped'&&gum.splat<1&&!stage.state?.reducedMotion){
        chew+=dt;if(Math.sin(chew*15)>.97)for(let i=0;i<body.count;i++)body.velocity[i*3+1]-=R*.25*Math.max(0,(body.initialRest[i*3+1]-centre.y)/R);
      }
      stage.dirty=true;
    },
    reset(){gum.reset();auto=0;reach.value=0;bubble.visible=false;bubble.scale.setScalar(1e-4);},
    dispose(){bubble.parent?.remove(bubble);bubble.geometry.dispose();material.dispose();},
  };
}

// A hamster and its cheek pouches. Sunflower seeds lie in front of it; a
// finger carries one to its mouth and it stuffs it into the emptier cheek,
// which bulges, then munches. A squeeze pops the seeds back out one by one.
function hamsterPouch(stage,record){
  const skin=stage.meshes.find(item=>/^body_hamster/.test(item.mesh.name)),mouthItem=stage.meshes.find(item=>/^face_mouth/.test(item.mesh.name));
  const seedItems=stage.meshes.filter(item=>/^prop_seed_/.test(item.mesh.name)).sort((a,b)=>a.mesh.name.localeCompare(b.mesh.name,undefined,{numeric:true}));
  const eyes=stage.meshes.filter(item=>/^face_eye/.test(item.mesh.name));
  if(!skin||!mouthItem||!seedItems.length||eyes.length<2||!stage.body)return null;
  const body=stage.body,random=seeded(record.id??record.kind),box=boxOf([skin]),R=box.getSize(new T.Vector3()).x/2,centre=box.getCenter(new T.Vector3());
  // Seeds go in a little in front of the lips.
  const mouth=mouthItem.center.clone().add(new T.Vector3(0,0,R*.12));
  const pouch=new CheekPouch({seeds:seedItems.map(item=>[item.center.x,0,item.center.z]),mouth:mouth.toArray(),size:R,random});
  const lift=seedItems.map(item=>item.center.y);
  // Pouches swell under the blush, a little outside and below it.
  const blush=stage.meshes.filter(item=>/^face_blush/.test(item.mesh.name)).map(item=>item.center).sort((a,b)=>a.x-b.x);
  const eye=eyes.map(item=>item.center).sort((a,b)=>a.x-b.x);
  const cheeks=(blush.length===2?blush:eye).map(c=>new T.Vector3(c.x*1.08,c.y-R*.06,c.z-R*.05));
  const reach=R*.42,amount=R*.3;
  // Rest normals, so each pouch rises out of the skin as a round bump.
  const skinNormals=skin.mesh.geometry.attributes.normal.array.slice();
  for(const item of stage.meshes)if(!item.isProp&&!item.isShell)item.behaviorDeform=true;
  // Face parts and ears ride the bump, moving the way the skin rises at the pouch's centre.
  const rise=cheeks.map(c=>{let best=0,dist=Infinity;const r=skin.rest;for(let i=0;i<r.length;i+=3){const d=Math.hypot(r[i]-c.x,r[i+1]-c.y,r[i+2]-c.z);if(d<dist){dist=d;best=i;}}return [skinNormals[best],skinNormals[best+1],skinNormals[best+2]];});
  let held=-1,ejectAt=0,munch=0,landed=0,eaten=0,clock=0,feeding=0;
  stage.canvas.dataset.cheekSeeds='0';stage.canvas.dataset.seedsLanded='0';
  const busy=()=>held>=0||pouch.seeds.some(x=>x.state==='eating'||x.state==='falling'||x.state==='flying')||Math.abs(pouch.bulge[0]-pouch.cheeks[0]/pouch.perCheek)>.001||Math.abs(pouch.bulge[1]-pouch.cheeks[1]/pouch.perCheek)>.001||munch>0;
  const quaternion=new T.Quaternion(),spinAxis=new T.Vector3(0,1,0);
  return {
    pouch,
    // Full cheeks would make the view shrink the hamster and hide the bulge, so the fit holds.
    get freeFit(){return (pouch.count>0||pouch.bulge[0]>0||pouch.bulge[1]>0||pouch.seeds.some(x=>x.state==='flying'||x.state==='held'))&&stage.playFitScale!==undefined;},
    get activity(){return busy()?1:0;},
    get active(){return busy();},
    get held(){return held>=0;},
    pickable(){return seedItems.filter((item,i)=>pouch.seeds[i].state==='floor'||pouch.seeds[i].state==='falling').map(item=>item.mesh);},
    grab(hit){const i=seedItems.findIndex(item=>item.mesh===hit.object);if(i<0||!pouch.hold(i))return;held=i;return 'tool';},
    moveTool(target){if(held>=0)pouch.move(held,[target[0],target[1]-lift[held],target[2]]);},
    pointerUp(){if(held<0)return;pouch.drop(held);held=-1;},
    // The button hands it the nearest seed on the floor.
    squeeze(){
      const i=pouch.seeds.findIndex(x=>x.state==='floor');if(i<0||pouch.full)return false;
      pouch.hold(i);pouch.move(i,mouth.toArray());pouch.drop(i);return true;
    },
    step(dt){
      clock+=dt;
      for(const event of pouch.step(dt)){
        if(event==='eat'){munch=.7;stage.canvas.dataset.cheekSeeds=String(++eaten);}
        if(event==='land')stage.canvas.dataset.seedsLanded=String(++landed);
      }
      // Pressing the hamster pops seeds out, one at a time.
      if(body.hold&&held<0&&pouch.count){if(ejectAt===0)ejectAt=clock+.25;if(clock>=ejectAt&&pouch.eject())ejectAt=clock+.4;}
      else ejectAt=0;
      if(munch>0){
        munch=Math.max(0,munch-dt);
        if(!stage.state?.reducedMotion&&Math.sin(munch*28)>.9)for(let i=0;i<body.count;i++){const w=Math.max(0,1-Math.hypot(body.initialRest[i*3]-mouth.x,body.initialRest[i*3+1]-mouth.y)/(R*.6));body.velocity[i*3+1]+=R*.6*w;}
      }
      stage.canvas.dataset.cheeks=pouch.bulge.map(b=>b.toFixed(2)).join(',');
    },
    // Cheeks swell outward around their centres; the face moves with them.
    deform(item,i,out){
      const r=item.rest,x=r[i*3],y=r[i*3+1],z=r[i*3+2],own=item===skin;
      for(let c=0;c<2;c++){
        const b=pouch.bulge[c];if(b<=0)continue;
        const d=Math.hypot(x-cheeks[c].x,y-cheeks[c].y,z-cheeks[c].z)/reach;if(d>=1)continue;
        // A round pouch: full height in the middle, easing to nothing at its edge.
        const t=1-d*d,w=t*t*(3-2*t)*b*amount;
        const n=own?[skinNormals[i*3],skinNormals[i*3+1],skinNormals[i*3+2]]:rise[c];
        out[0]+=n[0]*w;out[1]+=n[1]*w;out[2]+=n[2]*w;
      }
    },
    poseProp(item){
      const i=seedItems.indexOf(item);if(i<0)return;const seed=pouch.seeds[i],c=item.center;
      item.mesh.visible=seed.state!=='eaten';
      const shrink=seed.state==='eating'?Math.max(.2,1-seed.t*.8):1;
      item.mesh.position.set(seed.p[0]-c.x,seed.p[1]+lift[i]-c.y,seed.p[2]-c.z);
      if(seed.state==='flying'){quaternion.setFromAxisAngle(spinAxis,clock*seed.spin);item.mesh.quaternion.copy(quaternion);}
      item.mesh.scale.setScalar(shrink);
    },
    reset(){pouch.reset();held=-1;munch=0;stage.needsSurfaceUpdate=true;for(const item of seedItems){item.mesh.visible=true;item.mesh.scale.setScalar(1);item.mesh.quaternion.identity();}},
    dispose(){},
  };
}

// A fluffy popped kernel: an icosphere pushed out into a few lumps.
function popcornGeometry(random){
  const g=new T.IcosahedronGeometry(1,3),p=g.attributes.position,lumps=Array.from({length:6},()=>new T.Vector3(random()-.5,random()-.5,random()-.5).normalize());
  const v=new T.Vector3();
  for(let i=0;i<p.count;i++){
    v.fromBufferAttribute(p,i);let r=.72;
    for(const l of lumps)r+=.34*Math.max(0,v.dot(l)-.35)**1.5*2.2;
    r+=.05*Math.sin(v.x*9+v.y*7)*Math.sin(v.z*8);v.multiplyScalar(r);p.setXYZ(i,v.x,v.y,v.z);
  }
  g.computeVertexNormals();return g;
}

// A pot of popcorn with a clear lid. Pressing the pot (or the button) puts it
// on the heat: a glow spreads under it, the kernels start to shiver, then pop
// one by one, slowly, faster, and trailing off. Each puff jumps, rattles
// against the glass lid, and settles on a pile that lifts the lid and finally
// spills over the rim onto the floor. Off the heat, popping stops soon after.
function popcornPot(stage,record){
  // The lid is the glass and its knob: they move as one.
  const skin=stage.meshes.find(item=>/^body_pot/.test(item.mesh.name)),lids=stage.meshes.filter(item=>/^prop_lid/.test(item.mesh.name));
  const kernels=stage.meshes.filter(item=>/^prop_kernel_/.test(item.mesh.name));
  if(!skin||!lids.length||!kernels.length||!stage.body)return null;
  const body=stage.body,random=seeded(record.id??record.kind),r=skin.rest;
  // The pot's walls from its own rest mesh: outer radius, inner wall, inner floor, rim.
  let outer=0,rim=-Infinity,bottom=Infinity;
  for(let i=0;i<r.length;i+=3){outer=Math.max(outer,Math.hypot(r[i],r[i+2]));rim=Math.max(rim,r[i+1]);bottom=Math.min(bottom,r[i+1]);}
  let inner=outer,floor=bottom;
  for(let i=0;i<r.length;i+=3){const d=Math.hypot(r[i],r[i+2]),y=r[i+1];if(y>bottom+(rim-bottom)*.35&&y<rim-(rim-bottom)*.1&&d<inner)inner=d;if(d<outer*.3)floor=Math.max(floor,y);}
  const pot=new PopcornPot({kernels:kernels.map(item=>item.center.toArray()),inner,outer,floorY:floor,rimY:rim,size:outer,random});
  const shapes=[0,1,2].map(()=>popcornGeometry(random)),fluff=new T.MeshStandardMaterial({color:'#fff1d2',roughness:.82});
  const white=new T.Color('#fff1d2'),pink=new T.Color('#ffc2d6');
  // Every group gets its colour buffer up front: they share one shader, and a group
  // without one would read black.
  const groups=shapes.map(g=>{const m=new T.InstancedMesh(g,fluff,kernels.length);for(let i=0;i<kernels.length;i++)m.setColorAt(i,white);m.count=0;m.castShadow=true;m.frustumCulled=false;m.name='popcorn';stage.toy.add(m);return m;});
  // A warm glow on the floor under the pot while it is hot.
  const canvas=typeof document!=='undefined'?document.createElement('canvas'):null;let glow=null;
  if(canvas?.getContext){canvas.width=canvas.height=64;const c=canvas.getContext('2d');if(c){const grad=c.createRadialGradient(32,32,0,32,32,32);grad.addColorStop(0,'rgba(255,150,60,1)');grad.addColorStop(.6,'rgba(255,90,30,.45)');grad.addColorStop(1,'rgba(255,60,20,0)');c.fillStyle=grad;c.fillRect(0,0,64,64);}
    glow=new T.Mesh(new T.PlaneGeometry(outer*3,outer*3),new T.MeshBasicMaterial({map:new T.CanvasTexture(canvas),transparent:true,opacity:0,depthWrite:false,blending:T.AdditiveBlending,toneMapped:false}));
    glow.rotation.x=-Math.PI/2;glow.position.y=.004;glow.name='pot_glow';stage.toy.add(glow);}
  const bend=cageFollower(stage,lids),shift=[0,0,0],at=[0,0,0];
  let auto=0,pops=0,clinks=0,clock=0;stage.canvas.dataset.popcornPops='0';stage.canvas.dataset.lidClinks='0';stage.canvas.dataset.popcornHot='0';
  const matrix=new T.Matrix4(),q=new T.Quaternion(),euler=new T.Euler(),pos=new T.Vector3(),scale=new T.Vector3();
  const draw=()=>{
    const counts=[0,0,0];
    pot.puffs.forEach((f,i)=>{
      const g=i%3,m=groups[g];euler.set(f.turn*.7,f.turn,f.turn*.4);q.setFromEuler(euler);
      // Corn in the pot rides along when the soft pot is squeezed.
      if(f.rest&&Math.hypot(f.p[0],f.p[2])<inner){at[0]=f.p[0];at[1]=f.p[1];at[2]=f.p[2];body.displacementAt(at,shift);}else shift[0]=shift[1]=shift[2]=0;
      m.setMatrixAt(counts[g],matrix.compose(pos.set(f.p[0]+shift[0],f.p[1]+shift[1],f.p[2]+shift[2]),q,scale.setScalar(f.size)));m.setColorAt(counts[g],f.pink?pink:white);counts[g]++;
    });
    groups.forEach((m,g)=>{m.count=counts[g];m.instanceMatrix.needsUpdate=true;if(m.instanceColor)m.instanceColor.needsUpdate=true;});
  };
  return {
    pot,
    get freeFit(){return pot.puffs.some(f=>!f.rest)&&stage.playFitScale!==undefined;},
    get activity(){return pot.busy||auto>0?1:0;},
    get active(){return pot.busy||auto>0;},
    squeeze(){auto=9;return true;},
    step(dt){
      clock+=dt;const heating=!!body.hold||auto>0;if(auto>0)auto=Math.max(0,auto-dt);
      for(const e of pot.step(dt,heating)){if(e==='pop')stage.canvas.dataset.popcornPops=String(++pops);if(e==='lid')stage.canvas.dataset.lidClinks=String(++clinks);}
      stage.canvas.dataset.popcornHot=pot.heat>.3?'1':'0';
      if(glow)glow.material.opacity=Math.min(1,pot.heat*1.1);
      bend();draw();stage.dirty=true;
    },
    poseProp(item){
      if(lids.includes(item)){
        // The lid rides on the pile and rattles when corn hits it.
        item.mesh.position.set(0,pot.lid,0);item.mesh.rotation.set(Math.sin(clock*31)*Math.min(.06,pot.lidSpeed*.4),0,Math.sin(clock*27)*Math.min(.06,pot.lidSpeed*.4));return;
      }
      const i=kernels.indexOf(item);if(i<0)return;const k=pot.kernels[i];
      item.mesh.visible=!k.popped;
      // Hot kernels shiver in the oil before they pop.
      const shake=Math.max(0,pot.heat-.3)*outer*.012,p=body.sample(item.centerBinding),c=item.center;
      item.mesh.position.set(p[0]-c.x+(random()-.5)*shake,p[1]-c.y+(random()-.5)*shake,p[2]-c.z+(random()-.5)*shake);
    },
    reset(){pot.reset();auto=0;draw();for(const item of kernels)item.mesh.visible=true;for(const lid of lids){lid.mesh.position.set(0,0,0);lid.mesh.rotation.set(0,0,0);}if(glow)glow.material.opacity=0;},
    dispose(){for(const m of groups){m.parent?.remove(m);m.dispose();}for(const g of shapes)g.dispose();fluff.dispose();if(glow){glow.parent?.remove(glow);glow.geometry.dispose();glow.material.map.dispose();glow.material.dispose();}},
  };
}

// Mochi pounding. Steamed rice sits in a wooden mortar under a mallet. A tap
// (or the button) swings the mallet down onto that spot: the rice squashes
// and springs back, and each strike leaves it smoother, the grains melting
// into a soft, glossy dough while the steam thins. Holding keeps a steady
// rhythm. Once it is pounded through, the mochi opens its eyes.
function mochiTsuki(stage,record){
  const rice=stage.meshes.find(item=>/^body_rice/.test(item.mesh.name)),kine=stage.meshes.find(item=>/^prop_kine/.test(item.mesh.name));
  if(!rice||!kine||!stage.body)return null;
  const body=stage.body,box=boxOf([rice]),R=box.getSize(new T.Vector3()).x/2,top=box.max.y,centre=box.getCenter(new T.Vector3());
  // The head's lowest point and its centre across, from the mallet's own mesh.
  let headBottom=Infinity;const kr=kine.rest;for(let i=0;i<kr.length;i+=3)if(Math.abs(kr[i])<R*.5)headBottom=Math.min(headBottom,kr[i+1]);
  const headCentre=new T.Vector3(0,headBottom,0);
  const pounding=new MochiPounding({strikes:12});
  const face=stage.meshes.filter(item=>/^face_/.test(item.mesh.name));
  // A hidden face must not catch taps meant for the rice.
  const showFace=value=>{for(const item of face){item.mesh.visible=value;item.unpickable=!value;}stage.canvas.dataset.mochiDone=value?'1':'0';};
  showFace(false);
  // Rice to mochi on the surface: grains and the gaps between them fade as it smooths.
  const smooth={value:0},material=rice.mesh.material,previous=material.onBeforeCompile;
  if(!rice.mesh.geometry.getAttribute('restPosition'))rice.mesh.geometry.setAttribute('restPosition',new T.BufferAttribute(rice.rest.slice(),3));
  material.onBeforeCompile=(shader,renderer)=>{
    previous?.call(material,shader,renderer);
    shader.uniforms.riceSmooth=smooth;
    shader.vertexShader='attribute vec3 restPosition;varying vec3 vRiceRest;\n'+shader.vertexShader.replace('#include <begin_vertex>','#include <begin_vertex>\nvRiceRest=restPosition;');
    shader.fragmentShader='uniform float riceSmooth;varying vec3 vRiceRest;\nfloat riceHash(vec3 p){p=fract(p*.3183099+.1);p*=17.;return fract(p.x*p.y*p.z*(p.x+p.y+p.z));}\n'+shader.fragmentShader
      .replace('#include <color_fragment>',`#include <color_fragment>
        // Grains: an elongated oval in each cell, at its own angle, with shadowed gaps.
        vec3 riceQ=vRiceRest*${(18/R).toFixed(3)};vec3 riceC=floor(riceQ);vec3 riceL=fract(riceQ)-.5;
        float riceA=riceHash(riceC)*6.283;vec2 riceO=vec2(cos(riceA)*riceL.x+sin(riceA)*riceL.z,-sin(riceA)*riceL.x+cos(riceA)*riceL.z+riceL.y*.5);
        float riceGrain=1.-smoothstep(.32,.46,length(riceO*vec2(1.,2.1)));
        float riceRough=1.-riceSmooth;
        diffuseColor.rgb*=mix(1.,mix(.8,1.04,riceGrain),riceRough);`)
      .replace('#include <roughnessmap_fragment>','#include <roughnessmap_fragment>\nroughnessFactor=mix(.24,.62,1.-riceSmooth);')
      .replace('#include <lights_physical_fragment>','#include <lights_physical_fragment>\n#ifdef USE_CLEARCOAT\nmaterial.clearcoat*=riceSmooth;\n#endif');
  };
  material.customProgramCacheKey=()=>'mochi-rice';material.needsUpdate=true;
  if(material.isMeshPhysicalMaterial)Object.assign(material,{clearcoat:.55,clearcoatRoughness:.2});
  // Steam rises off the hot rice and thins as it is pounded.
  const steam=[],steamCanvas=typeof document!=='undefined'?document.createElement('canvas'):null;let steamTexture=null;
  if(steamCanvas?.getContext){steamCanvas.width=steamCanvas.height=64;const c=steamCanvas.getContext('2d');if(c){const g=c.createRadialGradient(32,32,0,32,32,32);g.addColorStop(0,'rgba(255,255,255,.75)');g.addColorStop(1,'rgba(255,255,255,0)');c.fillStyle=g;c.fillRect(0,0,64,64);}
    steamTexture=new T.CanvasTexture(steamCanvas);
    for(let i=0;i<7;i++){const sp=new T.Sprite(new T.SpriteMaterial({map:steamTexture,transparent:true,opacity:0,depthWrite:false}));sp.userData={age:i/7*2.4,x:(Math.random()-.5)*R,z:(Math.random()-.5)*R*.6};stage.toy.add(sp);steam.push(sp);}}
  let press=null,hits=0,clock=0,shake=0,last=null;stage.canvas.dataset.poundHits='0';
  const strikeAt=point=>pounding.request([point[0],Math.max(point[1],top*.85),point[2]]);
  return {
    pounding,
    get freeFit(){return !!pounding.swing&&stage.playFitScale!==undefined;},
    get activity(){return pounding.swing||pounding.queue.length||press?1:0;},
    // Steam keeps drifting until the rice is pounded smooth.
    get active(){return this.activity>0||pounding.smooth<1;},
    get held(){return !!press;},
    // The hand turns and kneads the soft rice; a quick tap calls the mallet down there.
    grab(hit){if(!stage.grabPoint||!/^(body_rice|face_)/.test(hit.object.name))return;press={at:[...stage.grabPoint],start:clock,moved:0};last=null;},
    pointerUp(){
      if(!press)return;
      if(clock-press.start<.25&&press.moved<R*.12)strikeAt(press.at);
      press=null;last=null;
    },
    squeeze(){strikeAt([centre.x,top,centre.z]);return true;},
    step(dt){
      clock+=dt;
      // Kneading: the farther the hand works the rice, the smoother it gets.
      const target=press&&body.hold?stage.dragTarget:null;
      if(target&&last){const moved=Math.hypot(...target.map((v,a)=>v-last[a]));press.moved+=moved;for(const e of pounding.knead(moved/R*.35))if(e.type==='done'){showFace(true);stage.face?.flinch(.4);}}
      last=target?[...target]:null;
      for(const e of pounding.step(dt)){
        if(e.type==='hit'){
          stage.canvas.dataset.poundHits=String(++hits);shake=1;
          // The blow squashes the rice under the head; it springs back.
          for(let i=0;i<body.count;i++){
            const x=body.initialRest[i*3]-e.at[0],z=body.initialRest[i*3+2]-e.at[2],w=Math.exp(-(x*x+z*z)/(R*R*.35))*Math.max(0,(body.initialRest[i*3+1]-box.min.y)/(top-box.min.y));
            body.velocity[i*3+1]-=R*7*w;body.velocity[i*3]+=x*4*w;body.velocity[i*3+2]+=z*4*w;
          }
          body.activity=1;
        }
        if(e.type==='done'){showFace(true);stage.face?.flinch(.4);}
      }
      smooth.value=pounding.smooth;stage.canvas.dataset.riceSmooth=pounding.smooth.toFixed(2);
      shake=Math.max(0,shake-dt*6);
      for(const sp of steam){
        const d=sp.userData;d.age=(d.age+dt*.42)%2.4;const k=d.age/2.4;
        sp.position.set(centre.x+d.x*(1+k*.5)+Math.sin(clock+d.z*9)*R*.08,top+R*(.1+k*1.2),centre.z+d.z);
        sp.scale.setScalar(R*(.35+.5*k));sp.material.opacity=.32*Math.sin(Math.PI*k)*(1-pounding.smooth*.85);
      }
    },
    poseProp(item){
      if(item!==kine)return;
      // The mallet head comes down onto the strike point and back up to wait.
      const p=pounding.pose,at=p.at??[centre.x,top,centre.z],hover=headCentre.y-at[1];
      const y=at[1]+p.height*hover-headCentre.y;
      item.mesh.position.set(at[0]-headCentre.x,y,at[2]-headCentre.z);
      item.mesh.rotation.set(0,0,p.lean*.5);
    },
    reset(){pounding.reset();press=null;last=null;showFace(false);smooth.value=0;},
    dispose(){for(const sp of steam){sp.parent?.remove(sp);sp.material.dispose();}steamTexture?.dispose();},
  };
}

// A jellyfish in a tank of water. It swims as one does: a quick squeeze of
// the bell jets it forward, then it glides and slowly sinks, pulsing now and
// then to hold its depth, and glowing faintly with each pulse. A touch on it,
// or a tap near it in the water, makes it squeeze hard and jet away. Its
// tentacles trail through the water behind it.
function jellyfish(stage,record){
  const bell=stage.meshes.find(item=>/^body_jellyfish/.test(item.mesh.name)),water=stage.meshes.find(item=>/^prop_water/.test(item.mesh.name));
  if(!bell||!water||!stage.body)return null;
  const parts=stage.meshes.filter(item=>!item.isProp&&!item.isShell),random=seeded(record.id??record.kind);
  const box=boxOf([bell]),c=box.getCenter(new T.Vector3()),R=Math.max(box.max.x-c.x,box.max.z-c.z);
  // The bell, its face and arms swim together as one group.
  const group=new T.Group();group.name='jellyfish';group.matrixAutoUpdate=false;stage.toy.add(group);
  for(const item of parts){stage.toy.remove(item.mesh);group.add(item.mesh);item.behaviorDeform=true;}
  // Tentacles start at eight points round the rim.
  let rimY=Infinity;const r=bell.rest;for(let i=0;i<r.length;i+=3)if(Math.hypot(r[i]-c.x,r[i+2]-c.z)>R*.75)rimY=Math.min(rimY,r[i+1]);
  const anchors=Array.from({length:8},(_,i)=>{const a=i/8*Math.PI*2;return [Math.cos(a)*R*.82,rimY-c.y+R*.03,Math.sin(a)*R*.82];});
  const wbox=new T.Box3().setFromBufferAttribute(water.mesh.geometry.attributes.position);
  const swim=new JellySwim({bounds:{min:wbox.min.toArray(),max:[wbox.max.x,wbox.max.y-R*.25,wbox.max.z]},start:c.toArray(),size:R,anchors,segments:12,length:R*2.6,random});
  // Tentacles: thin four-sided tubes rebuilt each frame from the chains.
  const K=13,S=4,count=anchors.length,positions=new Float32Array(count*K*S*3),index=[];
  for(let t=0;t<count;t++)for(let k=0;k<K-1;k++)for(let j=0;j<S;j++){const a=(t*K+k)*S+j,b=(t*K+k)*S+(j+1)%S,c2=a+S,d=b+S;index.push(a,c2,b,b,c2,d);}
  const strands=new T.BufferGeometry();strands.setAttribute('position',new T.BufferAttribute(positions,3));strands.setIndex(index);
  const strandMaterial=new T.MeshStandardMaterial({color:'#ff9cc6',emissive:'#ff7fb4',emissiveIntensity:.15,roughness:.4,transparent:true,opacity:.75});
  const tentacles=new T.Mesh(strands,strandMaterial);tentacles.name='tentacles';tentacles.frustumCulled=false;stage.toy.add(tentacles);
  const glowColor=new T.Color('#ff6fae'),bellMaterial=bell.mesh.material;
  Object.assign(bellMaterial,{transparent:true,opacity:.86});
  // Bubbles rise from the sand.
  const bubbles=[];
  for(let i=0;i<6;i++){const m=new T.Mesh(new T.SphereGeometry(1,10,8),new T.MeshPhysicalMaterial({color:'#e8f6ff',transparent:true,opacity:.45,roughness:.05,clearcoat:1}));m.userData={x:wbox.min.x+(wbox.max.x-wbox.min.x)*random(),z:wbox.min.z+(wbox.max.z-wbox.min.z)*(.2+.6*random()),t:random()*3,r:R*(.03+.04*random())};stage.toy.add(m);bubbles.push(m);}
  const q=new T.Quaternion(),up=new T.Vector3(0,1,0),axis=new T.Vector3(),m4=new T.Matrix4(),shift=new T.Matrix4().makeTranslation(-c.x,-c.y,-c.z);
  const inverse=new T.Matrix4(),origin=new T.Vector3(),dir=new T.Vector3();
  let jets=0,pokes=0,lastPoke=-1,clock=0,held=null;stage.canvas.dataset.jellyJets='0';stage.canvas.dataset.jellyPokes='0';
  const poke=point=>{if(clock-lastPoke<.6)return;lastPoke=clock;swim.poke(point);stage.canvas.dataset.jellyPokes=String(++pokes);stage.face?.flinch(.35);};
  const place=()=>{
    axis.set(...swim.axis);q.setFromUnitVectors(up,axis);
    group.matrix.makeTranslation(...swim.p).multiply(m4.makeRotationFromQuaternion(q)).multiply(shift);group.matrixWorldNeedsUpdate=true;
    const p=positions,side=new T.Vector3(),other=new T.Vector3(),along=new T.Vector3();
    swim.tentacles.forEach((chain,t)=>chain.forEach((pt,k)=>{
      const next=chain[Math.min(K-1,k+1)],prev=chain[Math.max(0,k-1)];along.set(next[0]-prev[0],next[1]-prev[1],next[2]-prev[2]).normalize();
      side.set(1,0,0).cross(along);if(side.lengthSq()<1e-6)side.set(0,0,1);side.normalize();other.crossVectors(along,side);
      const radius=R*.035*(1-.75*k/(K-1));
      for(let j=0;j<S;j++){const a=j/S*Math.PI*2,o=(t*K+k)*S+j;p[o*3]=pt[0]+(side.x*Math.cos(a)+other.x*Math.sin(a))*radius;p[o*3+1]=pt[1]+(side.y*Math.cos(a)+other.y*Math.sin(a))*radius;p[o*3+2]=pt[2]+(side.z*Math.cos(a)+other.z*Math.sin(a))*radius;}
    }));
    strands.attributes.position.needsUpdate=true;strands.computeVertexNormals();
  };
  place();
  return {
    swim,group,
    // It swims all the time.
    get activity(){return 1;},
    get active(){return true;},
    // The bell is soft jelly: a press holds it still and squishes it under the
    // finger; let go, it squeezes hard and jets away.
    get bodyMatrix(){return group.matrix;},
    get held(){return !!held;},
    grab(hit){if(!stage.grabPoint||!parts.some(item=>item.mesh===hit.object))return;held={at:[...stage.grabPoint]};},
    pointerUp(){if(!held)return;const at=held.at;held=null;lastPoke=-1;poke(at);},
    // A tap in the water near it startles it too.
    nudge(ray){
      stage.toy.updateMatrixWorld();inverse.copy(stage.toy.matrixWorld).invert();
      origin.copy(ray.origin).applyMatrix4(inverse);dir.copy(ray.direction).transformDirection(inverse);
      const to=new T.Vector3(...swim.p).sub(origin),t=Math.max(0,to.dot(dir)),closest=origin.clone().addScaledVector(dir,t);
      // Right on the bell it is a press, handled by grab.
      const d=closest.distanceTo(new T.Vector3(...swim.p));
      if(!held&&d>R*.95&&d<R*1.8&&stage.body&&!stage.active)poke(closest.toArray());
    },
    squeeze(){poke([swim.p[0]+(random()-.5)*R*2,swim.p[1]-R*1.2,swim.p[2]+R]);return true;},
    step(dt){
      clock+=dt;
      // Held, it stays in the finger's grip; its tentacles still sway.
      const at=held?[...swim.p]:null,axis=held?[...swim.axis]:null;
      if(held){swim.pulse=null;swim.nextPulse=swim.clock+1;}
      for(const e of swim.step(dt))if(e==='jet')stage.canvas.dataset.jellyJets=String(++jets);
      if(held){swim.p=at;swim.axis=axis;swim.v=[0,0,0];}
      place();
      bellMaterial.emissive?.copy(glowColor);if('emissiveIntensity' in bellMaterial)bellMaterial.emissiveIntensity=.12+.55*swim.contraction;
      strandMaterial.emissiveIntensity=.12+.4*swim.contraction;
      for(const b of bubbles){const d=b.userData;d.t+=dt;const h=(d.t*.35*(wbox.max.y-wbox.min.y)/3)%(wbox.max.y-wbox.min.y);b.position.set(d.x+Math.sin(d.t*2+d.z*9)*R*.05,wbox.min.y+h,d.z);b.scale.setScalar(d.r*(1+h/(wbox.max.y-wbox.min.y)*.5));}
      stage.dirty=true;
    },
    // The bell squeezes in at the rim and stretches a little as it contracts.
    // The finger's squish stays on top of the swimming pulse.
    deform(item,i,out){
      const rr=item.rest,x=rr[i*3]-c.x,y=rr[i*3+1]-c.y,z=rr[i*3+2]-c.z,con=swim.contraction;if(con<=0)return;
      const w=Math.min(1,Math.max(0,(R*.35-y)/(R*.6))),k=1-con*(.12+.28*w);
      out[0]+=x*(k-1);out[2]+=z*(k-1);out[1]+=y*.14*con-con*w*R*.06;
    },
    reset(){swim.reset();held=null;place();},
    dispose(){group.parent?.remove(group);tentacles.parent?.remove(tentacles);strands.dispose();strandMaterial.dispose();for(const b of bubbles){b.parent?.remove(b);b.geometry.dispose();b.material.dispose();}},
  };
}

// Shaved ice. Holding the polar-bear machine (or the button) turns its handle;
// fine shavings fall into the glass cup and the mound grows until the ice
// block is used up. Tapping a syrup bottle lifts it over the mound and pours:
// the syrup soaks in at the top, runs down, colours the ice where it goes,
// mixes where two syrups meet, and the soaked ice settles a little.
function kakigori(stage,record){
  const skin=stage.meshes.find(item=>/^body_ice/.test(item.mesh.name));
  const crank=stage.meshes.filter(item=>/^prop_crank/.test(item.mesh.name)),machine=stage.meshes.filter(item=>/^prop_machine/.test(item.mesh.name));
  const bottles=[0,1,2].map(i=>({body:stage.meshes.find(item=>item.mesh.name===`prop_syrup_${i}`),cap:stage.meshes.find(item=>item.mesh.name===`prop_syrupcap_${i}`)})).filter(b=>b.body&&b.cap);
  if(!skin||!crank.length||!machine.length||!bottles.length||!stage.body)return null;
  const random=seeded(record.id??record.kind),box=boxOf([skin]),floor=box.min.y,top=box.max.y,cx=(box.min.x+box.max.x)/2,cz=(box.min.z+box.max.z)/2,R=(box.max.x-box.min.x)/2;
  // The cup is served heaped with fluffy ice, ready to press.
  const ice=new ShavedIce({rest:skin.rest,floor,top,start:1});
  const cup=stage.meshes.find(item=>item.mesh.name==='prop_cup'),rim=cup?boxOf([cup]).max.y:floor;
  const give=y=>Math.max(0,Math.min(1,(y-rim+(top-floor)*.08)/((top-floor)*.3)));
  for(const b of bottles)b.colour=b.body.mesh.material.color.toArray();
  // Soak colour and amount drive the ice shader.
  const geometry=skin.mesh.geometry,soakColour=new T.BufferAttribute(ice.colour,3),soakAmount=new T.BufferAttribute(ice.soak,1);
  geometry.setAttribute('soakColour',soakColour);geometry.setAttribute('soak',soakAmount);
  if(!geometry.getAttribute('restPosition'))geometry.setAttribute('restPosition',new T.BufferAttribute(skin.rest.slice(),3));
  const material=skin.mesh.material,previous=material.onBeforeCompile;
  material.onBeforeCompile=(shader,renderer)=>{
    previous?.call(material,shader,renderer);
    shader.vertexShader='attribute vec3 soakColour;attribute float soak;attribute vec3 restPosition;varying vec3 vSoakColour;varying float vSoak;varying vec3 vIceRest;\n'+shader.vertexShader.replace('#include <begin_vertex>','#include <begin_vertex>\nvSoakColour=soakColour;vSoak=soak;vIceRest=restPosition;');
    shader.fragmentShader='varying vec3 vSoakColour;varying float vSoak;varying vec3 vIceRest;\nfloat iceHash(vec3 p){p=fract(p*.3183099+.1);p*=17.;return fract(p.x*p.y*p.z*(p.x+p.y+p.z));}\n'+shader.fragmentShader
      .replace('#include <color_fragment>',`#include <color_fragment>
        float iceWet=clamp(vSoak*2.6,0.,1.);
        // Fine shavings sparkle; soaked ice turns the syrup's colour and goes clear.
        float iceGrain=iceHash(floor(vIceRest*${(70/R).toFixed(2)}));
        diffuseColor.rgb*=mix(.9+.14*iceGrain,1.,iceWet);
        diffuseColor.rgb=mix(diffuseColor.rgb,vSoakColour,iceWet*.9);`)
      .replace('#include <roughnessmap_fragment>','#include <roughnessmap_fragment>\nroughnessFactor=mix(.85,.22,clamp(vSoak*2.6,0.,1.));')
      .replace('#include <emissivemap_fragment>','#include <emissivemap_fragment>\ntotalEmissiveRadiance+=vec3(.9,.95,1.)*step(.985,iceHash(floor(vIceRest*'+(110/R).toFixed(2)+')))*(1.-clamp(vSoak*2.6,0.,1.))*.35;');
  };
  material.customProgramCacheKey=()=>'shaved-ice';material.needsUpdate=true;
  skin.behaviorDeform=true;
  // Shavings: small flakes falling from under the machine.
  const outlet=boxOf(machine.filter(item=>item.mesh.name==='prop_machine')).min.y;
  const flakeGeometry=new T.IcosahedronGeometry(1,0),flakes=new T.InstancedMesh(flakeGeometry,new T.MeshStandardMaterial({color:'#f6fbff',roughness:.7}),90);
  flakes.count=0;flakes.frustumCulled=false;flakes.name='ice_flakes';stage.toy.add(flakes);
  const falling=[];
  // The syrup stream from a tilted bottle to the top of the mound.
  const stream=new T.Mesh(new T.CylinderGeometry(1,1,1,10,1,true),new T.MeshStandardMaterial({color:'#ffffff',roughness:.15,transparent:true,opacity:.85}));stream.visible=false;stream.name='syrup_stream';stage.toy.add(stream);
  const pivot=crank[0].center.clone();pivot.y=crank[0].mesh.geometry.boundingBox?.min.y??pivot.y;
  let cranking=false,pressing=false,auto=0,angle=0,shaves=0,lastShave=0,clock=0,pourCount=0,pouring=null;
  const frame=new T.Matrix4(),scaleAt=new T.Matrix4(),shift=new T.Matrix4();
  const spread=()=>.45+.55*ice.volume**.4;
  stage.canvas.dataset.iceShaves='0';stage.canvas.dataset.syrupPours='0';stage.canvas.dataset.iceFull='0';
  const matrix=new T.Matrix4(),q=new T.Quaternion(),pos=new T.Vector3(),scale=new T.Vector3(),turn=new T.Euler();
  const startPour=i=>{
    if(pouring)return;const b=bottles[i];
    pouring={i,t:0,poured:false,home:b.body.center.clone()};
  };
  const surfaceTop=()=>floor+(top-floor)*ice.height;
  return {
    ice,
    get freeFit(){return !!pouring&&stage.playFitScale!==undefined;},
    // The mound is drawn scaled to the ice in the cup; presses meet it there.
    get bodyMatrix(){
      const w=spread(),h=ice.height;
      return frame.makeTranslation(cx,floor,cz).multiply(scaleAt.makeScale(w,h,w)).multiply(shift.makeTranslation(-cx,-floor,-cz));
    },
    get activity(){return cranking||pressing||auto>0||falling.length||pouring||ice.pours.length||ice.wet.some(v=>v>1e-4)?1:0;},
    get active(){return this.activity>0;},
    pickable(){return [...machine,...crank,...bottles.flatMap(b=>[b.body,b.cap])].map(item=>item.mesh);},
    grab(hit){
      const b=bottles.findIndex(x=>x.body.mesh===hit.object||x.cap.mesh===hit.object);
      if(b>=0){startPour(b);return 'tool';}
      if([...machine,...crank].some(item=>item.mesh===hit.object)){cranking=true;return 'tool';}
      if(hit.object===skin.mesh)pressing=true;
    },
    moveTool(){},
    pointerUp(){cranking=false;pressing=false;},
    squeeze(){auto=4;return true;},
    step(dt){
      clock+=dt;if(auto>0)auto=Math.max(0,auto-dt);
      const crankOn=cranking||auto>0;
      // A held press packs the soft ice under the finger down a little.
      if(pressing&&stage.body?.hold)ice.pack(dt*.06);
      for(const e of ice.step(dt,crankOn?1:0)){
        if(e==='shave'&&clock-lastShave>.3){lastShave=clock;stage.canvas.dataset.iceShaves=String(++shaves);}
        if(e==='full')stage.canvas.dataset.iceFull='1';
      }
      if(crankOn&&ice.block>0){angle+=dt*7;for(let k=0;k<2;k++)if(falling.length<90)falling.push({p:[cx+(random()-.5)*R*.35,outlet,cz+(random()-.5)*R*.35],v:-R*(1.2+random()),s:R*(.02+.025*random()),r:random()*6});}
      // Flakes fall onto the mound and vanish into it.
      const land=surfaceTop();
      for(let i=falling.length-1;i>=0;i--){const f=falling[i];f.v-=R*6*dt;f.p[1]+=f.v*dt;f.r+=dt*5;if(f.p[1]<land)falling.splice(i,1);}
      falling.forEach((f,i)=>{turn.set(f.r,f.r*.7,0);q.setFromEuler(turn);flakes.setMatrixAt(i,matrix.compose(pos.set(...f.p),q,scale.setScalar(f.s)));});
      flakes.count=falling.length;flakes.instanceMatrix.needsUpdate=true;
      if(pouring){
        const p=pouring;p.t+=dt;
        if(!p.poured&&p.t>=.6){p.poured=true;ice.pour(bottles[p.i].colour);stage.canvas.dataset.syrupPours=String(++pourCount);}
        if(p.t>2.4){pouring=null;stream.visible=false;}
      }
      skin.mesh.visible=ice.volume>.01;skin.unpickable=!skin.mesh.visible;
      soakColour.needsUpdate=true;soakAmount.needsUpdate=true;stage.needsSurfaceUpdate=true;stage.dirty=true;
    },
    // The mound grows from the cup with the shaved volume, and settles where soaked.
    deform(item,i,out){
      if(item!==skin)return;
      const r=item.rest,x=r[i*3],y=r[i*3+1],z=r[i*3+2],h=ice.height*(1-ice.slump(i)),w=spread();
      const k=give(floor+(y-floor)*h),dx=(out[0]-x)*k,dy=(out[1]-y)*k,dz=(out[2]-z)*k;
      out[0]=cx+(x-cx)*w+dx;out[1]=floor+(y-floor)*h+dy;out[2]=cz+(z-cz)*w+dz;
    },
    poseProp(item){
      if(crank.includes(item)){
        // The handle turns about its post.
        item.mesh.rotation.set(0,angle,0);const c=Math.cos(angle),s=Math.sin(angle);
        item.mesh.position.set(pivot.x-(c*pivot.x+s*pivot.z),0,pivot.z-(-s*pivot.x+c*pivot.z));return;
      }
      const b=bottles.find(x=>x.body===item||x.cap===item);if(!b)return;
      const p=pouring&&bottles[pouring.i]===b?pouring:null;
      if(!p){item.mesh.position.set(0,0,0);item.mesh.rotation.set(0,0,0);return;}
      // Lift over the mound, tip, pour, and come back.
      const t=p.t,lift=t<.4?t/.4:t>2?Math.max(0,1-(t-2)/.4):1,tip=t<.4?0:t<.6?(t-.4)/.2:t>1.9?Math.max(0,1-(t-1.9)/.2):1;
      // Beside and in front of the mound, below the machine, where the pour shows.
      const side=Math.sign(p.home.x-cx)||1,target=new T.Vector3(cx+side*R*.95,surfaceTop()+R*.25,cz+R*.75),now=p.home.clone().lerp(target,lift);
      const tilt=-side*tip*1.9;
      // Tilt about the bottle's centre: the mesh turns about the toy's origin,
      // so place it where its centre lands at now plus its turned offset.
      const c=b.body.center,ic=item.center,ct=Math.cos(tilt),st=Math.sin(tilt),rx=ic.x-c.x,ry=ic.y-c.y;
      item.mesh.rotation.set(0,0,tilt);
      const px=now.x+(rx*ct-ry*st),py=now.y+(rx*st+ry*ct),pz=now.z+(ic.z-c.z);
      item.mesh.position.set(px-(ic.x*ct-ic.y*st),py-(ic.x*st+ic.y*ct),pz-ic.z);
      if(item===b.body){
        const pouringNow=t>.55&&t<1.95;stream.visible=pouringNow;
        if(pouringNow){
          const mouth=new T.Vector3(now.x-side*R*.32,now.y+R*.02,now.z),landAt=new T.Vector3(cx+side*R*.15,surfaceTop()-R*.05,cz+R*.2),length=mouth.distanceTo(landAt);
          stream.position.copy(mouth).add(landAt).multiplyScalar(.5);stream.scale.set(R*.035,length,R*.035);
          stream.quaternion.setFromUnitVectors(new T.Vector3(0,1,0),mouth.clone().sub(landAt).normalize());
          stream.material.color.setRGB(...b.colour);
        }
      }
    },
    reset(){ice.reset();cranking=false;pressing=false;auto=0;angle=0;falling.length=0;pouring=null;stream.visible=false;stage.canvas.dataset.iceFull='0';soakColour.needsUpdate=true;soakAmount.needsUpdate=true;stage.needsSurfaceUpdate=true;},
    dispose(){flakes.parent?.remove(flakes);flakes.dispose();flakeGeometry.dispose();stream.parent?.remove(stream);stream.geometry.dispose();stream.material.dispose();},
  };
}

// A roly-poly daruma. Push it over and it rocks back upright on its round,
// weighted bottom, rolling a little along the floor as it leans, with a tiny
// bell inside. A quick tap pokes it into rocking. Knocked over seven times,
// it gets up every time, and then its second eye is painted in: its wish has
// come true (nana-korobi ya-oki).
function darumaToy(stage,record){
  const skin=stage.meshes.find(item=>/^body_daruma/.test(item.mesh.name));
  const pupils=stage.meshes.filter(item=>/^face_pupil/.test(item.mesh.name)).sort((a,b)=>a.center.x-b.center.x);
  if(!skin||pupils.length<2||!stage.body)return null;
  const box=boxOf([skin]),H=box.max.y-box.min.y,base=box.min.y,cx=(box.min.x+box.max.x)/2,cz=(box.min.z+box.max.z)/2,Rb=(box.max.x-box.min.x)/2;
  const toy=new RolyPoly({radius:Rb,height:H});
  const parts=stage.meshes.filter(item=>!item.isProp&&!item.isShell);
  const group=new T.Group();group.name='daruma';group.matrixAutoUpdate=false;stage.toy.add(group);
  for(const item of parts){stage.toy.remove(item.mesh);group.add(item.mesh);}
  // The face sits in layers just over the red skin: panel, then eyes, brows and
  // whiskers on it. Squeezed, the gaps close, so each layer wins over the one below.
  // Its round bottom rolls rather than grips the floor.
  stage.body.floorFriction=0;
  for(const item of parts)if(/^face_/.test(item.mesh.name)){const top=item.mesh.name!=='face_panel';Object.assign(item.mesh.material,{polygonOffset:true,polygonOffsetFactor:top?-12:-6,polygonOffsetUnits:top?-24:-12});}
  // The painted eye is the one on the left; the right one waits for the wish.
  const wishEye=pupils[1].mesh,eyeCentre=pupils[1].center;
  let paint=0,painted=false,press=null,clock=0,rises=0;
  stage.canvas.dataset.darumaRises='0';stage.canvas.dataset.darumaWish='0';stage.canvas.dataset.darumaRocks='0';let rocks=0;
  const showEye=k=>{wishEye.visible=k>0;const s=Math.max(1e-4,k);wishEye.matrixAutoUpdate=false;wishEye.matrix.makeTranslation(eyeCentre.x,eyeCentre.y,eyeCentre.z).multiply(new T.Matrix4().makeScale(s,s,1)).multiply(new T.Matrix4().makeTranslation(-eyeCentre.x,-eyeCentre.y,-eyeCentre.z));wishEye.matrixWorldNeedsUpdate=true;};
  showEye(0);
  const m=new T.Matrix4(),r=new T.Matrix4();
  const place=()=>{
    const [ox,oz]=toy.offset;
    m.makeTranslation(cx+ox,base+Rb,cz+oz).multiply(r.makeRotationX(toy.tilt[1])).multiply(new T.Matrix4().makeRotationZ(-toy.tilt[0])).multiply(new T.Matrix4().makeTranslation(-cx,-(base+Rb),-cz));
    group.matrix.copy(m);group.matrixWorldNeedsUpdate=true;
  };
  const lean=d=>Math.max(-1.4,Math.min(1.4,Math.atan2(d,H*.55)*1.7));
  return {
    toy,group,
    // The soft foam body squishes under the finger wherever the daruma leans.
    get bodyMatrix(){return group.matrix;},
    get activity(){return toy.busy||press||(painted&&paint<1)?1:0;},
    get active(){return this.activity>0;},
    get held(){return !!press;},
    // A press holds it where it is, so the squish stays under the finger.
    grab(hit){if(!parts.some(item=>item.mesh===hit.object))return;press={at:[...stage.grabPoint],start:clock};toy.hold([...toy.tilt]);},
    // Let go, the lean the finger gave the soft body becomes a real lean, and it
    // rocks back up from there. A quick tap pokes it into rocking.
    pointerUp(){
      if(!press)return;toy.release();
      const from=stage.grabPoint,to=stage.dragTarget,dx=to&&from?to[0]-from[0]:0,dz=to&&from?to[2]-from[2]:0,moved=Math.hypot(dx,dz);
      if(moved>H*.05){toy.tilt[0]+=lean(dx);toy.tilt[1]+=lean(dz);toy.spin=[toy.spin[0]+lean(dx)*1.5,toy.spin[1]+lean(dz)*1.5];}
      else toy.poke([press.at[0]-cx,press.at[1]-base,press.at[2]-cz].map(v=>v*(clock-press.start<.3?1:.5)));
      press=null;
    },
    squeeze(){toy.poke([Rb*.9,H*.85,0]);return true;},
    step(dt){
      clock+=dt;
      for(const e of toy.step(dt)){
        if(e==='rock')stage.canvas.dataset.darumaRocks=String(++rocks);
        if(e==='rise')stage.canvas.dataset.darumaRises=String(++rises);
        if(e==='wish'){painted=true;stage.canvas.dataset.darumaWish='1';}
      }
      if(painted&&paint<1){paint=Math.min(1,paint+dt/.7);showEye(1-(1-paint)**3);}
      place();stage.dirty=true;
    },
    reset(){toy.reset();press=null;place();if(!painted)showEye(0);},
    dispose(){group.parent?.remove(group);},
  };
}

// A paper balloon to keep in the air. A tap (on it, or just beside it) sends
// it floating up and away from the finger, crinkling the paper where it was
// hit and letting a little air out; it drifts down slowly, being so light.
// Ten taps in a row without touching the floor shower it with paper
// confetti. The button blows into its hole and puffs it round again.
function paperBalloon(stage,record){
  const skin=stage.meshes.find(item=>/^body_balloon/.test(item.mesh.name));
  if(!skin||!stage.body)return null;
  const box=boxOf([skin]),R=(box.max.x-box.min.x)/2,c=box.getCenter(new T.Vector3());
  const ball=new PaperBalloon({radius:R,start:[c.x,c.y,c.z],bounds:{min:[c.x-R*2.6,0,c.z-R*1.2],max:[c.x+R*2.6,R*6.2,c.z+R*1.2]}});
  const parts=stage.meshes.filter(item=>!item.isProp&&!item.isShell);
  const group=new T.Group();group.name='kamifusen';group.matrixAutoUpdate=false;stage.toy.add(group);
  for(const item of parts){stage.toy.remove(item.mesh);group.add(item.mesh);item.behaviorDeform=true;}
  // The whole play space counts for the view, so the balloon stays in sight up high.
  const space=new T.Mesh(new T.BoxGeometry(R*5.2,R*6.2,R*2.4),new T.MeshBasicMaterial());space.visible=false;space.position.set(c.x,R*3.1,c.z);space.name='play_space';stage.toy.add(space);
  // Paper confetti for a good streak.
  const confettiGeometry=new T.PlaneGeometry(1,1.4),confetti=new T.InstancedMesh(confettiGeometry,new T.MeshStandardMaterial({side:T.DoubleSide,roughness:.8}),48);
  const palette=['#e8233a','#f7b500','#1f6fe0','#2fa84f','#ef5a9a','#f7741a'].map(x=>new T.Color(x));
  for(let i=0;i<48;i++)confetti.setColorAt(i,palette[i%palette.length]);
  confetti.count=0;confetti.frustumCulled=false;confetti.name='confetti';stage.toy.add(confetti);
  const bits=[],random=seeded(record.id??record.kind);
  const spin=new T.Quaternion(),turn=new T.Quaternion(),axis=new T.Vector3(),m=new T.Matrix4(),q=new T.Quaternion(),pos=new T.Vector3(),scl=new T.Vector3(),e=new T.Euler();
  const inverse=new T.Matrix4(),origin=new T.Vector3(),dir=new T.Vector3(),centre=new T.Vector3();
  let hits=0,cheers=0,lastHit=-1,clock=0;stage.canvas.dataset.balloonHits='0';stage.canvas.dataset.balloonCheers='0';stage.canvas.dataset.balloonStreak='0';
  let press=null;
  const tap=(point,power=1,force=false)=>{
    if(!force&&clock-lastHit<.12)return;lastHit=clock;
    for(const ev of ball.hit(point,power)){
      if(ev==='hit')stage.canvas.dataset.balloonHits=String(++hits);
      if(ev==='cheer'){stage.canvas.dataset.balloonCheers=String(++cheers);for(let i=0;i<48;i++)bits.push({p:[ball.p[0]+(random()-.5)*R,ball.p[1]+R,ball.p[2]+(random()-.5)*R],v:[(random()-.5)*R*4,R*(2+3*random()),(random()-.5)*R*2],r:random()*6,w:(random()-.5)*12,age:0});}
    }
    // A hit sets it tumbling.
    axis.set(point[2]-ball.p[2],0,-(point[0]-ball.p[0]));if(axis.lengthSq()<1e-8)axis.set(1,0,0);axis.normalize();ball.tumble={axis:axis.clone(),rate:5+random()*3};
  };
  const place=()=>{
    if(ball.tumble){turn.setFromAxisAngle(ball.tumble.axis,ball.tumble.rate/60);spin.premultiply(turn);ball.tumble.rate*=.985;if(ball.tumble.rate<.05)ball.tumble=null;}
    group.matrix.makeTranslation(...ball.p).multiply(m.makeRotationFromQuaternion(spin)).multiply(new T.Matrix4().makeTranslation(-c.x,-c.y,-c.z));group.matrixWorldNeedsUpdate=true;
  };
  return {
    ball,group,
    get activity(){return ball.busy||press||bits.length||ball.tumble?1:0;},
    get active(){return this.activity>0;},
    // Paper is soft: a press catches the balloon and squishes it in the hand.
    get bodyMatrix(){return group.matrix;},
    grab(hit){if(!stage.grabPoint||!parts.some(item=>item.mesh===hit.object))return;press={at:[...stage.grabPoint],start:clock,deep:0};ball.catch();ball.tumble=null;},
    // A quick tap bats it as before; a squeeze let go springs it up, harder the deeper it was pressed.
    pointerUp(){
      if(!press)return;ball.free();
      const quick=clock-press.start<.22&&press.deep<R*.25;
      tap(press.at,quick?1:.9+Math.min(1,press.deep/(R*.5))*.5,true);press=null;
    },
    // A tap that just misses still counts: a balloon is easy to bat.
    nudge(ray){
      if(stage.active||press)return;
      stage.toy.updateMatrixWorld();inverse.copy(stage.toy.matrixWorld).invert();
      origin.copy(ray.origin).applyMatrix4(inverse);dir.copy(ray.direction).transformDirection(inverse);centre.set(...ball.p);
      const t=Math.max(0,centre.clone().sub(origin).dot(dir)),near=origin.clone().addScaledVector(dir,t);
      if(near.distanceTo(centre)<ball.size*1.5&&near.distanceTo(centre)>ball.size*.9)tap(near.toArray());
    },
    squeeze(){ball.inflate();return true;},
    step(dt){
      clock+=dt;
      if(press&&stage.dragTarget&&stage.grabPoint)press.deep=Math.max(press.deep,Math.hypot(...stage.dragTarget.map((v,a)=>v-stage.grabPoint[a])),Math.min(R*.5,(clock-press.start)*R*.6));
      for(const ev of ball.step(dt))if(ev==='land')stage.canvas.dataset.balloonStreak='0';
      if(ball.streak)stage.canvas.dataset.balloonStreak=String(ball.streak);
      place();
      for(let i=bits.length-1;i>=0;i--){const b=bits[i];b.age+=dt;b.v[1]-=R*3*dt;for(let a=0;a<3;a++){b.v[a]*=Math.exp(-2.2*dt);b.p[a]+=b.v[a]*dt;}b.r+=b.w*dt;if(b.p[1]<.01){b.p[1]=.01;b.v=[0,0,0];}if(b.age>4)bits.splice(i,1);}
      bits.forEach((b,i)=>{e.set(b.r,b.r*.6,b.r*.3);q.setFromEuler(e);confetti.setMatrixAt(i,m.compose(pos.set(...b.p),q,scl.setScalar(R*.16)));});
      confetti.count=bits.length;confetti.instanceMatrix.needsUpdate=true;if(confetti.instanceColor)confetti.instanceColor.needsUpdate=true;
      stage.needsSurfaceUpdate=true;stage.dirty=true;
    },
    // Dents where it was struck, and crinkles as it loses air, over the squish of the hand.
    deform(item,i,out){
      const r=item.rest,x=r[i*3]-c.x,y=r[i*3+1]-c.y,z=r[i*3+2]-c.z,l=Math.hypot(x,y,z)||1,n=[x/l,y/l,z/l];
      const sx=out[0]-r[i*3],sy=out[1]-r[i*3+1],sz=out[2]-r[i*3+2];
      let radius=l*ball.size/R;
      for(const d of ball.dents){const k=Math.max(0,n[0]*d.dir[0]+n[1]*d.dir[1]+n[2]*d.dir[2]-.8)/.2;if(k>0)radius-=d.depth*k*k;}
      const wrinkle=Math.sin(n[0]*23+n[1]*7)*Math.sin(n[2]*19-n[1]*11)+.5*Math.sin(n[1]*31+n[0]*13);
      radius-=(1-ball.air)*R*.09*(.6+.4*wrinkle);
      out[0]=c.x+n[0]*radius+sx;out[1]=c.y+n[1]*radius+sy;out[2]=c.z+n[2]*radius+sz;
    },
    reset(){ball.reset();spin.identity();ball.tumble=null;bits.length=0;press=null;place();},
    dispose(){group.parent?.remove(group);space.parent?.remove(space);space.geometry.dispose();space.material.dispose();confetti.parent?.remove(confetti);confetti.dispose();confettiGeometry.dispose();},
  };
}

// A hikaru dorodango: a ball of mud polished into a mirror by rubbing. Only a
// sliding finger polishes, a little at a time, more slowly as each spot
// shines; the rough, cracked, matte mud turns dark, smooth and glossy until
// it reflects the room. The button dusts it with fine dry powder, which
// makes the next rubbing work faster and is wiped away by it. Once it gleams
// all over, stars twinkle and it opens its eyes.
function mudDango(stage,record){
  const skin=stage.meshes.find(item=>/^body_dango/.test(item.mesh.name));
  if(!skin||!stage.body)return null;
  const box=boxOf([skin]),c=box.getCenter(new T.Vector3()),R=(box.max.x-box.min.x)/2;
  // The face toward the viewer is what a finger rubs; the shine is judged there.
  const dango=new DangoPolish({rest:skin.rest,radius:R,reachable:(x,y,z)=>z>c.z+R*.3&&y>c.y-R*.45});
  const geometry=skin.mesh.geometry,polishAttribute=new T.BufferAttribute(dango.polish,1),powderAttribute=new T.BufferAttribute(dango.powder,1);
  geometry.setAttribute('polish',polishAttribute);geometry.setAttribute('powder',powderAttribute);
  if(!geometry.getAttribute('restPosition'))geometry.setAttribute('restPosition',new T.BufferAttribute(skin.rest.slice(),3));
  const material=skin.mesh.material,previous=material.onBeforeCompile;
  if(material.isMeshPhysicalMaterial)Object.assign(material,{clearcoat:1,clearcoatRoughness:.05,envMapIntensity:1.4});
  material.onBeforeCompile=(shader,renderer)=>{
    previous?.call(material,shader,renderer);
    shader.vertexShader='attribute float polish;attribute float powder;attribute vec3 restPosition;varying float vPolish;varying float vPowder;varying vec3 vMudRest;\n'+shader.vertexShader.replace('#include <begin_vertex>','#include <begin_vertex>\nvPolish=polish;vPowder=powder;vMudRest=restPosition;');
    shader.fragmentShader='varying float vPolish;varying float vPowder;varying vec3 vMudRest;\nfloat mudHash(vec3 p){p=fract(p*.3183099+.1);p*=17.;return fract(p.x*p.y*p.z*(p.x+p.y+p.z));}\nfloat mudNoise(vec3 x){vec3 i=floor(x),f=fract(x);f=f*f*(3.-2.*f);return mix(mix(mix(mudHash(i),mudHash(i+vec3(1,0,0)),f.x),mix(mudHash(i+vec3(0,1,0)),mudHash(i+vec3(1,1,0)),f.x),f.y),mix(mix(mudHash(i+vec3(0,0,1)),mudHash(i+vec3(1,0,1)),f.x),mix(mudHash(i+vec3(0,1,1)),mudHash(i+vec3(1,1,1)),f.x),f.y),f.z);}\n'+shader.fragmentShader
      .replace('#include <color_fragment>',`#include <color_fragment>
        vec3 mudP=vMudRest*${(9/R).toFixed(3)};
        // Rough mud: grainy, with fine dry cracks; polished: smooth, deep and dark.
        float mudCrack=(1.-smoothstep(.0,.025,abs(mudNoise(mudP)-.5)))*smoothstep(.45,.7,mudNoise(mudP*.5+3.1));
        float mudGrain=mudHash(floor(vMudRest*${(60/R).toFixed(2)}));
        float mudRough=1.-clamp(vPolish,0.,1.);
        vec3 mudColour=diffuseColor.rgb*(.8+.35*mudGrain)*(1.-.35*mudCrack*mudRough);
        diffuseColor.rgb=mix(diffuseColor.rgb*vec3(.62,.55,.5),mudColour,mudRough);
        diffuseColor.rgb=mix(diffuseColor.rgb,vec3(.78,.68,.54),clamp(vPowder,0.,1.)*.6);`)
      .replace('#include <roughnessmap_fragment>','#include <roughnessmap_fragment>\nroughnessFactor=mix(.95,.06,pow(clamp(vPolish,0.,1.),1.4));roughnessFactor=mix(roughnessFactor,1.,clamp(vPowder,0.,1.));')
      .replace('#include <lights_physical_fragment>','#include <lights_physical_fragment>\n#ifdef USE_CLEARCOAT\nmaterial.clearcoat*=pow(clamp(vPolish,0.,1.),2.)*(1.-clamp(vPowder,0.,1.));\n#endif');
  };
  material.customProgramCacheKey=()=>'mud-dango';material.needsUpdate=true;
  const face=stage.meshes.filter(item=>/^face_/.test(item.mesh.name));
  const showFace=value=>{for(const item of face){item.mesh.visible=value;item.unpickable=!value;}stage.canvas.dataset.dangoShine=value?'1':'0';};
  showFace(false);
  // Twinkling stars for the finished shine.
  const starShape=new T.Shape();for(let i=0;i<8;i++){const a=i/8*Math.PI*2,r=i%2?.35:1;starShape[i?'lineTo':'moveTo'](Math.cos(a)*r,Math.sin(a)*r);}
  const starGeometry=new T.ShapeGeometry(starShape),stars=[];
  // Hidden objects still count toward the view's fit, so idle stars are shrunk away.
  for(let i=0;i<5;i++){const m=new T.Mesh(starGeometry,new T.MeshBasicMaterial({color:'#fff6cf',transparent:true,opacity:0,depthWrite:false,toneMapped:false}));const a=i/5*Math.PI*2;m.userData={a,t:-i*.2};m.position.copy(c);m.scale.setScalar(1e-4);stage.toy.add(m);stars.push(m);}
  let last=null,twinkle=0,clock=0,strokes=0,lastStroke=0;stage.canvas.dataset.dangoPolish='0.00';stage.canvas.dataset.dangoStrokes='0';
  return {
    dango,
    get activity(){return twinkle>0?1:0;},
    get active(){return twinkle>0||!!stage.body?.hold;},
    squeeze(){dango.dust();powderAttribute.needsUpdate=true;stage.dirty=true;return true;},
    step(dt){
      clock+=dt;
      const target=stage.body?.hold?stage.dragTarget:null;
      if(target&&last){
        const moved=Math.hypot(...target.map((v,a)=>v-last[a]));
        // Rub where the finger presses on the ball's surface.
        const d=new T.Vector3(...target).sub(c),point=c.clone().addScaledVector(d.lengthSq()>1e-8?d.normalize():new T.Vector3(0,0,1),R).toArray();
        for(const e of dango.rub(point,moved,dt))if(e==='shine'){showFace(true);twinkle=2.4;stage.face?.flinch(.3);}
        if(moved>1e-4){polishAttribute.needsUpdate=true;powderAttribute.needsUpdate=true;stage.dirty=true;if(clock-lastStroke>.25){lastStroke=clock;stage.canvas.dataset.dangoStrokes=String(++strokes);}}
      }
      last=target?[...target]:null;
      stage.canvas.dataset.dangoPolish=dango.mean.toFixed(2);
      if(twinkle>0){
        twinkle=Math.max(0,twinkle-dt);
        for(const s of stars){const u=s.userData;u.t+=dt;const k=Math.max(0,Math.min(1,u.t))%1;s.position.set(c.x+Math.cos(u.a+clock*.6)*R*1.25,c.y+Math.sin(u.a*2+clock)*R*.6,c.z+R*.8);s.scale.setScalar(Math.max(1e-4,R*.12*Math.sin(Math.PI*k)));s.material.opacity=Math.sin(Math.PI*k)*Math.min(1,twinkle);s.lookAt?.(stage.camera.position);}
        stage.dirty=true;
      }else for(const s of stars){s.material.opacity=0;s.scale.setScalar(1e-4);s.position.copy(c);}
    },
    reset(){dango.reset();polishAttribute.needsUpdate=true;powderAttribute.needsUpdate=true;showFace(false);twinkle=0;},
    dispose(){for(const s of stars){s.parent?.remove(s);s.material.dispose();}starGeometry.dispose();},
  };
}

// A soap film look: clear in the middle, a bright rim, and thin-film colours
// that shift with the viewing angle.
function soapFilm(opacity=1){
  const m=new T.MeshPhysicalMaterial({color:'#ffffff',transparent:true,roughness:.03,clearcoat:1,iridescence:1,iridescenceIOR:1.33,iridescenceThicknessRange:[200,700],depthWrite:false,side:T.DoubleSide});
  m.userData.opacity={value:opacity};
  m.onBeforeCompile=shader=>{
    shader.uniforms.filmOpacity=m.userData.opacity;
    shader.fragmentShader='uniform float filmOpacity;\n'+shader.fragmentShader.replace('#include <emissivemap_fragment>',`#include <emissivemap_fragment>
      float rim=pow(1.-abs(dot(normalize(normal),normalize(vViewPosition))),2.2);
      vec3 bands=.5+.5*cos(6.2832*(rim*1.6+vec3(0.,.33,.67)));
      totalEmissiveRadiance+=bands*rim*.35;
      diffuseColor.a=mix(.05,.8,rim)*filmOpacity;`);
  };
  m.customProgramCacheKey=()=>'soap-film';return m;
}

// Soap bubbles. Pressing anywhere in the air (not on the cup) takes the wand:
// it follows the finger, and swept through the air its film stretches into
// bubbles, a few big ones from a slow sweep, many small ones from a fast one.
// Let go and it goes back into the cup for a fresh film. Bubbles float,
// drift and wobble, and pop at a touch, on the floor, or after a while.
function bubbleWand(stage,record){
  const cupItem=stage.meshes.find(item=>/^body_cup/.test(item.mesh.name)),wand=stage.meshes.find(item=>/^prop_wand/.test(item.mesh.name)),solution=stage.meshes.find(item=>/^prop_solution/.test(item.mesh.name));
  if(!cupItem||!wand||!solution||!stage.body)return null;
  const cupBox=boxOf([cupItem]),R=(cupBox.max.x-cupBox.min.x)/2,cc=cupBox.getCenter(new T.Vector3());
  // The ring: the top part of the wand, where the circle is.
  const wr=wand.rest;let top=-Infinity;for(let i=1;i<wr.length;i+=3)top=Math.max(top,wr[i]);
  const ring=new T.Vector3(),ringBox=new T.Box3();let n=0;
  for(let i=0;i<wr.length;i+=3)if(wr[i+1]>top-R*.85){ring.x+=wr[i];ring.y+=wr[i+1];ring.z+=wr[i+2];n++;ringBox.expandByPoint(new T.Vector3(wr[i],wr[i+1],wr[i+2]));}
  ring.divideScalar(Math.max(1,n));const ringR=(ringBox.max.y-ringBox.min.y)/2;
  let wandBottom=Infinity;for(let i=1;i<wr.length;i+=3)wandBottom=Math.min(wandBottom,wr[i]);
  const space={min:[cc.x-R*1.6,0,cc.z-R*1.4],max:[cc.x+R*4.2,R*6.4,cc.z+R*1.4]};
  const bubbles=new SoapBubbles({size:R*1.4,cup:{center:[cc.x,0,cc.z],radius:R*.85,top:solution.center.y+R*.1},bounds:space,random:seeded(record.id??record.kind)});
  // An invisible board across the play space: presses in the air take the wand,
  // and the whole space counts for the view.
  const board=new T.Mesh(new T.BoxGeometry(space.max[0]-space.min[0],space.max[1],R*.02),new T.MeshBasicMaterial());board.visible=false;
  board.position.set((space.min[0]+space.max[0])/2,space.max[1]/2,cc.z);board.name='bubble_air';stage.toy.add(board);
  const film=new T.Mesh(new T.CircleGeometry(1,32),soapFilm());film.name='wand_film';stage.toy.add(film);
  const sphere=new T.IcosahedronGeometry(1,3),bubbleMaterial=soapFilm(),drawn=new T.InstancedMesh(sphere,bubbleMaterial,48);
  drawn.count=0;drawn.frustumCulled=false;drawn.renderOrder=3;drawn.name='soap_bubbles';stage.toy.add(drawn);
  const rest=ring.clone();let held=false,approaching=false,target=null,pos=rest.clone(),clock=0,blown=0,popped=0,dips=0,squeezing=false,puffs=0;
  const bend=cageFollower(stage,[solution]),ride=[0,0,0],foot=[cc.x,cupBox.min.y+R*.3,cc.z];
  stage.canvas.dataset.bubblesBlown='0';stage.canvas.dataset.bubblesPopped='0';stage.canvas.dataset.wandDips='0';
  const m=new T.Matrix4(),q=new T.Quaternion(),p=new T.Vector3(),s=new T.Vector3(),inverse=new T.Matrix4(),origin=new T.Vector3(),dir=new T.Vector3();
  const count=events=>{for(const e of events){if(e==='blow')stage.canvas.dataset.bubblesBlown=String(++blown);if(e==='pop')stage.canvas.dataset.bubblesPopped=String(++popped);if(e==='dip')stage.canvas.dataset.wandDips=String(++dips);}};
  bubbles.moveWand(rest.toArray(),1/60);
  return {
    bubbles,
    get activity(){return held||bubbles.bubbles.length||pos.distanceTo(rest)>1e-3?1:0;},
    get active(){return this.activity>0;},
    get held(){return held;},
    pickable(){return [board,wand.mesh];},
    grab(hit){
      // The soft bottle squishes in the hand and pushes air through the ring.
      if(hit.object===cupItem.mesh){squeezing=true;return;}
      if(hit.object!==board&&hit.object!==wand.mesh)return;held=true;approaching=true;target=stage.grabPoint?[...stage.grabPoint]:null;return 'tool';
    },
    moveTool(t){if(held)target=[t[0],t[1],cc.z];},
    pointerUp(){held=false;target=null;squeezing=false;},
    // A touch passing through a bubble pops it.
    nudge(ray){
      // The finger steering the wand does not pop what it blows.
      if(!bubbles.bubbles.length||held)return;
      stage.toy.updateMatrixWorld();inverse.copy(stage.toy.matrixWorld).invert();
      origin.copy(ray.origin).applyMatrix4(inverse);dir.copy(ray.direction).transformDirection(inverse);
      for(const b of [...bubbles.bubbles]){const c=new T.Vector3(...b.p),t=c.clone().sub(origin).dot(dir);if(t>0&&origin.clone().addScaledVector(dir,t).distanceTo(c)<b.r)count(bubbles.poke(b.p));}
    },
    // The button sweeps the wand once in a slow arc.
    squeeze(){this.sweep=0;return true;},
    step(dt){
      clock+=dt;
      if(this.sweep!==undefined){
        this.sweep+=dt;const k=Math.min(1,this.sweep/1.6),a=k*Math.PI*.8;
        target=[cc.x+Math.sin(a)*R*2.6,rest.y+Math.sin(k*Math.PI)*R*.8,cc.z];if(k>=1){this.sweep=undefined;target=null;}
      }
      // The wand eases toward the finger, or back into the cup.
      // The stick never goes through the floor.
      const goal=target?new T.Vector3(target[0],Math.max(target[1],ring.y-wandBottom+R*.02),target[2]):rest,ease=1-Math.exp(-dt*(target?18:6));pos.lerp(goal,ease);
      if(!target&&pos.distanceTo(rest)<R*.02)pos.copy(rest);
      // Back in the cup the ring dips into the solution.
      const dipping=!target&&pos.distanceTo(rest)<R*.05;
      // It blows only once it has first caught up with the finger, not on its way there.
      if(approaching&&pos.distanceTo(goal)<R*.35)approaching=false;
      count(bubbles.moveWand(dipping?[cc.x,bubbles.cup.top-R*.05,cc.z]:pos.toArray(),dt,!!target&&!approaching));
      // Squeezed with the wand at home, bubbles stream from the ring: more, the harder it is pressed.
      const body=stage.body;
      if(squeezing&&body?.hold&&dipping){
        let most=0;for(let i=0;i<body.count;i++)most=Math.max(most,Math.hypot(body.p[i*3]-body.rest[i*3],body.p[i*3+1]-body.rest[i*3+1],body.p[i*3+2]-body.rest[i*3+2]));
        const k=Math.min(1,most/(R*.5));puffs+=dt*k*9;
        while(puffs>=1&&bubbles.bubbles.length<40){puffs-=1;count(bubbles.puff([rest.x,rest.y+ringR*.6,rest.z],k));}
      }else puffs=0;
      count(bubbles.step(dt));
      bend();
      // At home the stick leans with the squeezed cup it stands in.
      const home=1-Math.min(1,pos.distanceTo(rest)/(R*.3));if(home>0&&stage.body)stage.body.displacementAt(foot,ride);else ride[0]=ride[1]=ride[2]=0;
      wand.mesh.position.copy(pos).sub(ring).add(p.set(ride[0]*home,ride[1]*home,ride[2]*home));
      // The film in the ring bulges along the sweep.
      const v=bubbles.wand.v,speed=Math.hypot(...v),stretch=1+Math.min(1.2,speed/(R*6));
      film.visible=bubbles.wand.film>0;film.position.copy(pos);film.scale.set(ringR*.92,ringR*.92,1);
      film.material.userData.opacity.value=.35+.65*bubbles.wand.film;
      if(speed>1e-3){film.scale.x*=stretch;film.rotation.z=Math.atan2(v[1],v[0]);}
      bubbles.bubbles.forEach((b,i)=>{const w=1+.06*Math.sin(clock*6+b.phase);q.identity();drawn.setMatrixAt(i,m.compose(p.set(...b.p),q,s.set(b.r*w,b.r/w,b.r)));});
      drawn.count=bubbles.bubbles.length;drawn.instanceMatrix.needsUpdate=true;
      stage.dirty=true;
    },
    reset(){bubbles.reset();held=false;squeezing=false;puffs=0;target=null;pos.copy(rest);this.sweep=undefined;bubbles.moveWand(rest.toArray(),1/60);},
    dispose(){for(const o of [board,film,drawn])o.parent?.remove(o);board.geometry.dispose();board.material.dispose();film.geometry.dispose();film.material.dispose();sphere.dispose();bubbleMaterial.dispose();},
  };
}

// Goldfish scooping. Goldfish wander about the tub. The paper poi follows
// the finger over the water; pressing dips it under, lifting scoops whatever
// lies over the paper into the bowl beside the tub. Paper in the water soaks,
// faster while swished about, and tears; a heavy catch can break wet paper
// too. The button hands over a fresh poi.
function goldfishScoop(stage,record){
  const tub=stage.meshes.find(item=>/^body_tub/.test(item.mesh.name)),water=stage.meshes.find(item=>/^prop_tubwater/.test(item.mesh.name));
  const frame=stage.meshes.find(item=>item.mesh.name==='prop_poi'),paper=stage.meshes.find(item=>/^prop_poipaper/.test(item.mesh.name)),bowl=stage.meshes.find(item=>/^prop_bowl/.test(item.mesh.name));
  const fishItems=stage.meshes.filter(item=>/^prop_fish_/.test(item.mesh.name)).sort((a,b)=>a.mesh.name.localeCompare(b.mesh.name,undefined,{numeric:true}));
  if(!tub||!water||!frame||!paper||!bowl||!fishItems.length||!stage.body)return null;
  const tb=boxOf([tub]),c=tb.getCenter(new T.Vector3()),surface=water.center.y,pb=new T.Box3().setFromBufferAttribute(paper.mesh.geometry.attributes.position);
  const poiR=(pb.max.x-pb.min.x)/2,bowlBox=new T.Box3().setFromBufferAttribute(bowl.mesh.geometry.attributes.position),bowlC=bowlBox.getCenter(new T.Vector3());
  const game=new KingyoSukui({tub:{center:[c.x,0,c.z],rx:(tb.max.x-tb.min.x)/2*.9,rz:(tb.max.z-tb.min.z)/2*.88,surface,floor:tb.min.y+(surface-tb.min.y)*.25},poi:poiR,fish:fishItems.length,bowl:[bowlC.x,bowlBox.min.y+(bowlBox.max.y-bowlBox.min.y)*.45,bowlC.z],random:seeded(record.id??record.kind)});
  // Each fish's resting heading, from its centre toward its head (away from the long tail).
  const rest=fishItems.map((item,i)=>{
    const r=item.rest,cc=item.center;let far=0,tip=[1,0];for(let k=0;k<r.length;k+=3){const d=Math.hypot(r[k]-cc.x,r[k+2]-cc.z);if(d>far){far=d;tip=[r[k]-cc.x,r[k+2]-cc.z];}}
    game.fish[i].p=[cc.x,cc.y,cc.z];return Math.atan2(-tip[1],-tip[0]);
  });
  game.fish.forEach((f,i)=>{f.heading=rest[i];f.kind=i===0?'black':'red';});
  const poiRest=paper.center.clone();
  // A torn paper: a ragged ring left in the frame.
  const tornShape=new T.Shape();tornShape.absarc(0,0,poiR,0,Math.PI*2,false);const hole=new T.Path();
  for(let i=0;i<=24;i++){const a=i/24*Math.PI*2,r=poiR*(.55+.25*Math.abs(Math.sin(i*2.7)));i?hole.lineTo(Math.cos(a)*r,Math.sin(a)*r):hole.moveTo(Math.cos(a)*r,Math.sin(a)*r);}
  tornShape.holes.push(hole);const torn=new T.Mesh(new T.ShapeGeometry(tornShape),paper.mesh.material);torn.rotation.x=-Math.PI/2;torn.visible=false;torn.name='poi_torn';stage.toy.add(torn);
  const paperMaterial=paper.mesh.material;Object.assign(paperMaterial,{transparent:true});
  // Clear water, so the fish show.
  Object.assign(water.mesh.material,{transparent:true,opacity:.18,depthWrite:false});
  // A level sheet over the water catches presses there and steers the poi.
  const sheet=new T.Mesh(new T.PlaneGeometry(tb.max.x-tb.min.x,tb.max.z-tb.min.z),new T.MeshBasicMaterial({side:T.DoubleSide}));sheet.rotation.x=-Math.PI/2;sheet.position.set(c.x,surface+.002,c.z);sheet.visible=false;sheet.name='water_sheet';stage.toy.add(sheet);
  const bend=cageFollower(stage,[water]),ride=[0,0,0];
  let held=false,clock=0,dips=0,tears=0,catches=0,squeeze=null,waves=0;const slosh={x:0,z:0,vx:0,vz:0};stage.canvas.dataset.tubWaves='0';stage.canvas.dataset.poiDips='0';stage.canvas.dataset.poiTears='0';stage.canvas.dataset.fishCaught='0';
  const plane=new T.Plane(),hit=new T.Vector3(),inverse=new T.Matrix4(),local=new T.Ray();
  const steer=ray=>{
    stage.toy.updateMatrixWorld();inverse.copy(stage.toy.matrixWorld).invert();local.copy(ray).applyMatrix4(inverse);
    plane.set(new T.Vector3(0,1,0),-surface);if(local.intersectPlane(plane,hit)){
      const ex=(hit.x-c.x)/game.tub.rx,ez=(hit.z-c.z)/game.tub.rz,e=Math.hypot(ex,ez),k=e>1?1/e:1;
      game.movePoi([c.x+(hit.x-c.x)*k,c.z+(hit.z-c.z)*k]);
    }
  };
  const count=events=>{for(const e of events){if(e==='tear')stage.canvas.dataset.poiTears=String(++tears);if(e==='catch')stage.canvas.dataset.fishCaught=String(++catches);}};
  return {
    game,
    get activity(){return 1;},
    get active(){return true;},
    get held(){return held||!!squeeze;},
    pickable(){return [sheet];},
    grab(hit){
      // The soft vinyl tub squishes in the hand, and the water sloshes away from the squeeze.
      if(hit.object===tub.mesh&&stage.grabPoint){squeeze={at:[...stage.grabPoint],sent:false};return;}
      if(hit.object!==sheet)return;held=true;steer(stage.ray.ray);game.press();stage.canvas.dataset.poiDips=String(++dips);return 'tool';
    },
    moveTool(){},
    nudge(ray){if(!squeeze)steer(ray);},
    pointerUp(){squeeze=null;if(!held)return;held=false;count(game.release());},
    squeeze(){game.newPoi();torn.visible=false;return true;},
    step(dt){
      clock+=dt;
      if(squeeze&&stage.body?.hold&&!squeeze.sent){
        const b=stage.body;let most=0;for(let i=0;i<b.count;i++)most=Math.max(most,Math.hypot(b.p[i*3]-b.rest[i*3],b.p[i*3+1]-b.rest[i*3+1],b.p[i*3+2]-b.rest[i*3+2]));
        if(most>game.tub.rx*.08){
          squeeze.sent=true;game.wave(squeeze.at);stage.canvas.dataset.tubWaves=String(++waves);
          const dx=c.x-squeeze.at[0],dz=c.z-squeeze.at[2],l=Math.hypot(dx,dz)||1;slosh.vx+=dx/l*.9;slosh.vz+=dz/l*.9;
        }
      }
      // The water tips away from the squeeze and rocks back.
      for(const a of ['x','z']){const v='v'+a;slosh[v]+=(-60*slosh[a]-3.5*slosh[v])*dt;slosh[a]+=slosh[v]*dt;}
      water.mesh.rotation.set(slosh.z*.12,0,-slosh.x*.12);bend();
      count(game.step(dt));
      fishItems.forEach((item,i)=>{
        const f=game.fish[i],cc=item.center;let heading=f.heading,p=f.p;
        // Caught fish circle slowly in the bowl.
        if(f.state==='bowl'){const a=clock*.8+i*1.3;p=[game.bowl[0]+Math.cos(a)*poiR*.5,game.bowl[1],game.bowl[2]+Math.sin(a)*poiR*.5];heading=a+Math.PI/2;}
        const wiggle=Math.sin(clock*(f.state==='swim'?11:6)+i)*.18,theta=rest[i]-heading+wiggle,ct=Math.cos(theta),st=Math.sin(theta);
        // A fish in the little bowl is drawn smaller, so it fits.
        const sc=f.state==='bowl'?.5:f.state==='flying'?1-.5*Math.min(1,f.t):1;
        item.mesh.rotation.set(0,theta,0);item.mesh.scale.setScalar(sc);
        // Fish in the tub move with the water when the tub is squeezed.
        if(f.state==='swim'&&stage.body){stage.body.displacementAt(p,ride);p=[p[0]+ride[0],p[1]+ride[1],p[2]+ride[2]];}
        // Turn about the fish's own centre: place the turned, scaled centre where it swims.
        item.mesh.position.set(p[0]-sc*(ct*cc.x+st*cc.z),p[1]-sc*cc.y,p[2]-sc*(-st*cc.x+ct*cc.z));
      });
      const pp=game.poi.p;
      frame.mesh.position.set(pp[0]-poiRest.x,pp[1]-poiRest.y,pp[2]-poiRest.z);paper.mesh.position.copy(frame.mesh.position);
      paper.mesh.visible=!game.poi.torn;torn.visible=game.poi.torn;torn.position.set(pp[0],pp[1],pp[2]);
      paperMaterial.opacity=.85-.45*game.poi.wet;
      stage.dirty=true;
    },
    poseProp(){},
    reset(){game.reset();fishItems.forEach((item,i)=>{game.fish[i].p=[item.center.x,item.center.y,item.center.z];game.fish[i].heading=rest[i];item.mesh.scale.setScalar(1);});held=false;torn.visible=false;},
    dispose(){for(const o of [torn,sheet]){o.parent?.remove(o);o.geometry.dispose();}sheet.material.dispose();},
  };
}

// Whipping meringue. Pressing in the bowl takes the whisk; moving it round
// beats air into the whites, which go from a clear, runny pool to white
// foam, soft peaks and stiff, satiny peaks, swelling as they go. Lifting the
// whisk pulls up a peak that droops if soft and stands if stiff. The button
// turns the bowl upside down: stiff meringue stays put, softer whites slide
// out and splat on the floor.
function meringueBowl(stage,record){
  const mass=stage.meshes.find(item=>/^body_meringue/.test(item.mesh.name)),bowl=stage.meshes.find(item=>item.mesh.name==='prop_mixbowl');
  const whisk=stage.meshes.filter(item=>/^prop_whisk/.test(item.mesh.name)),bowlParts=stage.meshes.filter(item=>/^prop_(mixbowl|bowl_eye)/.test(item.mesh.name));
  if(!mass||!bowl||!whisk.length||!stage.body)return null;
  const mb=boxOf([mass]),c=mb.getCenter(new T.Vector3()),R=(mb.max.x-mb.min.x)/2,base=mb.min.y,H=mb.max.y-base;
  const bb=new T.Box3().setFromBufferAttribute(bowl.mesh.geometry.attributes.position),rim=bb.max.y,bowlR=(bb.max.x-bb.min.x)/2;
  const game=new Meringue({center:[c.x,0,c.z],radius:R});
  // Bowl, its face and the whites turn over together; the whites can come loose.
  const group=new T.Group();group.name='mixing_bowl';group.matrixAutoUpdate=false;stage.toy.add(group);
  const inner=new T.Group();inner.name='whites';inner.matrixAutoUpdate=false;group.add(inner);
  for(const item of bowlParts){stage.toy.remove(item.mesh);group.add(item.mesh);}
  stage.toy.remove(mass.mesh);inner.add(mass.mesh);mass.behaviorDeform=true;mass.unpickable=false;
  // The whites' look follows the air beaten in.
  const air={value:0},material=mass.mesh.material,previous=material.onBeforeCompile;
  if(!mass.mesh.geometry.getAttribute('restPosition'))mass.mesh.geometry.setAttribute('restPosition',new T.BufferAttribute(mass.rest.slice(),3));
  Object.assign(material,{transparent:true});
  material.onBeforeCompile=(shader,renderer)=>{
    previous?.call(material,shader,renderer);shader.uniforms.whipAir=air;
    shader.vertexShader='attribute vec3 restPosition;varying vec3 vWhipRest;\n'+shader.vertexShader.replace('#include <begin_vertex>','#include <begin_vertex>\nvWhipRest=restPosition;');
    shader.fragmentShader='uniform float whipAir;varying vec3 vWhipRest;\nfloat whipHash(vec3 p){p=fract(p*.3183099+.1);p*=17.;return fract(p.x*p.y*p.z*(p.x+p.y+p.z));}\n'+shader.fragmentShader
      .replace('#include <color_fragment>',`#include <color_fragment>
        float whipFoam=smoothstep(.15,.55,whipAir);
        // Foam: little bubbles, finest once it is stiff.
        float whipBubble=whipHash(floor(vWhipRest*mix(40.,120.,smoothstep(.5,1.,whipAir))/${R.toFixed(3)}));
        diffuseColor.rgb=mix(vec3(.97,.93,.72),diffuseColor.rgb*(.94+.08*whipBubble),whipFoam);
        diffuseColor.a=mix(.62,1.,whipFoam);`)
      .replace('#include <roughnessmap_fragment>','#include <roughnessmap_fragment>\nroughnessFactor=whipAir<.3?.05:mix(.7,.38,smoothstep(.6,1.,whipAir));');
  };
  material.customProgramCacheKey=()=>'meringue';material.needsUpdate=true;
  const whiskRest=boxOf(whisk).getCenter(new T.Vector3());
  let held=false,spot=null,whiskAt=null,spin=0,clock=0,flip=null,splat=null,lifts=0,flips=0,poke=null;
  const frame=new T.Matrix4(),rise=new T.Matrix4();
  stage.canvas.dataset.whipStage='liquid';stage.canvas.dataset.peaks='0';stage.canvas.dataset.flips='0';
  const plane=new T.Plane(),hit=new T.Vector3(),inverse=new T.Matrix4(),local=new T.Ray();
  const aim=ray=>{
    stage.toy.updateMatrixWorld();inverse.copy(stage.toy.matrixWorld).invert();local.copy(ray).applyMatrix4(inverse);
    plane.set(new T.Vector3(0,1,0),-(base+H*game.volume/3*.7));
    if(local.intersectPlane(plane,hit)){const d=Math.hypot(hit.x-c.x,hit.z-c.z),k=d>bowlR*.8?bowlR*.8/d:1;spot=[c.x+(hit.x-c.x)*k,0,c.z+(hit.z-c.z)*k];}
  };
  const surfaceY=()=>base+H*game.volume/3;
  const m=new T.Matrix4(),pivot=new T.Vector3(c.x,rim*.6,c.z);
  return {
    game,group,
    get activity(){return held||poke||flip||whiskAt||(splat&&splat.k<1)?1:0;},
    get active(){return true;},
    get held(){return held||!!poke;},
    // The whites are drawn risen with the air in them, in the bowl as it turns.
    get bodyMatrix(){
      return frame.copy(group.matrix).multiply(inner.matrix).multiply(rise.makeTranslation(0,base,0).multiply(new T.Matrix4().makeScale(1,game.volume/3,1)).multiply(new T.Matrix4().makeTranslation(0,-base,0)));
    },
    // The bowl itself takes a press too: runny whites sit low in it.
    pickable(){return bowlParts.map(item=>item.mesh);},
    // The rim of the bowl, or runny whites, take the whisk. Foam is soft to the
    // finger: it squishes, and pulled up it leaves a peak.
    grab(hit){
      if(!stage.grabPoint||flip)return;
      if(hit.object===mass.mesh&&(game.air>=.3||splat)){poke={at:[...stage.grabPoint]};return;}
      if(splat||![mass.mesh,...bowlParts.map(i=>i.mesh)].includes(hit.object))return;
      held=true;aim(stage.ray.ray);return 'tool';
    },
    moveTool(){},
    nudge(ray){if(held)aim(ray);},
    pointerUp(){
      if(poke){
        const from=stage.grabPoint,to=stage.dragTarget;
        if(!splat&&from&&to&&to[1]-from[1]>H*.12&&game.lift(poke.at))stage.canvas.dataset.peaks=String(++lifts);
        poke=null;return;
      }
      if(!held)return;held=false;if(spot&&game.lift(spot))stage.canvas.dataset.peaks=String(++lifts);spot=null;
    },
    squeeze(){if(flip||splat)return true;flip={t:0,result:game.flip()};stage.canvas.dataset.flips=String(++flips);return true;},
    step(dt){
      clock+=dt;
      for(const e of game.step(dt,held&&spot?{p:spot,inside:true}:null))stage.canvas.dataset.whipStage=e;
      air.value=game.air;
      // The whisk follows the finger round the bowl, spinning, or rests at the side.
      const target=held&&spot?new T.Vector3(spot[0],surfaceY()-H*.25,spot[2]):null;
      whiskAt=whiskAt??whiskRest.clone();const goal=target??whiskRest;whiskAt.lerp(goal,1-Math.exp(-dt*(target?16:6)));
      if(!target&&whiskAt.distanceTo(whiskRest)<1e-3)whiskAt=null;
      spin+=dt*(held?14:0);
      for(const item of whisk){
        // Spin about the whisk's own axis, placed where the finger has it.
        const at=whiskAt??whiskRest,cc=whiskRest,ct=Math.cos(spin),st=Math.sin(spin);
        item.mesh.rotation.set(0,spin,0);
        item.mesh.position.set(at.x-(ct*cc.x+st*cc.z),at.y-cc.y,at.z-(-st*cc.x+ct*cc.z));
      }
      // Turning the bowl over: up, over, a pause, and back.
      let angle=0;
      if(flip){
        flip.t+=dt;const t=flip.t,k=t<.6?t/.6:t<1.5?1:t<2.1?1-(t-1.5)/.6:0;angle=Math.PI*.95*(k*k*(3-2*k));
        if(flip.result==='falls'&&!splat&&t>.55)splat={k:0};
        if(t>2.1)flip=null;
      }
      group.matrix.makeTranslation(pivot.x,pivot.y+Math.sin(angle)*.25*H,pivot.z).multiply(m.makeRotationX(angle)).multiply(new T.Matrix4().makeTranslation(-pivot.x,-pivot.y,-pivot.z));group.matrixWorldNeedsUpdate=true;
      // Soft whites slide out and splat on the floor, and stay there until reset.
      if(splat){
        splat.k=Math.min(1,splat.k+dt*1.6);const k=splat.k;
        const fallTo=new T.Matrix4().makeTranslation(c.x,0,c.z+R*.4).multiply(new T.Matrix4().makeScale(1+.5*k,Math.max(.25,1-.75*k),1+.5*k)).multiply(new T.Matrix4().makeTranslation(-c.x,-base,-c.z));
        inner.matrix.copy(group.matrix).invert().multiply(fallTo);
      }else inner.matrix.identity();
      inner.matrixWorldNeedsUpdate=true;stage.canvas.dataset.whitesFell=splat?'1':'0';
      stage.dirty=true;
    },
    // The whites rise with the air in them; peaks stand where the whisk lifted out.
    deform(item,i,out){
      if(item!==mass)return;
      const r=item.rest,x=r[i*3],y=r[i*3+1],z=r[i*3+2],h=game.volume/3;
      let ny=base+(y-base)*h,nx=x,nz=z;
      const top=(y-base)/H;
      // Foam is a little lumpy on top.
      ny+=Math.sin(x*23/R+z*17/R)*Math.sin(z*19/R-x*7/R)*H*.03*smoothFoam(game.air)*top;
      for(const p of game.peaks){
        const d=Math.hypot(x-p.x,z-p.z)/(R*.22);if(d>=1.6)continue;const w=Math.exp(-d*d*2)*top*top;
        ny+=w*p.height;const lean=w*w*p.bend*p.height;nx+=Math.cos(p.turn)*lean;nz+=Math.sin(p.turn)*lean;ny-=w*w*p.bend*p.height*.5;
      }
      out[0]=nx+(out[0]-x);out[1]=ny+(out[1]-y);out[2]=nz+(out[2]-z);
    },
    reset(){game.reset();flip=null;splat=null;held=false;poke=null;spot=null;whiskAt=null;stage.canvas.dataset.whipStage='liquid';inner.matrix.identity();},
    dispose(){group.parent?.remove(group);},
  };
}
const smoothFoam=a=>Math.max(0,Math.min(1,(a-.25)/.35));

// A snowball packed by hand. The crust cracks like the candy shells but
// lighter, and every clump that lands bursts into powder snow, so nothing
// covers the snow bunny sitting inside.
function snowBunny(stage,record){
  const shell=candyShell(stage,record,'snow',{lining:'#e4ecf7',shatter:1,landing:-Infinity});
  if(!shell)return null;
  // The leaf ears lie flat along the head to fit inside the crust. Once the
  // crust over them is gone they spring up with a little bounce (pyoko).
  const {center,dirs}=shell,above=dirs.map((d,i)=>d[1]>.55?i:-1).filter(i=>i>=0);
  const ears=stage.meshes.filter(item=>/^leaf_ear/.test(item.mesh.name)).map(item=>{
    const r=item.rest;let base=0;for(let i=3;i<r.length;i+=3)if(r[i+2]>r[base+2])base=i;
    item.mesh.matrixAutoUpdate=false;return {item,base:base/3};
  });
  const ear={angle:0,speed:0},pose=new T.Matrix4(),turn=new T.Matrix4(),pivot=new T.Vector3();
  const lift=()=>above.length&&above.filter(i=>stage.crackle.isBroken(i)).length>=above.length*.6;
  const poseEars=()=>{
    for(const {item,base} of ears){
      const p=item.mesh.geometry.attributes.position;pivot.set(p.getX(base),p.getY(base),p.getZ(base));
      pose.makeTranslation(pivot.x,pivot.y,pivot.z).multiply(turn.makeRotationX(ear.angle)).multiply(new T.Matrix4().makeTranslation(-pivot.x,-pivot.y,-pivot.z));
      item.mesh.matrix.copy(pose);item.mesh.matrixWorldNeedsUpdate=true;
    }
  };
  let earsMoving=false;
  return {
    web:shell.web,shards:shell.shards,rigidShell:true,ear,
    get freeFit(){return stage.crackle.damage.length>0&&stage.playFitScale!==undefined;},
    get activity(){return shell.busy||earsMoving?1:0;},
    get active(){return shell.busy||earsMoving;},
    step(dt){
      shell.step(dt);
      const target=lift()?1.05:0,h=Math.min(dt,1/30),omega=stage.state?.reducedMotion?14:16,zeta=stage.state?.reducedMotion?1:.35;
      ear.speed+=(omega*omega*(target-ear.angle)-2*zeta*omega*ear.speed)*h;ear.angle+=ear.speed*h;
      earsMoving=Math.abs(ear.speed)>.002||Math.abs(target-ear.angle)>.002;
      if(!earsMoving){ear.angle=target;ear.speed=0;}
      poseEars();stage.canvas.dataset.earsUp=ear.angle>.5?'1':'0';
    },
    shellPose:shell.shellPose,shellNudge:shell.shellNudge,
    reset(){shell.reset();ear.angle=0;ear.speed=0;poseEars();},
    dispose(){shell.dispose();},
  };
}

// A mochi asleep in the frost on a cold night, set apart from the powdered
// mochi by temperature. Cold, it is pale blue, a little firm, and covered in
// branching ice. A resting finger warms the skin under it; the warmth spreads
// outward, turns the skin rosy and soft, and melts the frost where it is warm.
// The button cups it in warm hands. Once enough of it is warm, its sleep
// bubble pops and it wakes with a hop. Left alone it cools, frost grows back
// and it dozes off again, bubble and all.
function frostMochi(stage,record){
  const skin=stage.meshes.find(item=>/^body_mochi_frost/.test(item.mesh.name));
  if(!skin||!stage.body)return null;
  const box=boxOf([skin]),center=box.getCenter(new T.Vector3()).toArray(),size=box.getSize(new T.Vector3()),radius=size.length()/2;
  const coat=new FrostCoat({rest:skin.rest,center,radius,seed:9});coat.prime();
  const field=new WarmthField(skin.rest,{radius});
  // The first look shows both states: one warm, bare swipe in the frost.
  for(let i=0;i<field.warmth.length;i++)field.warmth[i]=Math.max(0,.9-coat.powder[i]);
  const startWarmth=field.warmth.slice();
  const frostShown=coat.powder.slice(),warmShown=field.warmth.slice(),geometry=skin.mesh.geometry;
  const frostAttribute=new T.BufferAttribute(frostShown,1),warmAttribute=new T.BufferAttribute(warmShown,1);
  geometry.setAttribute('frost',frostAttribute);geometry.setAttribute('warmth',warmAttribute);
  if(!geometry.getAttribute('restPosition'))geometry.setAttribute('restPosition',new T.BufferAttribute(skin.rest.slice(),3));
  if(skin.mesh.material.isMeshPhysicalMaterial)Object.assign(skin.mesh.material,{clearcoat:.6,clearcoatRoughness:.14,roughness:Math.min(skin.mesh.material.roughness,.28)});
  applyFrostShading(skin.mesh.material);
  // Cold mochi is firm like mochi from the freezer; warm, it gives and sags.
  const body=stage.body;body.settings={...body.settings};
  const firmness=m=>{body.settings.edge=.00015+(.0011-.00015)*m;body.settings.recovery=13-6*m;};
  const sleepers=stage.meshes.filter(item=>/^face_sleep/.test(item.mesh.name)),eyes=stage.meshes.filter(item=>/^face_(eye|glint)/.test(item.mesh.name));
  // The sleep bubble sits at the nose, between and below the closed eyes.
  const sleepBox=boxOf(sleepers.length?sleepers:[skin]),eyeGap=sleepBox.getSize(new T.Vector3()).x;
  const nose=sleepBox.getCenter(new T.Vector3());nose.y-=eyeGap*.12;
  const out=nose.clone().sub(new T.Vector3(...center)).setY(0).normalize();
  const noseBinding=body.bind(nose.toArray()),bubbleSize=eyeGap*.16;
  // A soap-film look: clear in the middle, a bright bluish rim, and a glint.
  const film=new T.MeshPhysicalMaterial({color:'#8fc0f2',transparent:true,opacity:1,roughness:.05,clearcoat:1,iridescence:1,iridescenceIOR:1.3,depthWrite:false});
  film.onBeforeCompile=shader=>{shader.fragmentShader=shader.fragmentShader.replace('#include <emissivemap_fragment>','#include <emissivemap_fragment>\nfloat filmRim=pow(1.-abs(dot(normalize(normal),normalize(vViewPosition))),2.2);\ntotalEmissiveRadiance+=vec3(.45,.65,1.)*filmRim*.25;\ndiffuseColor.a=mix(.12,.95,filmRim);');};
  film.customProgramCacheKey=()=>'sleep-bubble';
  const bubble=new T.Mesh(new T.SphereGeometry(1,28,18),film),glint=new T.Mesh(new T.SphereGeometry(1,10,8),new T.MeshBasicMaterial({color:'#ffffff',transparent:true,opacity:.9,depthWrite:false}));
  glint.position.set(-.36,.4,.6);glint.scale.setScalar(.2);bubble.add(glint);
  bubble.name='sleep_bubble';bubble.unpickable=true;bubble.renderOrder=2;stage.toy.add(bubble);
  const nap={phase:0,grow:1,pops:0,regrowAt:0};
  let awake=false,idle=0,cupped=0,moving=false,last=null,wasHeld=false,clock=0;
  const popBubble=()=>{if(nap.grow<.2)return;nap.grow=0;nap.regrowAt=clock+2.5;stage.canvas.dataset.bubblePops=String(++nap.pops);};
  const setAwake=value=>{
    awake=value;for(const item of sleepers)item.mesh.visible=!value;for(const item of eyes)item.mesh.visible=value;
    stage.canvas.dataset.frostAwake=value?'1':'0';stage.dirty=true;
    if(value)popBubble();else{nap.grow=0;nap.regrowAt=clock+.8;}
  };
  const hop=()=>{
    if(stage.state?.reducedMotion)return;
    const half=size.y/2;
    for(let i=0;i<body.count;i++){const w=Math.max(0,(body.initialRest[i*3+1]-center[1])/half);body.velocity[i*3+1]+=2.2*half*w;}
    body.activity=1;
  };
  stage.canvas.dataset.bubblePops='0';setAwake(false);nap.grow=1;
  const warmShare=()=>{let n=0;for(const w of field.warmth)if(w>.3)n++;return n/field.warmth.length;};
  return {
    coat,field,bubble,
    get awake(){return awake;},
    get activity(){return moving||cupped>0?1:0;},
    // Asleep it breathes through its bubble; awake it keeps drawing while it cools.
    get active(){return moving||cupped>0||!awake||field.mean>.01||coat.coverage<.999;},
    squeeze(){cupped=2.2;idle=0;return true;},
    step(dt){
      clock+=dt;
      const hold=body.hold,target=stage.dragTarget,held=!!(hold&&stage.grabPoint);
      if(held){
        field.heat(stage.grabPoint,dt);
        // Rubbing warms faster, like hands rubbed together.
        if(target&&last){const motion=Math.hypot(...target.map((n,a)=>n-last[a]));if(motion>1e-4)field.heat(stage.grabPoint,Math.min(dt,motion*4));}
        idle=0;
      }else idle+=dt;
      if(held&&!wasHeld&&!awake)popBubble();
      wasHeld=held;last=target?[...target]:null;
      if(cupped>0){cupped=Math.max(0,cupped-dt);field.heatAll(dt);idle=0;}
      field.step(dt);
      // Frost melts where the skin is warm, and grows back where it is cold.
      const frost=coat.powder,warm=field.warmth;
      for(let i=0;i<frost.length;i++){
        const w=warm[i];
        if(w>.35)frost[i]=Math.max(0,frost[i]-dt*1.8*Math.min(1,(w-.35)/.35));
        else if(idle>2&&w<.25&&frost[i]<1)frost[i]=Math.min(1,frost[i]+dt*(.12+.1*((i*.618)%1))*(.35+frost[i])*(1-w/.25));
      }
      let changed=false;const k=1-Math.exp(-dt/.12);
      for(let i=0;i<frost.length;i++){
        const df=frost[i]-frostShown[i],dw=warm[i]-warmShown[i];
        if(Math.abs(df)>.002){frostShown[i]+=df*k;changed=true;}else if(df){frostShown[i]=frost[i];changed=true;}
        if(Math.abs(dw)>.002){warmShown[i]+=dw*k;changed=true;}else if(dw){warmShown[i]=warm[i];changed=true;}
      }
      if(changed){frostAttribute.needsUpdate=true;warmAttribute.needsUpdate=true;stage.dirty=true;}
      moving=changed;
      const mean=field.mean;firmness(Math.min(1,mean*1.6));
      const share=warmShare(),cover=coat.coverage;
      if(!awake&&(share>.35||cover<.4)){setAwake(true);hop();}
      else if(awake&&share<.08&&cover>.75&&idle>3)setAwake(false);
      // The sleep bubble swells and shrinks with each breath.
      if(!awake&&nap.grow<1&&clock>=nap.regrowAt)nap.grow=Math.min(1,nap.grow+dt*1.5);
      nap.phase+=dt*1.4;
      const breath=.45+.55*(.5-.5*Math.cos(nap.phase)),r=bubbleSize*breath*nap.grow,at=body.sample(noseBinding);
      bubble.visible=!awake&&r>bubbleSize*.05;
      bubble.position.set(at[0]+out.x*r*1.05,at[1]+out.y*r*1.05-r*.2,at[2]+out.z*r*1.05);bubble.scale.setScalar(Math.max(1e-4,r));
      stage.canvas.dataset.frost=cover.toFixed(2);stage.canvas.dataset.warmth=mean.toFixed(2);
    },
    reset(){coat.restore();field.warmth.set(startWarmth);frostShown.set(coat.powder);warmShown.set(field.warmth);frostAttribute.needsUpdate=true;warmAttribute.needsUpdate=true;cupped=0;idle=0;setAwake(false);nap.grow=1;},
    dispose(){bubble.parent?.remove(bubble);bubble.geometry.dispose();film.dispose();glint.geometry.dispose();glint.material.dispose();},
  };
}

function crackleBat(stage,record){
  let swarm=null;stage.canvas.dataset.batsOut='0';
  const shell=candyShell(stage,record,'bat',{lining:'#1a0d24',onBreak:()=>{if(stage.crackle.damage.length>=3)release();}});
  if(!shell)return null;
  const {center,scale,dirs}=shell,shape=SHAPES.bat,size=shape.W*scale,unit=size*.07;
  // Seats: a heap of two layers (sunflower discs) on the inside of the bowl.
  const golden=Math.PI*(3-Math.sqrt(5)),seats=[];
  for(const [count,reach,layer] of [[20,5.4,0],[12,3.6,1]])for(let k=0;k<count;k++){
    const r=reach*unit*Math.sqrt((k+.5)/count),a=k*golden+layer*golden/2;seats.push({x:Math.cos(a)*r,z:Math.sin(a)*r,layer});
  }
  const bowl=r=>{const q=r/scale/(.955*shape.W);return (shape.C-.955*shape.H*Math.sqrt(Math.max(0,1-q*q))-shape.bottom)*scale;};
  for(const seat of seats){
    const y=bowl(Math.hypot(seat.x,seat.z)),d=new T.Vector3(seat.x,y,seat.z).sub(center).normalize();
    seat.bowl=y;seat.piece=dirs.reduce((best,v,i)=>d.dot(new T.Vector3(...v))>d.dot(new T.Vector3(...dirs[best]))?i:best,0);
  }
  const seat=i=>{const s=seats[i],floor=stage.crackle.isBroken(s.piece)?0:s.bowl;return [s.x,floor+unit*(1+1.8*s.layer),s.z];};
  // The door is a real hole: the broken piece nearest the mean opening,
  // leaning upward. Once most of the shell is gone the bats use the top.
  const door=()=>{
    const broken=stage.crackle.damage;
    if(broken.length>stage.shells.length*.7)return {point:[center.x,center.y+size*.2,center.z],normal:[0,1,0]};
    const mean=[0,.6,0];for(const i of broken)for(let a=0;a<3;a++)mean[a]+=dirs[i][a];
    const best=broken.reduce((b,i)=>dirs[i][0]*mean[0]+dirs[i][1]*mean[1]+dirs[i][2]*mean[2]>dirs[b][0]*mean[0]+dirs[b][1]*mean[1]+dirs[b][2]*mean[2]?i:b,broken[0]);
    const c=stage.shells[best].center,n=dirs[best];
    return {point:[c.x-n[0]*size*.05,c.y-n[1]*size*.05,c.z-n[2]*size*.05],normal:[...n]};
  };
  // Keep flyers inside the view: past a margin, steer back toward the middle.
  const ndc=new T.Vector3(),right=new T.Vector3(),up=new T.Vector3(),inverse=new T.Matrix4();
  const contain=(p,acc)=>{
    ndc.set(p[0],p[1],p[2]).applyMatrix4(stage.toy.matrixWorld).project(stage.camera);
    const ex=ndc.x>.78?ndc.x-.78:ndc.x<-.78?ndc.x+.78:0,ey=ndc.y>.6?ndc.y-.6:ndc.y<-.45?ndc.y+.45:0;
    if(ex||ey)for(let a=0;a<3;a++)acc[a]-=(right.getComponent(a)*ex+up.getComponent(a)*ey)*size*60;
  };
  // Flyers go round the shell while it stands, and round each ear until the
  // piece holding it breaks. Once the shell is mostly gone they fly through.
  const ears=[-1,1].map(side=>{
    const u=Math.PI/2+side*shape.earU,k=1+shape.ear*.55,e=[Math.sin(shape.earV)*Math.cos(u),Math.cos(shape.earV),Math.sin(shape.earV)*Math.sin(u)];
    const c=new T.Vector3(shape.W*e[0]*k*scale,(shape.C+shape.H*e[1]*k-shape.bottom)*scale,shape.W*e[2]*k*scale),d=c.clone().sub(center).normalize();
    return {c:c.toArray(),r:size*.3,piece:dirs.reduce((best,v,i)=>d.dot(new T.Vector3(...v))>d.dot(new T.Vector3(...dirs[best]))?i:best,0)};
  });
  let obstacles=[];
  const updateObstacles=()=>{
    obstacles=ears.filter(ear=>!stage.crackle.isBroken(ear.piece));
    if(stage.crackle.damage.length<stage.shells.length*.7)obstacles.push({c:center.toArray(),r:size*1.06});
  };
  updateObstacles();
  // The little bats are a lighter shade of the shell, so a rare colour carries over.
  const shellColor=shell.shells[0].mesh.material.color,body=[shellColor.r*1.9,shellColor.g*2.1,shellColor.b*1.75].map(v=>Math.min(1,v));
  swarm=new BatSwarm(stage.toy,{count:seats.length,home:center.toArray(),size,seat,contain,obstacles:()=>obstacles,body,random:shell.random});
  const release=()=>{if(stage.crackle.damage.length<3)return;swarm.release(door());stage.canvas.dataset.batsOut='1';};
  // An invisible target over the heap, so the bats can be startled once the
  // shell is gone. It stays inside the shell's bounds, so it never changes the view fit.
  const heap=new T.Mesh(new T.SphereGeometry(unit*6.5,12,8),new T.MeshBasicMaterial({visible:false}));
  heap.position.set(0,unit*6.5,0);heap.name='bat_heap_target';stage.toy.add(heap);
  let wasHeld=false,startles=0,startledAt=-1;stage.canvas.dataset.batsStartled='0';
  return {
    swarm,web:shell.web,shards:shell.shards,seat,door,rigidShell:true,
    rotation:[.18,-.25,0],
    get freeFit(){return stage.crackle.damage.length>0&&stage.playFitScale!==undefined;},
    get activity(){return swarm.airborne||swarm.settling||shell.busy?1:0;},
    get active(){return this.activity>0;},
    pickable(){return stage.crackle.damage.length>stage.shells.length*.5?[heap]:[];},
    grab(hit){if(hit.object!==heap)return;release();return 'tool';},
    // With the whole shell gone, the button wakes the bats instead.
    squeeze(){if(stage.crackle.damage.length<stage.shells.length)return false;release();return true;},
    // A finger passing through the flock, on or off the toy, makes the bats near it dart away.
    nudge(ray){
      if(!swarm.airborne)return;
      stage.toy.updateMatrixWorld();inverse.copy(stage.toy.matrixWorld).invert();
      const origin=ray.origin.clone().applyMatrix4(inverse),dir=ray.direction.clone().transformDirection(inverse);
      if(swarm.startle(origin.toArray(),dir.toArray())&&swarm.clock-startledAt>.8){startledAt=swarm.clock;stage.canvas.dataset.batsStartled=String(++startles);}
      stage.dirty=true;
    },
    step(dt){
      shell.step(dt);
      // A fresh press on an opened shell startles the bats out again.
      const held=!!stage.body?.hold;if(held&&!wasHeld)release();wasHeld=held;
      if(swarm.flying>7)swarm.recall(door());
      stage.toy.updateMatrixWorld();inverse.copy(stage.toy.matrixWorld).invert();
      right.setFromMatrixColumn(stage.camera.matrixWorld,0).transformDirection(inverse);up.setFromMatrixColumn(stage.camera.matrixWorld,1).transformDirection(inverse);
      updateObstacles();swarm.step(dt);stage.canvas.dataset.batsFlying=String(swarm.bats.filter(b=>b.mode!=='sit').length);
    },
    shellPose:shell.shellPose,shellNudge:shell.shellNudge,
    reset(){shell.reset();swarm.reset();stage.canvas.dataset.batsOut='0';},
    dispose(){shell.dispose();swarm.dispose();heap.parent?.remove(heap);heap.geometry.dispose();heap.material.dispose();},
  };
}

// A small seeded generator, so each acquired toy keeps its own surprises.
function seeded(text){let h=2166136261;for(const c of String(text))h=Math.imul(h^c.charCodeAt(0),16777619);return ()=>{h=Math.imul(h^(h>>>15),2246822507);h=Math.imul(h^(h>>>13),3266489909);return ((h^=h>>>16)>>>0)/4294967296;};}

function yakiMochi(stage,record){
  const body=stage.meshes.find(item=>item.mesh.name==='body_yakimochi'),coals=stage.meshes.find(item=>item.mesh.name==='prop_stove_coals');
  if(!body||!stage.body)return null;
  const random=seeded(record.id??record.kind),geometry=body.mesh.geometry,rest=body.rest,count=rest.length/3,box=geometry.boundingBox;
  const width=box.max.x-box.min.x,height=box.max.y-box.min.y,bottom=box.min.y,topY=box.max.y;
  // Browning: heat reaches the bottom first, the top slowly.
  const toast=new Float32Array(count),toastAttribute=new T.BufferAttribute(toast,1);
  geometry.setAttribute('toastLevel',toastAttribute);geometry.setAttribute('restPosition',new T.BufferAttribute(Float32Array.from(rest),3));
  const rate=new Float32Array(count);for(let v=0;v<count;v++){const up=(rest[v*3+1]-bottom)/height;rate[v]=.22+.78*(1-up)**2.5;}
  const coalMaterial=coals?.mesh.material;if(coalMaterial){coalMaterial.emissive=new T.Color('#ff5a1f');coalMaterial.emissiveIntensity=0;}
  // Weak spots on the top, away from the face at the front. A puff's whole
  // rim must lie on the flat top: over the rounded edge, the bulge would
  // lift the side of the mochi unevenly.
  const normals=geometry.attributes.normal.array,spots=[],puffRadius=width*.17;
  const face=stage.meshes.filter(item=>/^face_mochi_/.test(item.mesh.name)),faceFront=face.length?Math.min(...face.map(item=>item.mesh.geometry.boundingBox.min.z)):box.max.z;
  const flatAround=(sx,sz)=>{
    for(let v=0;v<count;v++){
      if(rest[v*3+1]<topY-puffRadius*.6||Math.hypot(rest[v*3]-sx,rest[v*3+2]-sz)>puffRadius*1.08)continue;
      if(normals[v*3+1]<.9)return false;
    }
    return true;
  };
  for(let attempt=0;spots.length<3&&attempt<120;attempt++){
    const sx=box.min.x+width*(.18+.64*random()),sz=box.min.z+(faceFront-puffRadius*1.15-box.min.z)*random();
    if(spots.some(s=>Math.hypot(s[0]-sx,s[2]-sz)<puffRadius*1.7)||!flatAround(sx,sz))continue;
    let best=-1,distance=Infinity;
    for(let v=0;v<count;v++){if(normals[v*3+1]<.8)continue;const d=Math.hypot(rest[v*3]-sx,rest[v*3+2]-sz);if(d<distance){distance=d;best=v;}}
    // The rim sits just under the surface, so the puff grows out of the mochi.
    if(best>=0)spots.push([rest[best*3],rest[best*3+1]-width*.004,rest[best*3+2]]);
  }
  // A puff is the mochi itself swelling. A pressure soft body (a round skin
  // over gas, pressure-puff.js) gives the shape; its displacement moves the
  // mochi's own top, so the bulge has no seam. Where the surface stretches,
  // the crust cracks into flakes over white mochi. A popped puff sinks back
  // into a wrinkled, pale patch.
  body.behaviorDeform=true;
  const bulge=new Float32Array(count*3),stretch=new Float32Array(count).fill(1),stretchAttribute=new T.BufferAttribute(stretch,1).setUsage(T.DynamicDrawUsage);
  geometry.setAttribute('skinStretch',stretchAttribute);
  body.mesh.material.onBeforeCompile=shader=>mochiToastShader(shader,{crust:true});body.mesh.material.customProgramCacheKey=()=>'mochi-body-crust-v1';body.mesh.material.needsUpdate=true;
  // An invisible copy of the skin, so a tap on a puff can pop it.
  const pickMaterial=new T.MeshBasicMaterial({visible:false,side:T.DoubleSide});
  const puffs=[];
  // Net nodes around a point at normalised radius rho and angle a, with weights.
  const netWeights=(puff,rho,a)=>{
    const R=puff.rings,S=puff.segments,k=Math.min(R,rho*R),r0=Math.floor(k),t=k-r0,list=[];
    const ring=(r,w)=>{
      if(w<=0)return;if(r===0){list.push(0,w);return;}
      const x=a/(Math.PI*2)*S-(r%2)*.5,f=((x%S)+S)%S,s0=Math.floor(f),g=f-s0,base=1+(r-1)*S;
      list.push(base+s0,w*(1-g),base+(s0+1)%S,w*g);
    };
    ring(r0,1-t);if(r0<R)ring(r0+1,t);return list;
  };
  const addPuff=spot=>{
    const lean=random()*Math.PI*2,puff=new PressurePuff({center:spot,radius:puffRadius,lean:[Math.cos(lean)*.35,0,Math.sin(lean)*.35],random});puff.tearAt=2.6;
    // Mochi vertices on the top inside the skin's rim, with their weights in the net.
    const affected=[];
    for(let v=0;v<count;v++){
      const dx=rest[v*3]-spot[0],dy=rest[v*3+1]-spot[1],dz=rest[v*3+2]-spot[2],up=dx*puff.normal[0]+dy*puff.normal[1]+dz*puff.normal[2];
      const c=dx*puff.u[0]+dy*puff.u[1]+dz*puff.u[2],d=dx*puff.v[0]+dy*puff.v[1]+dz*puff.v[2],rho=Math.hypot(c,d)/puff.radius;
      // Only the top moves; the share fades smoothly below it, so no vertex jumps.
      const share=Math.min(1,Math.max(0,(up+puff.radius*.3)/(puff.radius*.2)));
      if(rho<1&&share>0)affected.push({v,rho,share,weights:netWeights(puff,rho,Math.atan2(d,c))});
    }
    const positions=new Float32Array(puff.count*3),g=new T.BufferGeometry();
    g.setAttribute('position',new T.BufferAttribute(positions,3).setUsage(T.DynamicDrawUsage));g.setIndex(new T.BufferAttribute(puff.triangles,1));
    const t=puff.triangles,b=puff.base,area=(p,a,c,d)=>{const ux=p[c]-p[a],uy=p[c+1]-p[a+1],uz=p[c+2]-p[a+2],vx=p[d]-p[a],vy=p[d+1]-p[a+1],vz=p[d+2]-p[a+2];return Math.hypot(uy*vz-uz*vy,uz*vx-ux*vz,ux*vy-uy*vx)/2;};
    const restAreas=Float32Array.from({length:t.length/3},(_,k)=>area(b,t[k*3]*3,t[k*3+1]*3,t[k*3+2]*3));
    const mesh=new T.Mesh(g,pickMaterial);mesh.name='mochi_puff';mesh.frustumCulled=false;stage.toy.add(mesh);
    const neighbours=Array.from({length:puff.count},()=>[]);for(const [a,c] of puff.edges){neighbours[a].push(c);neighbours[c].push(a);}
    const binding=stage.body.bind(spot),entry={puff,mesh,positions,restAreas,area,neighbours,binding,affected,offset:[0,0,0],
      sums:new Float32Array(puff.count*2),smooth:new Float32Array(puff.count*3),shift:new Float32Array(puff.count*3),stretches:new Float32Array(puff.count)};
    puff.onPop=()=>{steam(spot);popped++;next=2.5;eyesOpen=1.6;stage.canvas.dataset.mochiPops=String(popped);};
    puffs.push(entry);return entry;
  };
  // The skin's shape this frame, carried onto the mochi.
  const drawPuff=entry=>{
    const {puff,positions,restAreas,area,sums,neighbours,smooth,shift,stretches,offset}=entry,x=puff.x,t=puff.triangles;
    positions.set(x);
    // A deflated skin folds on a coarse net; a few smoothing passes on the drawn
    // surface turn its sharp creases into soft mochi wrinkles. The rim stays put.
    if(puff.popped)for(let pass=0;pass<3;pass++){
      smooth.set(positions);
      for(let i=0;i<puff.count;i++){
        if(puff.fixed[i])continue;const list=neighbours[i];let ax=0,ay=0,az=0;
        for(const j of list){ax+=smooth[j*3];ay+=smooth[j*3+1];az+=smooth[j*3+2];}
        const k=i*3,n=list.length;positions[k]+=(ax/n-positions[k])*.6;positions[k+1]+=(ay/n-positions[k+1])*.6;positions[k+2]+=(az/n-positions[k+2])*.6;
      }
    }
    for(let i=0;i<puff.count;i++)for(let a=0;a<3;a++)shift[i*3+a]=positions[i*3+a]-puff.base[i*3+a]-offset[a];
    // Each node's stretch is the area of its triangles over their rest area.
    sums.fill(0);
    for(let k=0;k<t.length/3;k++){const a=t[k*3],b=t[k*3+1],c=t[k*3+2],now=area(x,a*3,b*3,c*3),was=restAreas[k];for(const i of [a,b,c]){sums[i*2]+=now;sums[i*2+1]+=was;}}
    for(let i=0;i<puff.count;i++)stretches[i]=sums[i*2]/Math.max(1e-12,sums[i*2+1]);
    for(const {v,rho,share,weights} of entry.affected){
      let dx=0,dy=0,dz=0,st=0;
      for(let k=0;k<weights.length;k+=2){const n=weights[k],w=weights[k+1];dx+=shift[n*3]*w;dy+=shift[n*3+1]*w;dz+=shift[n*3+2]*w;st+=stretches[n]*w;}
      // Sideways movement fades toward the rim, so the bulge does not fold over the crust.
      const side=Math.min(1,(1-rho)*3)*share;bulge[v*3]+=dx*side;bulge[v*3+1]+=dy*share;bulge[v*3+2]+=dz*side;stretch[v]=Math.max(stretch[v],1+(st-1)*share);
    }
    const a=entry.mesh.geometry.attributes;a.position.needsUpdate=true;entry.mesh.geometry.computeBoundingSphere();
  };
  // Steam: soft white sprites that rise, swell and fade after a pop.
  const steamCount=40,steamPositions=new Float32Array(steamCount*3),steamLife=new Float32Array(steamCount),steamVelocity=new Float32Array(steamCount*3);
  const steamGeometry=new T.BufferGeometry();steamGeometry.setAttribute('position',new T.BufferAttribute(steamPositions,3).setUsage(T.DynamicDrawUsage));steamGeometry.setAttribute('life',new T.BufferAttribute(steamLife,1).setUsage(T.DynamicDrawUsage));
  const steamMaterial=new T.ShaderMaterial({transparent:true,depthWrite:false,uniforms:{scale:{value:width*220}},
    vertexShader:'attribute float life;uniform float scale;varying float vLife;void main(){vLife=life;vec4 v=modelViewMatrix*vec4(position,1.);gl_Position=projectionMatrix*v;gl_PointSize=life>0.?scale*(.25+.75*(1.-life))/-v.z:0.;}',
    fragmentShader:'varying float vLife;void main(){vec2 c=gl_PointCoord*2.-1.;float r=dot(c,c);if(r>1.||vLife<=0.)discard;gl_FragColor=vec4(vec3(1.),(1.-r)*(1.-r)*vLife*.55);}'});
  const steamPoints=new T.Points(steamGeometry,steamMaterial);steamPoints.frustumCulled=false;steamPoints.renderOrder=30;stage.toy.add(steamPoints);
  let steamCursor=0,steaming=0;
  const steam=spot=>{
    for(let n=0;n<16;n++){
      const i=steamCursor++%steamCount;steamLife[i]=1;
      steamPositions[i*3]=spot[0]+(random()-.5)*width*.08;steamPositions[i*3+1]=spot[1]+width*.05;steamPositions[i*3+2]=spot[2]+(random()-.5)*width*.08;
      steamVelocity[i*3]=(random()-.5)*width*.25;steamVelocity[i*3+1]=width*(.5+.5*random());steamVelocity[i*3+2]=(random()-.5)*width*.25;
    }
    steaming=2;
  };
  const eyes=stage.meshes.filter(item=>/^face_mochi_(eyes|glints)$/.test(item.mesh.name)),lids=stage.meshes.filter(item=>item.mesh.name==='face_mochi_lids');
  let fire=false,temperature=1,popped=0,next=1.2,eyesOpen=0;
  const look=()=>{const open=eyesOpen>0||puffs.some(e=>!e.puff.popped&&e.puff.height>.45);for(const item of eyes)item.mesh.visible=open;for(const item of lids)item.mesh.visible=!open;stage.canvas.dataset.mochiAwake=open?'1':'0';};
  look();const sample=[0,0,0];
  return {
    puffs,toast,
    rotation:[.32,-.3,0],
    get activity(){return fire||temperature>1.001||puffs.some(e=>e.puff.activity)||steaming>0||eyesOpen>0?1:0;},
    get active(){return this.activity>0;},
    pickable(){return puffs.filter(e=>!e.puff.popped).map(e=>e.mesh);},
    // A poke on a puff lets it out; it does not squeeze the mochi.
    grab(hit){const entry=puffs.find(e=>e.mesh===hit.object);if(entry){entry.puff.pop();return 'tool';}},
    step(dt){
      if(!(dt>0))return;
      temperature=fire?Math.min(1.5,temperature+dt*.075):Math.max(1,temperature-dt*.06);
      if(fire||temperature>1.001){
        const heat=(temperature-1)*2*dt*.16;let changed=false;
        for(let v=0;v<count;v++)if(toast[v]<1){toast[v]=Math.min(1,toast[v]+heat*rate[v]);changed=true;}
        if(changed){toastAttribute.needsUpdate=true;}
      }
      // A new puff swells from the next weak spot once the mochi is hot.
      if(fire&&temperature>1.12&&puffs.every(e=>e.puff.popped)&&puffs.length<spots.length){next-=dt;if(next<=0)addPuff(spots[puffs.length]);}
      const held=!!stage.body?.hold&&!stage.active?.tool,press=held?stage.pressAmount??0:0;
      if(puffs.length){bulge.fill(0);stretch.fill(1);}
      for(const entry of puffs){
        const {puff,binding}=entry;stage.body.sample(binding,sample);
        entry.offset[0]=sample[0]-binding.point[0];entry.offset[1]=sample[1]-binding.point[1];entry.offset[2]=sample[2]-binding.point[2];
        puff.follow(entry.offset);puff.temperature=temperature;puff.squeeze=press*.45;puff.step(dt);drawPuff(entry);
      }
      if(puffs.length)stretchAttribute.needsUpdate=true;
      if(steaming>0){
        steaming-=dt;
        for(let i=0;i<steamCount;i++){if(steamLife[i]<=0)continue;steamLife[i]=Math.max(0,steamLife[i]-dt/1.6);for(let a=0;a<3;a++){steamPositions[i*3+a]+=steamVelocity[i*3+a]*dt;steamVelocity[i*3+a]*=Math.exp(-dt*1.2);}}
        steamGeometry.attributes.position.needsUpdate=true;steamGeometry.attributes.life.needsUpdate=true;
      }
      if(eyesOpen>0)eyesOpen=Math.max(0,eyesOpen-dt);
      if(coalMaterial)coalMaterial.emissiveIntensity=fire?.9+.25*Math.sin(performance.now()/180):Math.max(0,(temperature-1)*1.5);
      look();stage.canvas.dataset.mochiHeat=temperature.toFixed(2);
    },
    // The mochi's top carries the puffs' bulges.
    deform(item,i,out){if(item!==body)return;const k=i*3;out[0]+=bulge[k];out[1]+=bulge[k+1];out[2]+=bulge[k+2];},
    squeeze(){fire=!fire;if(fire&&!puffs.length)next=1.2;stage.canvas.dataset.mochiFire=fire?'1':'0';return true;},
    release(){},
    reset(){
      fire=false;temperature=1;popped=0;next=1.2;eyesOpen=0;steaming=0;toast.fill(0);toastAttribute.needsUpdate=true;steamLife.fill(0);steamGeometry.attributes.life.needsUpdate=true;
      for(const entry of puffs){stage.toy.remove(entry.mesh);entry.mesh.geometry.dispose();}puffs.length=0;
      bulge.fill(0);stretch.fill(1);stretchAttribute.needsUpdate=true;
      if(coalMaterial)coalMaterial.emissiveIntensity=0;look();stage.canvas.dataset.mochiFire='0';stage.canvas.dataset.mochiPops='0';
    },
    dispose(){for(const entry of puffs){stage.toy.remove(entry.mesh);entry.mesh.geometry.dispose();}pickMaterial.dispose();stage.toy.remove(steamPoints);steamGeometry.dispose();steamMaterial.dispose();},
  };
}

// Small extruded charms. Each has a veil uniform: 0 clear, 1 lost in the jelly.
const CHARMS={
  star:{color:'#ffd34d',shape:(s)=>{const p=new T.Shape();for(let i=0;i<10;i++){const a=Math.PI/2+i*Math.PI/5,r=i%2?s*.45:s;i?p.lineTo(Math.cos(a)*r,Math.sin(a)*r):p.moveTo(Math.cos(a)*r,Math.sin(a)*r);}return p;}},
  heart:{color:'#ff7fa6',shape:(s)=>{const p=new T.Shape();p.moveTo(0,-s);p.bezierCurveTo(s*1.3,-s*.1,s*.9,s*1.05,0,s*.45);p.bezierCurveTo(-s*.9,s*1.05,-s*1.3,-s*.1,0,-s);return p;}},
  flower:{color:'#c39cff',shape:(s)=>{const p=new T.Shape();for(let i=0;i<=60;i++){const a=i/60*Math.PI*2,r=s*(.62+.38*Math.abs(Math.cos(a*2.5)));i?p.lineTo(Math.cos(a)*r,Math.sin(a)*r):p.moveTo(Math.cos(a)*r,Math.sin(a)*r);}return p;}},
  moon:{color:'#fff0a8',shape:(s)=>{const p=new T.Shape();p.absarc(0,0,s,Math.PI*.3,Math.PI*1.7,false);p.absarc(s*.45,0,s*.75,Math.PI*1.55,Math.PI*.45,true);return p;}},
  fish:{color:'#ff9a5c',shape:(s)=>{const p=new T.Shape();p.moveTo(-s,0);p.quadraticCurveTo(-s*.2,s*.75,s*.55,0);p.lineTo(s,s*.45);p.lineTo(s,-s*.45);p.lineTo(s*.55,0);p.quadraticCurveTo(-s*.2,-s*.75,-s,0);return p;}},
  crown:{color:'#ffcf3f',rare:true,shape:(s)=>{const p=new T.Shape();p.moveTo(-s,-s*.6);p.lineTo(-s,s*.5);p.lineTo(-s*.5,0);p.lineTo(0,s*.7);p.lineTo(s*.5,0);p.lineTo(s,s*.5);p.lineTo(s,-s*.6);p.lineTo(-s,-s*.6);return p;}},
};
function makeCharm(kind,size){
  const spec=CHARMS[kind],geometry=new T.ExtrudeGeometry(spec.shape(size),{depth:size*.35,bevelEnabled:true,bevelThickness:size*.12,bevelSize:size*.1,bevelSegments:3,curveSegments:16});
  geometry.center();
  const veil={value:.9},material=new T.MeshPhysicalMaterial({color:spec.color,roughness:spec.rare?.25:.35,metalness:spec.rare?.65:0,clearcoat:.8,clearcoatRoughness:.15,emissive:spec.rare?new T.Color('#ffb300'):new T.Color(0),emissiveIntensity:spec.rare?.25:0});
  material.customProgramCacheKey=()=> 'charm-veil-v1';
  material.onBeforeCompile=shader=>{
    shader.uniforms.veil=veil;shader.uniforms.veilColor={value:new T.Color('#e4effa')};
    shader.fragmentShader='uniform float veil;uniform vec3 veilColor;\n'+shader.fragmentShader.replace('#include <opaque_fragment>','#include <opaque_fragment>\n  gl_FragColor.rgb=mix(gl_FragColor.rgb,veilColor,veil);');
  };
  const mesh=new T.Mesh(geometry,material);mesh.name='treasure_'+kind;mesh.castShadow=false;mesh.userData.veil=veil;mesh.userData.kind=kind;
  return mesh;
}

function treasureJelly(stage,record){
  const jelly=stage.meshes.find(item=>item.mesh.name==='body_treasure_jelly'),dish=stage.meshes.find(item=>item.mesh.name==='prop_treasure_dish');
  if(!jelly||!dish||!stage.body)return null;
  const random=seeded((record.id??record.kind)+':treasure'),jb=jelly.mesh.geometry.boundingBox,db=dish.mesh.geometry.boundingBox;
  const center=jb.getCenter(new T.Vector3()),half=jb.getSize(new T.Vector3()).multiplyScalar(.5),size=half.x*2;
  // Pick five treasures; one in seven jellies hides the rare crown.
  const common=['star','heart','flower','moon','fish'],kinds=[...common].sort(()=>random()-.5);
  if(random()<1/7)kinds[Math.floor(random()*5)]='crown';
  // Spots inside the dome: rest positions well within the surface.
  const rest=jelly.rest,inside=p=>{const y=(p[1]-jb.min.y)/(half.y*2);if(y<.15||y>.72)return false;let r=0;for(let v=0;v<rest.length;v+=3)if(Math.abs(rest[v+1]-p[1])<half.y*.08)r=Math.max(r,Math.hypot(rest[v]-center.x,rest[v+2]-center.z));return Math.hypot(p[0]-center.x,p[2]-center.z)<r*.68;};
  const spots=[];
  for(let attempt=0;spots.length<5&&attempt<400;attempt++){
    const p=[center.x+(random()*2-1)*half.x*.7,jb.min.y+half.y*2*(.18+.5*random()),center.z+(random()*1.4-.5)*half.z*.7];
    // Keep the face clear: nothing hides right behind the eyes and mouth.
    const faceZone=p[2]>center.z+half.z*.15&&Math.abs(p[0]-center.x)<half.x*.42&&(p[1]-jb.min.y)/(half.y*2)>.38;
    if(faceZone||!inside(p)||spots.some(q=>Math.hypot(p[0]-q[0],p[1]-q[1],p[2]-q[2])<size*.2))continue;spots.push(p);
  }
  const charms=spots.map((spot,i)=>{
    const mesh=makeCharm(kinds[i],size*.075);mesh.rotation.set((random()-.5)*.8,(random()-.5)*1.2,(random()-.5)*.8);stage.toy.add(mesh);
    return {mesh,spot,binding:stage.body.bind(spot),state:'hidden',veil:.9,rare:!!CHARMS[kinds[i]].rare,slot:-1};
  });
  // Found treasures sit in a ring in the dish.
  const dishCenter=db.getCenter(new T.Vector3()),dishTop=db.max.y,dishRadius=(db.max.x-db.min.x)/2;
  const slotOf=n=>{const a=n/5*Math.PI*2+.6;return [dishCenter.x+Math.cos(a)*dishRadius*.45,dishTop+size*.03,dishCenter.z+Math.sin(a)*dishRadius*.45];};
  let found=0;
  // Haze: how much jelly lies between a treasure and the viewer, measured each few frames by a ray from the camera.
  const ray=new T.Raycaster(),world=new T.Vector3(),eye=new T.Vector3(),toCharm=new T.Vector3(),sample=[0,0,0];
  const measure=()=>{
    stage.toy.updateMatrixWorld(true);eye.copy(stage.camera.position);
    const scale=stage.toy.matrixWorld.getMaxScaleOnAxis();
    for(const charm of charms){
      if(charm.state!=='hidden'&&charm.state!=='pulling')continue;
      world.copy(charm.mesh.position).applyMatrix4(stage.toy.matrixWorld);toCharm.copy(world).sub(eye);const distance=toCharm.length();toCharm.normalize();
      ray.set(eye,toCharm);ray.far=distance;
      const hit=ray.intersectObject(jelly.mesh,false)[0],depth=hit?(distance-hit.distance)/scale:0;
      charm.depth=depth;charm.veil=1-Math.exp(-depth/(size*.11));
      charm.mesh.userData.veil.value=Math.min(.94,charm.veil);
    }
  };
  // Pulling: the jelly surface is dragged out with the treasure, then a strand snaps.
  let pull=null,demo=null,frame=0,flying=[];
  const strandMaterial=new T.MeshPhysicalMaterial({color:'#e9f4ff',transparent:true,opacity:.7,roughness:.15,clearcoat:1,depthWrite:false});
  const strand=new T.Mesh(new T.BufferGeometry(),strandMaterial);strand.name='treasure_strand';strand.visible=false;strand.frustumCulled=false;stage.toy.add(strand);
  const drawStrand=(from,to,thickness)=>{
    const mid=new T.Vector3().addVectors(from,to).multiplyScalar(.5);mid.y-=from.distanceTo(to)*.15;
    strand.geometry.dispose();strand.geometry=new T.TubeGeometry(new T.QuadraticBezierCurve3(from.clone(),mid,to.clone()),16,thickness,7,false);strand.visible=true;
  };
  const startPull=(charm,exit)=>{
    charm.state='pulling';stage.body.grab(exit.toArray());
    pull={charm,exit,start:charm.mesh.position.clone(),target:charm.mesh.position.clone(),snap:size*.32,free:false};
  };
  const finishPull=keep=>{
    if(!pull)return;const charm=pull.charm;
    stage.body.release();strand.visible=false;
    if(keep){charm.state='flying';flying.push({charm,from:charm.mesh.position.clone(),to:new T.Vector3(...slotOf(found)),t:0});charm.slot=found++;stage.canvas.dataset.treasures=String(found);if(charm.rare)stage.canvas.dataset.rareTreasure='1';}
    else{charm.state='hidden';}
    pull=null;
  };
  const exitPoint=charm=>{
    // Where the line from the treasure to the viewer leaves the jelly.
    stage.toy.updateMatrixWorld(true);world.copy(charm.mesh.position).applyMatrix4(stage.toy.matrixWorld);
    const dir=stage.camera.position.clone().sub(world).normalize(),r=new T.Raycaster(stage.camera.position.clone(),dir.clone().negate());
    const hit=r.intersectObject(jelly.mesh,false)[0];
    return hit?stage.toy.worldToLocal(hit.point.clone()):charm.mesh.position.clone();
  };
  // Treasures are denser than the jelly and follow it only partly, so a press
  // brings the surface close to them instead of pushing them away.
  const pose=()=>{for(const charm of charms)if(charm.state==='hidden'){stage.body.sample(charm.binding,sample);const s=charm.spot;charm.mesh.position.set(s[0]+(sample[0]-s[0])*.3,s[1]+(sample[1]-s[1])*.3,s[2]+(sample[2]-s[2])*.3);}};
  pose();measure();
  return {
    charms,
    rotation:[.2,-.32,0],
    get activity(){return pull||demo||flying.length?1:0;},
    get active(){return !!pull||!!demo||flying.length>0;},
    get held(){return !!pull||!!demo;},
    grab(hit){
      if(hit.object!==jelly.mesh||demo)return;
      // The clearest treasure near the finger on screen comes out; others stay hidden.
      const finger=hit.point.clone().project(stage.camera);let best=null,score=Infinity;
      for(const charm of charms){
        if(charm.state!=='hidden'||charm.veil>.6)continue;
        stage.toy.updateMatrixWorld(true);const p=charm.mesh.position.clone().applyMatrix4(stage.toy.matrixWorld).project(stage.camera);
        const d=Math.hypot((p.x-finger.x)*stage.camera.aspect,p.y-finger.y);if(d<.18&&d<score){score=d;best=charm;}
      }
      if(!best)return;
      startPull(best,exitPoint(best));return 'tool';
    },
    moveTool(goal){
      if(!pull||!goal?.every(Number.isFinite))return;
      pull.target.copy(pull.start).add(new T.Vector3(...goal).sub(new T.Vector3(...stage.grabPoint)).multiplyScalar(1.15));
    },
    step(dt){
      if(!(dt>0))return;
      if(demo){
        demo.t+=dt;const charm=demo.charm;
        if(demo.t<1){stage.body.move(demo.exit.clone().addScaledVector(demo.inward,size*.16*Math.min(1,demo.t/.7)).toArray());}
        else if(!pull){stage.body.release();startPull(charm,exitPoint(charm));demo.outward=pull.exit.clone().sub(pull.start).normalize();}
        if(pull&&demo.t>=1)pull.target.copy(pull.start).addScaledVector(demo.outward,size*.5*Math.min(1,(demo.t-1)/.8));
        if(demo.t>2.1){finishPull(true);demo=null;}
      }
      pose();
      if(pull){
        const charm=pull.charm,p=charm.mesh.position;p.lerp(pull.target,1-Math.exp(-dt*14));
        const outside=p.distanceTo(pull.exit);
        if(!pull.free){
          // The jelly surface follows the treasure out, like stretchy putty.
          stage.body.move(pull.exit.clone().lerp(p,.55).toArray());
          const from=pull.exit.clone().lerp(p,.25);
          if(outside>pull.snap){pull.free=true;stage.body.release();strand.visible=false;stage.canvas.dataset.strandSnap=String(Number(stage.canvas.dataset.strandSnap??0)+1);}
          else drawStrand(from,p,size*.025*Math.max(.35,1-outside/pull.snap));
        }
      }
      for(let i=flying.length-1;i>=0;i--){
        const f=flying[i];f.t=Math.min(1,f.t+dt/.6);const e=f.t*f.t*(3-2*f.t);
        f.charm.mesh.position.lerpVectors(f.from,f.to,e);f.charm.mesh.position.y+=Math.sin(Math.PI*e)*size*.35;
        f.charm.mesh.userData.veil.value=Math.max(0,f.charm.mesh.userData.veil.value-dt*3);
        f.charm.mesh.rotation.set(0,f.charm.mesh.rotation.y+dt*6*(1-e),0);
        if(f.t>=1){f.charm.state='found';f.charm.mesh.userData.veil.value=0;flying.splice(i,1);}
      }
      if(++frame%3===0||pull)measure();
      stage.canvas.dataset.treasureVeils=charms.map(c=>c.state==='found'?'x':c.veil.toFixed(2)).join(',');
    },
    pointerUp(){if(pull&&!demo)finishPull(pull.free);},
    squeeze(){
      if(demo||pull)return true;
      const hidden=charms.filter(c=>c.state==='hidden').sort((a,b)=>(a.depth??1)-(b.depth??1));
      if(!hidden.length){this.reset();return true;}
      const charm=hidden[0],exit=exitPoint(charm);
      stage.body.grab(exit.toArray());demo={charm,exit,inward:charm.mesh.position.clone().sub(exit).normalize(),t:0};return true;
    },
    release(){if(pull&&!demo)finishPull(pull.free);},
    reset(){
      if(pull)finishPull(false);demo=null;flying.length=0;found=0;stage.body.release();
      for(const charm of charms){charm.state='hidden';charm.slot=-1;}
      pose();measure();stage.canvas.dataset.treasures='0';delete stage.canvas.dataset.rareTreasure;
    },
    dispose(){for(const charm of charms){stage.toy.remove(charm.mesh);charm.mesh.geometry.dispose();charm.mesh.material.dispose();}stage.toy.remove(strand);strand.geometry.dispose();strandMaterial.dispose();},
  };
}

// A bath ball that fizzes, dissolves from the outside in, and frees a mascot.
// The surface erodes by a noise threshold (the common game "dissolve" effect),
// its colour follows the layer now exposed, and the dye spreads in the water.
function makeBathBall(radius){
  const uniforms={erosion:{value:0},time:{value:0}};
  const material=new T.MeshStandardMaterial({color:'#ffffff',roughness:.85});
  material.customProgramCacheKey=()=> 'bath-ball-v1';
  material.onBeforeCompile=shader=>{
    Object.assign(shader.uniforms,uniforms);
    shader.vertexShader='varying vec3 vBall;\n'+shader.vertexShader.replace('#include <begin_vertex>','#include <begin_vertex>\n  vBall=normalize(position);');
    shader.fragmentShader=`uniform float erosion;uniform float time;varying vec3 vBall;
      float ballHash(vec3 p){p=fract(p*.3183099+.1);p*=17.;return fract(p.x*p.y*p.z*(p.x+p.y+p.z));}
      float ballNoise(vec3 x){vec3 i=floor(x),f=fract(x);f=f*f*(3.-2.*f);
        return mix(mix(mix(ballHash(i),ballHash(i+vec3(1,0,0)),f.x),mix(ballHash(i+vec3(0,1,0)),ballHash(i+vec3(1,1,0)),f.x),f.y),
          mix(mix(ballHash(i+vec3(0,0,1)),ballHash(i+vec3(1,0,1)),f.x),mix(ballHash(i+vec3(0,1,1)),ballHash(i+vec3(1,1,1)),f.x),f.y),f.z);}
      `+shader.fragmentShader.replace('#include <color_fragment>',`#include <color_fragment>
        float n=ballNoise(vBall*6.+time*.6)*.6+ballNoise(vBall*15.-time)*.4;
        if(n<erosion*.42)discard;
        // Layers: pink shell, lilac middle, mint core.
        vec3 pink=vec3(.98,.36,.55),lilac=vec3(.60,.42,.95),mint=vec3(.40,.88,.72);
        vec3 layer=mix(pink,lilac,smoothstep(.25,.5,erosion));layer=mix(layer,mint,smoothstep(.6,.85,erosion));
        float speck=step(.86,ballNoise(vBall*42.));
        diffuseColor.rgb=mix(layer,vec3(1.),speck*.7)*(.9+.2*smoothstep(erosion*.42,erosion*.42+.08,n));`);
  };
  const mesh=new T.Mesh(new T.SphereGeometry(radius,48,32),material);mesh.name='bath_ball';mesh.castShadow=true;mesh.userData.uniforms=uniforms;
  return mesh;
}

function fizzBall(stage,record){
  const bowl=stage.meshes.find(item=>item.mesh.name==='prop_bath_bowl');
  if(!bowl||!stage.body)return null;
  const groups={seal:/^(body_mascot_seal|face_seal_)/,penguin:/^(body_mascot_penguin|face_penguin_)/,bunny:/^(body_mascot_bunny|bunny_inner|face_bunny_)/};
  const mascots=Object.fromEntries(Object.entries(groups).map(([name,re])=>[name,stage.meshes.filter(item=>re.test(item.mesh.name))]));
  if(Object.values(mascots).some(items=>!items.length))return null;
  for(const items of Object.values(mascots))for(const item of items)item.behaviorDeform=true;
  const random=seeded((record.id??record.kind)+':bath'),order=Object.keys(mascots).sort(()=>random()-.5);
  const bb=bowl.mesh.geometry.boundingBox,center=bb.getCenter(new T.Vector3()),outer=(bb.max.x-bb.min.x)/2,inner=outer*.8;
  const waterY=bb.min.y+(bb.max.y-bb.min.y)*.66,floorY=bb.min.y+(bb.max.y-bb.min.y)*.17;
  const box=new T.Box3();for(const item of mascots.seal)box.union(item.mesh.geometry.boundingBox);
  const mascotCenter=box.getCenter(new T.Vector3()),mascotSize=box.getSize(new T.Vector3()),ballRadius=Math.max(mascotSize.x,mascotSize.z)*.62;
  // Water with ripples and dye, as a polar disc with vertex colours.
  const ripple=new RippleWater({n:64,center:[center.x,center.z],radius:inner});
  const rings=26,segments=96,positions=[center.x,waterY,center.z],indices=[];
  for(let r=1;r<=rings;r++)for(let s=0;s<segments;s++){const a=s/segments*Math.PI*2,d=inner*r/rings;positions.push(center.x+Math.cos(a)*d,waterY,center.z+Math.sin(a)*d);}
  for(let s=0;s<segments;s++)indices.push(0,1+(s+1)%segments,1+s);
  for(let r=1;r<rings;r++)for(let s=0;s<segments;s++){const a=1+(r-1)*segments+s,b=1+(r-1)*segments+(s+1)%segments;indices.push(a,b,b+segments,a,b+segments,a+segments);}
  const waterGeometry=new T.BufferGeometry();waterGeometry.setAttribute('position',new T.BufferAttribute(new Float32Array(positions),3).setUsage(T.DynamicDrawUsage));
  waterGeometry.setAttribute('color',new T.BufferAttribute(new Float32Array(positions.length).fill(1),3).setUsage(T.DynamicDrawUsage));waterGeometry.setIndex(indices);waterGeometry.computeVertexNormals();
  const waterMaterial=new T.MeshPhysicalMaterial({color:'#ffffff',vertexColors:true,roughness:.05,clearcoat:1,clearcoatRoughness:.03,transparent:true,opacity:.72,depthWrite:false});
  const water=new T.Mesh(waterGeometry,waterMaterial);water.name='bath_water';water.renderOrder=5;stage.toy.add(water);
  // Dye: three colour channels on a grid, swirled around the ball and diffused (semi-Lagrangian advection).
  const n=48,dye=new Float32Array(n*n*3),next=new Float32Array(n*n*3),cell=2*inner/n;
  const baseWater=new T.Color('#c9ecff'),tint=new T.Color();
  const stepDye=(dt,source,amount,swirl)=>{
    for(let j=0;j<n;j++)for(let i=0;i<n;i++){
      const x=-inner+(i+.5)*cell,z=-inner+(j+.5)*cell,dx=x-(source.x-center.x),dz=z-(source.z-center.z),r=Math.hypot(dx,dz)+1e-6;
      // Back-trace along a swirl that also spreads outward slowly.
      const vx=(-dz/r*swirl+dx/r*.12)*Math.exp(-r/inner),vz=(dx/r*swirl+dz/r*.12)*Math.exp(-r/inner);
      const bx=(x-vx*dt+inner)/cell-.5,bz=(z-vz*dt+inner)/cell-.5,i0=Math.max(0,Math.min(n-2,Math.floor(bx))),j0=Math.max(0,Math.min(n-2,Math.floor(bz))),fx=Math.min(1,Math.max(0,bx-i0)),fz=Math.min(1,Math.max(0,bz-j0));
      for(let c=0;c<3;c++){
        const at=(a,b)=>dye[((j0+b)*n+(i0+a))*3+c];
        next[(j*n+i)*3+c]=(at(0,0)*(1-fx)+at(1,0)*fx)*(1-fz)+(at(0,1)*(1-fx)+at(1,1)*fx)*fz;
      }
    }
    // A little diffusion, and the dye fed in at the ball.
    for(let j=1;j<n-1;j++)for(let i=1;i<n-1;i++)for(let c=0;c<3;c++){const q=(j*n+i)*3+c;dye[q]=next[q]*.9+(next[q-3]+next[q+3]+next[q-n*3]+next[q+n*3])*.025;}
    if(amount>0){
      const si=Math.round((source.x-center.x+inner)/cell-.5),sj=Math.round((source.z-center.z+inner)/cell-.5);
      for(let b=-2;b<=2;b++)for(let a=-2;a<=2;a++){const i=si+a,j=sj+b;if(i<0||j<0||i>=n||j>=n)continue;const w=Math.exp(-(a*a+b*b)/2)*amount*dt;
        dye[(j*n+i)*3]+=tint.r*w;dye[(j*n+i)*3+1]+=tint.g*w;dye[(j*n+i)*3+2]+=tint.b*w;}
    }
  };
  const drawWater=()=>{
    const p=waterGeometry.attributes.position.array,col=waterGeometry.attributes.color.array;
    for(let v=0;v<p.length;v+=3){
      p[v+1]=waterY+ripple.height(p[v],p[v+2])*1.2;
      const i=Math.max(0,Math.min(n-1,Math.floor((p[v]-center.x+inner)/cell))),j=Math.max(0,Math.min(n-1,Math.floor((p[v+2]-center.z+inner)/cell))),q=(j*n+i)*3;
      const amount=Math.min(.9,(dye[q]+dye[q+1]+dye[q+2])*3.2),k=amount/Math.max(1e-6,dye[q]+dye[q+1]+dye[q+2]);
      col[v]=baseWater.r*(1-amount)+dye[q]*k;col[v+1]=baseWater.g*(1-amount)+dye[q+1]*k;col[v+2]=baseWater.b*(1-amount)+dye[q+2]*k;
    }
    waterGeometry.attributes.position.needsUpdate=true;waterGeometry.attributes.color.needsUpdate=true;waterGeometry.computeVertexNormals();
  };
  // Bubbles: instanced spheres rising from the ball, popping as ripples at the surface.
  const bubbleGeometry=new T.IcosahedronGeometry(1,1),bubbleMaterial=new T.MeshPhysicalMaterial({color:'#ffffff',roughness:.05,transparent:true,opacity:.78,clearcoat:1,depthWrite:false});
  const bubbles=new T.InstancedMesh(bubbleGeometry,bubbleMaterial,180);bubbles.name='bath_bubbles';bubbles.count=0;bubbles.frustumCulled=false;bubbles.boundingBox=new T.Box3();stage.toy.add(bubbles);
  const live=[],matrix=new T.Matrix4(),quaternion=new T.Quaternion(),scale=new T.Vector3(),spot=new T.Vector3();
  const ball=makeBathBall(ballRadius);stage.toy.add(ball);
  const home=new T.Vector3(center.x+inner*.25,waterY+ballRadius*2.4,center.z+inner*.1);
  let state='ready',velocity=0,erosion=0,fizz=0,rise=0,riseSpeed=0,drops=0,current=null,awake=false,time=0,carry=0,bob=0,bobSpeed=0;
  const show=name=>{
    current=name;
    for(const [key,items] of Object.entries(mascots))for(const item of items){item.mesh.visible=key===name&&state!=='ready'&&erosion>.7;item.unpickable=key!==name||state!=='floating';}
    const lids=(mascots[name]??[]).filter(item=>/_lids$/.test(item.mesh.name)),eyes=(mascots[name]??[]).filter(item=>/_(eyes|glints)$/.test(item.mesh.name));
    for(const item of lids)if(item.mesh.visible)item.mesh.visible=!awake;for(const item of eyes)if(item.mesh.visible)item.mesh.visible=awake;
  };
  const restBall=()=>{ball.visible=true;ball.position.copy(home);ball.scale.setScalar(1);ball.userData.uniforms.erosion.value=0;};
  restBall();show(order[0]);
  const drop=()=>{
    if(state!=='ready'&&state!=='floating')return false;
    if(state==='floating'){drops++;restBall();}
    current=order[drops%order.length];awake=false;erosion=0;fizz=0;rise=0;riseSpeed=0;velocity=0;state='falling';show(current);stage.canvas.dataset.bathState=state;return true;
  };
  return {
    ripple,ball,
    // Tilted toward the viewer, so the water and the ball under it read from above.
    rotation:[.48,-.2,0],
    get activity(){return state!=='ready'&&state!=='floating'||live.length||ripple.activity||Math.abs(bobSpeed)>1e-4||Math.abs(bob)>1e-4?1:0;},
    get active(){return this.activity>0;},
    pickable(){return state==='ready'?[ball]:[];},
    grab(hit){if(hit.object===ball){drop();return 'tool';}},
    step(dt){
      if(!(dt>0))return;time+=dt;ball.userData.uniforms.time.value=time;
      if(state==='falling'){
        velocity-=ballRadius*9*dt;ball.position.y+=velocity*dt;
        if(ball.position.y<waterY+ballRadius*.2){state='fizzing';fizz=1;ripple.disturb(ball.position.x,ball.position.z,-.06,ballRadius*1.4);velocity*=.25;stage.canvas.dataset.bathState=state;}
      }else if(state==='fizzing'){
        // Sink to the bottom, fizzing hard at first, dissolving from the outside in.
        const bottom=floorY+ballRadius*(1-erosion*.9);velocity+=(bottom-ball.position.y)*8*dt-velocity*3*dt;ball.position.y+=velocity*dt;
        erosion=Math.min(1,erosion+dt/6);fizz=Math.sqrt(1-erosion);ball.userData.uniforms.erosion.value=erosion;
        ball.scale.setScalar(Math.max(.05,1-erosion*.92));ball.position.x+=(Math.random()-.5)*fizz*ballRadius*.01;
        tint.setRGB(...(erosion<.4?[.98,.36,.55]:erosion<.75?[.60,.42,.95]:[.40,.88,.72]));
        carry+=fizz*130*dt;
        while(carry>=1&&live.length<180){carry-=1;const a=Math.random()*Math.PI*2,b=Math.acos(Math.random()*2-1),r=ballRadius*ball.scale.x;
          live.push({x:ball.position.x+Math.sin(b)*Math.cos(a)*r,y:ball.position.y+Math.cos(b)*r,z:ball.position.z+Math.sin(b)*Math.sin(a)*r,size:ballRadius*(.05+.06*Math.random()),phase:Math.random()*6.28,surface:0});}
        if(erosion>.7)show(current);
        if(erosion>=1){state='rising';ball.visible=false;rise=ball.position.y-mascotCenter.y;riseSpeed=0;stage.canvas.dataset.bathState=state;}
      }else if(state==='rising'){
        // Buoyancy: the mascot floats up and settles with a little bob.
        riseSpeed+=(-rise*30-riseSpeed*4)*dt;rise+=riseSpeed*dt;
        if(Math.abs(rise)<ballRadius*.02&&Math.abs(riseSpeed)<ballRadius*.1){rise=0;riseSpeed=0;state='floating';awake=true;ripple.disturb(mascotCenter.x,mascotCenter.z,-.03,ballRadius*1.2);stage.canvas.dataset.bathState=state;stage.canvas.dataset.bathMascot=current;show(current);}
      }
      // A press on the floating mascot sinks it a little; it bobs back with ripples.
      const held=state==='floating'&&!!stage.body?.hold,press=held?stage.pressAmount??0:0;
      bobSpeed+=((-press*ballRadius*.25-bob)*50-bobSpeed*5)*dt;bob+=bobSpeed*dt;
      if(Math.abs(bobSpeed)>.03)for(let s=0;s<8;s++){const a=s/8*Math.PI*2;ripple.disturb(mascotCenter.x+Math.cos(a)*mascotSize.x*.55,mascotCenter.z+Math.sin(a)*mascotSize.z*.55,-bobSpeed*dt*.5,ballRadius*.4);}
      if(!held&&Math.abs(bob)<1e-4&&Math.abs(bobSpeed)<1e-4){bob=0;bobSpeed=0;}
      // Bubbles rise, wobble, pop at the surface.
      let m=0;
      for(let i=live.length-1;i>=0;i--){
        const b=live[i];
        if(b.surface>0){b.surface-=dt;if(b.surface<=0){ripple.disturb(b.x,b.z,-.006,ballRadius*.25);live.splice(i,1);continue;}}
        else{b.y+=ballRadius*2.4*dt;b.x+=Math.sin(time*7+b.phase)*ballRadius*.25*dt;b.z+=Math.cos(time*6+b.phase)*ballRadius*.25*dt;b.size*=1+dt*.3;
          if(b.y>=waterY){b.y=waterY+b.size*.3;b.surface=.2+Math.random()*.5;}}
        bubbles.setMatrixAt(m++,matrix.compose(spot.set(b.x,b.y,b.z),quaternion,scale.setScalar(b.size)));
      }
      bubbles.count=m;bubbles.instanceMatrix.needsUpdate=true;
      stepDye(dt,ball.position,state==='fizzing'?fizz*5:0,1.2);
      ripple.step(dt);drawWater();
    },
    deform(item,i,out){
      // Inside the ball until it is free, then afloat.
      if(state==='fizzing'||state==='falling'){out[0]+=ball.position.x-mascotCenter.x;out[1]+=ball.position.y-mascotCenter.y;out[2]+=ball.position.z-mascotCenter.z;}
      else if(state==='rising')out[1]+=rise;
      out[1]+=bob;
    },
    squeeze(){return drop();},
    release(){},
    reset(){
      state='ready';drops=0;awake=false;erosion=0;fizz=0;rise=0;bob=0;bobSpeed=0;live.length=0;bubbles.count=0;dye.fill(0);
      ripple.now.fill(0);ripple.before.fill(0);ripple.activity=0;restBall();show(order[0]);drawWater();stage.canvas.dataset.bathState=state;
    },
    dispose(){stage.toy.remove(water);waterGeometry.dispose();waterMaterial.dispose();stage.toy.remove(bubbles);bubbles.dispose();bubbleGeometry.dispose();bubbleMaterial.dispose();stage.toy.remove(ball);ball.geometry.dispose();ball.material.dispose();},
  };
}

// A reusable water balloon. A flick tosses it; a hard landing bursts it into
// particle water drawn by screen-space fluid rendering; a soft one bounces with
// squash and stretch. Once the puddle drains, the balloon closes and refills.
function waterBalloon(stage){
  const body=stage.meshes.find(item=>item.mesh.name==='body_waterballoon');
  if(!body||!stage.body)return null;
  const items=stage.meshes.filter(item=>!item.isProp);for(const item of items)item.behaviorDeform=true;
  const box=body.mesh.geometry.boundingBox,center=box.getCenter(new T.Vector3()),half=box.getSize(new T.Vector3()).multiplyScalar(.5),size=half.y*2;
  const h=size*.05,g=size*9;
  const fluid=new ParticleFluid({capacity:520,radius:h,viscosity:.06,stiffness:.02,nearStiffness:.06,gravity:-g,collide:(x,v,prev,i)=>{
    const k=i*3;if(x[k+1]<h*.3){x[k+1]=h*.3;prev[k]+=(x[k]-prev[k])*.12;prev[k+2]+=(x[k+2]-prev[k+2])*.12;if(prev[k+1]<x[k+1])prev[k+1]=x[k+1];}
  }});
  const fluidView=new ScreenSpaceFluid({capacity:520,radius:h*.85,tint:'#eef9ff',absorption:[.5,.18,.06],opacity:.45});stage.toy.add(fluidView.mesh);
  // Pooled water: drops that land join a shallow-water height field (the
  // yolk's virtual pipes with no yield). Water wets dry floor only where it is
  // deeper than a hold depth, and drag grows as it thins, so the splash stops
  // as a puddle about that deep. Seeded noise in the hold depth, like the
  // floor's grain, gives the puddle an uneven edge.
  const pn=80,extent=size*2,pmin=[center.x-extent,center.z-extent],pmax=[center.x+extent,center.z+extent];
  const film=size*.04,random=seeded('waterballoon'),fade=x=>x*x*(3-2*x);
  const octave=count=>{
    const n=count+1,lattice=Float32Array.from({length:n*n},()=>random());
    return (i,j)=>{const u=i/pn*count,v=j/pn*count,a=Math.floor(u),b=Math.floor(v),s=fade(u-a),t=fade(v-b);
      return (lattice[a+b*n]*(1-s)+lattice[a+1+b*n]*s)*(1-t)+(lattice[a+(b+1)*n]*(1-s)+lattice[a+1+(b+1)*n]*s)*t;};
  };
  const coarse=octave(6),fine=octave(17),holdDepth=new Float32Array(pn*pn);
  for(let j=0;j<pn;j++)for(let i=0;i<pn;i++)holdDepth[i+j*pn]=film*(.5+.65*coarse(i,j)+.35*fine(i,j));
  const puddle=new YolkFlow({nx:pn,nz:pn,min:pmin,max:pmax,terrain:new Float32Array(pn*pn),mask:new Uint8Array(pn*pn).fill(1),gravity:size*30,damping:1.5,yieldSlope:0,holdDepth,filmDrag:40});
  const dx=(pmax[0]-pmin[0])/pn,dz=(pmax[1]-pmin[1])/pn;
  // Drawn in one pass with premultiplied alpha: a tint darkens the wet floor,
  // and reflections add on top, so highlights stay bright over clear water.
  // The depth is thresholded per pixel with smoothstep over its screen-space
  // derivative, as Valve draws distance-field glyphs (Green, SIGGRAPH 2007),
  // so the edge stays crisp at any zoom; a thin meniscus line follows it the
  // way their outlines do.
  const pPositions=new Float32Array(pn*pn*3),pDepth=new Float32Array(pn*pn),pIndex=new Uint32Array((pn-1)*(pn-1)*6),smoothed=new Float32Array(pn*pn),pass=new Float32Array(pn*pn);
  for(let j=0;j<pn;j++)for(let i=0;i<pn;i++){const v=(i+j*pn)*3;pPositions[v]=pmin[0]+(i+.5)*dx;pPositions[v+2]=pmin[1]+(j+.5)*dz;}
  const pGeometry=new T.BufferGeometry();pGeometry.setAttribute('position',new T.BufferAttribute(pPositions,3).setUsage(T.DynamicDrawUsage));
  pGeometry.setAttribute('depth',new T.BufferAttribute(pDepth,1).setUsage(T.DynamicDrawUsage));pGeometry.setIndex(new T.BufferAttribute(pIndex,1).setUsage(T.DynamicDrawUsage));
  // Empty bounds keep the puddle out of the play fit.
  pGeometry.boundingBox=new T.Box3();pGeometry.boundingSphere=new T.Sphere(new T.Vector3(center.x,0,center.z),extent*1.5);
  const edge=film*.2;
  const pMaterial=new T.ShaderMaterial({transparent:true,depthWrite:false,blending:T.CustomBlending,blendSrc:T.OneFactor,blendDst:T.OneMinusSrcAlphaFactor,
    uniforms:{edge:{value:edge},tint:{value:new T.Vector3(.6,.7,.74)},sky:{value:new T.Vector3(.94,.97,1)},sun:{value:stage.key?.position.clone().normalize()??new T.Vector3(-3,7,5).normalize()}},
    vertexShader:`attribute float depth;varying float vDepth;varying vec3 vNormal;varying vec3 vView;
      void main(){vec4 world=modelMatrix*vec4(position,1.);vDepth=depth;vNormal=normalize(mat3(modelMatrix)*normal);vView=cameraPosition-world.xyz;gl_Position=projectionMatrix*viewMatrix*world;}`,
    fragmentShader:`uniform float edge;uniform vec3 tint;uniform vec3 sky;uniform vec3 sun;varying float vDepth;varying vec3 vNormal;varying vec3 vView;
      void main(){
        float w=fwidth(vDepth)+1e-7,inside=smoothstep(edge-w,edge+w,vDepth);if(inside<=0.)discard;
        vec3 n=normalize(vNormal),v=normalize(vView),r=reflect(-v,n);
        float fresnel=.02+.98*pow(1.-max(dot(n,v),0.),5.);
        float rim=smoothstep(edge,edge+w*1.5,vDepth)*(1.-smoothstep(edge+w*1.5,edge+w*4.,vDepth));
        float glint=pow(max(dot(r,sun),0.),300.)*2.5;
        float a=.38*inside;
        gl_FragColor=vec4(tint*a+(sky*(fresnel*.4+rim*.3)+glint)*inside,a);
      }`});
  const pMesh=new T.Mesh(pGeometry,pMaterial);pMesh.name='balloon_puddle';pMesh.frustumCulled=false;pMesh.renderOrder=6;pMesh.visible=false;pMesh.unpickable=true;stage.toy.add(pMesh);
  const drawPuddle=()=>{
    // A [1 2 1] blur each way rounds off the grid.
    const w=puddle.water;
    for(let j=0;j<pn;j++)for(let i=0;i<pn;i++){const q=i+j*pn;pass[q]=(2*w[q]+(i?w[q-1]:w[q])+(i<pn-1?w[q+1]:w[q]))/4;}
    for(let j=0;j<pn;j++)for(let i=0;i<pn;i++){const q=i+j*pn;smoothed[q]=(2*pass[q]+(j?pass[q-pn]:pass[q])+(j<pn-1?pass[q+pn]:pass[q]))/4;}
    let k=0;
    for(let q=0;q<pn*pn;q++){pDepth[q]=smoothed[q];pPositions[q*3+1]=.002+smoothed[q];}
    for(let j=0;j<pn-1;j++)for(let i=0;i<pn-1;i++){const a=i+j*pn,b=a+1,c=a+pn,d=c+1;if(Math.max(smoothed[a],smoothed[b],smoothed[c],smoothed[d])<edge)continue;pIndex[k++]=a;pIndex[k++]=c;pIndex[k++]=b;pIndex[k++]=b;pIndex[k++]=c;pIndex[k++]=d;}
    pGeometry.setDrawRange(0,k);pGeometry.index.needsUpdate=true;pGeometry.attributes.position.needsUpdate=true;pGeometry.attributes.depth.needsUpdate=true;pGeometry.computeVertexNormals();
    pMesh.visible=k>0;
  };
  const offset=new T.Vector3(),velocity=new T.Vector3();
  let state='resting',squash=0,squashSpeed=0,timer=0,grow=1,samples=[],hold=false,bursts=0;
  const visible=value=>{for(const item of items)item.mesh.visible=value;};
  // Stage releases the finger itself after pointer up; the button path has already released it.
  const launch=v=>{if(state!=='resting')return false;velocity.copy(v);state='flying';stage.canvas.dataset.balloon=state;return true;};
  // The burst: the body slumps and pours most of its water straight into the
  // puddle under it, while a crown of particle drops leaves its edge. Too few
  // particles could fill the balloon, so they carry only the crown.
  const faces=items.filter(item=>item!==body),skin=body.mesh.material,skinOpacity=skin.opacity;
  const total=Math.PI*(size*.46)**2*film,crownDrops=240,dropVolume=total*.25/crownDrops,slumpTime=.1;
  let slump=1,poured=0,crowned=0;
  const burst=()=>{
    state='splashing';timer=0;bursts++;slump=0;poured=0;crowned=0;
    stage.canvas.dataset.balloon=state;stage.canvas.dataset.balloonBursts=String(bursts);
    if(!stage.state?.reducedMotion)stage.hitstop=Math.max(stage.hitstop??0,.05);
  };
  const slumpEase=()=>1-(1-slump)**3;
  return {
    fluid,puddle,
    get activity(){return state!=='resting'||Math.abs(squash)>1e-4||Math.abs(squashSpeed)>1e-4||fluid.count||pMesh.visible?1:0;},
    get active(){return this.activity>0;},
    get freeFit(){return state!=='resting';},
    step(dt){
      if(!(dt>0))return;
      // Remember the drag, to tell a flick from a squeeze.
      const now=performance.now();
      if(stage.body?.hold&&stage.active&&!stage.active.orbit&&stage.dragTarget){samples.push({t:now,p:[...stage.dragTarget]});while(samples.length>8||samples[0].t<now-160)samples.shift();hold=true;}
      else hold=false;
      if(state==='flying'){
        velocity.y-=g*dt;offset.addScaledVector(velocity,dt);
        if(offset.y<=0&&velocity.y<0){
          const impact=-velocity.y;offset.y=0;
          if(impact>size*3){burst();}
          else{squashSpeed-=impact*.9;velocity.y=impact*.3;velocity.x*=.5;velocity.z*=.5;if(impact<size*.6){velocity.set(0,0,0);state='settling';}}
        }
        // Keep it on the stage.
        offset.x=clamp(offset.x,-size*.9,size*.9);offset.z=clamp(offset.z,-size*.6,size*.6);
      }else if(state==='settling'){offset.multiplyScalar(Math.exp(-dt*4));if(offset.length()<size*.003){offset.set(0,0,0);state='resting';stage.canvas.dataset.balloon=state;}}
      else if(state==='splashing'){
        timer+=dt;
        if(slump<1){
          // The body's water pours into the puddle as it slumps.
          slump=Math.min(1,slump+dt/slumpTime);const e=slumpEase(),radius=half.x*(1+.25*e),pour=total*.75*e-poured;poured+=pour;
          puddle.deposit([center.x+offset.x,center.z+offset.z],radius*.95,pour/(2/3*Math.PI*(radius*.95)**2));
          // The crown leaves the edge of the slumping water in two dense rings,
          // spaced by the golden angle. Most of it falls back onto the puddle's
          // edge; every eighth drop flies on and lands as a separate droplet.
          const due=slump>=.8?crownDrops:slump>=.35?crownDrops/2:0,squashZ=half.z/half.x;
          for(;crowned<due&&fluid.count<fluid.capacity;crowned++){
            const a=crowned*2.39996+Math.random()*.1,c=Math.cos(a),s=Math.sin(a),far=crowned%8===0;
            const out=size*(far?.7+.4*Math.random():.12+.33*Math.random()),up=size*(.6+1.1*Math.random()),r=radius*.8;
            fluid.add(center.x+offset.x+c*r,box.min.y+h*(.6+.55*(crowned%2)),center.z+offset.z+s*r*squashZ,velocity.x*.2+c*out,up,velocity.z*.2+s*out*squashZ);
          }
          if(slump>.3)for(const item of faces)item.mesh.visible=false;
          skin.opacity=skinOpacity*(1-clamp((slump-.15)/.65,0,1));if(slump>=1)visible(false);
        }
        // A drop that reaches the floor skids on a little, then joins the puddle.
        for(let i=fluid.count-1;i>=0;i--){
          const k=i*3;if(fluid.x[k+1]>=h*.6)continue;
          puddle.pour(fluid.x[k]+fluid.v[k]*.15,fluid.x[k+2]+fluid.v[k+2]*.15,dropVolume,dx*1.3);fluid.remove(i);
        }
        if(timer>2.2){
          // After a moment it soaks away, the thin edge first.
          const w=puddle.water;let left=0;
          for(let q=0;q<w.length;q++)if(w[q]>0){w[q]=Math.max(0,w[q]-dt*film*(.7+.8*Math.min(1,film*.3/w[q])));left=Math.max(left,w[q]);}
          puddle.activity=1;
          if(!fluid.count&&left<film*.03){puddle.clear();drawPuddle();state='closing';timer=0;offset.set(0,0,0);grow=0;skin.opacity=skinOpacity;visible(true);}
        }
      }else if(state==='closing'){
        // It closes and fills itself again, with an elastic overshoot.
        timer+=dt;const t=Math.min(1,timer/.7);grow=1-Math.exp(-6*t)*Math.cos(9*t);
        if(t>=1){grow=1;state='resting';stage.canvas.dataset.balloon=state;}
      }
      squashSpeed+=(-squash*120-squashSpeed*7)*dt;squash+=squashSpeed*dt;
      if(Math.abs(squash)<1e-4&&Math.abs(squashSpeed)<1e-4){squash=0;squashSpeed=0;}
      if(fluid.count){fluid.step(dt/2);fluid.step(dt/2);}
      if(puddle.activity){puddle.step(dt);drawPuddle();}
      stage.canvas.dataset.balloonDrops=String(fluid.count);
    },
    deform(item,i,out){
      // Squash about the floor, scale while refilling, then carry the flight.
      // While it bursts, the body slumps to a low, wide puddle shape.
      const e=state==='splashing'?slumpEase():0,sy=(1-clamp(squash/size,-.35,.45))*(1-.85*e),sx=1/Math.sqrt(Math.max(.3,1-clamp(squash/size,-.35,.45)))*(1+.25*e),s=grow;
      out[0]=center.x+(out[0]-center.x)*sx*s+offset.x;
      out[1]=box.min.y+(out[1]-box.min.y)*sy*s+offset.y;
      out[2]=center.z+(out[2]-center.z)*sx*s+offset.z;
    },
    beforeRender(renderer,camera){fluidView.update(fluid.x,fluid.count);fluidView.render(renderer,camera,stage.toy.matrixWorld);},
    pointerUp(){
      if(samples.length<2)return;
      const first=samples[0],last=samples.at(-1),seconds=(last.t-first.t)/1000;samples.length=0;if(seconds<=0)return;
      const v=last.p.map((value,a)=>(value-first.p[a])/seconds),speed=Math.hypot(...v);
      // A quick flick tosses it up; how hard sets how high, and so whether it bursts.
      if(speed>size*1.4)launch(new T.Vector3(v[0]*.12,Math.min(size*4.4,size*1.6+speed*.7),0));
    },
    squeeze(){if(state!=='resting')return true;return launch(new T.Vector3(size*.15,size*4.2,0));},
    release(){},
    reset(){state='resting';offset.set(0,0,0);velocity.set(0,0,0);squash=0;squashSpeed=0;grow=1;fluid.clear();puddle.clear();pMesh.visible=false;slump=1;skin.opacity=skinOpacity;visible(true);stage.canvas.dataset.balloon=state;},
    dispose(){stage.toy.remove(fluidView.mesh);fluidView.dispose();stage.toy.remove(pMesh);pGeometry.dispose();pMaterial.dispose();},
  };
}
